// БОЙ.1.1.v5: каркас боевого экрана.
//
// Изменения относительно v4:
//   - Drag-and-drop переписан на native touch + mouse events (не React
//     synthetic, не Pointer Events). Раньше pointer events в TG WebView
//     не доходили до handler'а в части случаев. Touch события — самые
//     надёжные на мобильном.
//   - touch-action: none на .hcard (не на phone) — не блокирует
//     горизонтальный scroll руки между карт.
//   - Tap на карте (без drag) → открывает детали. Hold убран — он
//     работал не у всех (нужно ждать 500мс).
//   - Мулиган: показывает статы каждой карты + есть кнопка «🔍» (тап)
//     → открывает GameCard.full. Tap по карте — селект для свапа.
//   - Фракция и Герой берутся из выбранной игроком колоды
//     (location.state.deckId → useGameStore.decks → faction/heroId).

import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// ПвП.6.1: импортируем из engine-shim вместо engine-bridge.
// Это drop-in замена: read-only функции pass-through; action-мутаторы
// (setPendingPlacement, heroAttackOrSkip, etc.) проверяют match._pvp и
// если true — шлют в Colyseus-room через getCurrentRoom().send(...) вместо
// мутации локального движка. См. app/src/pvp/engine-shim.ts.
import {
  createMatchForBattle, PHASE,
  applyMulligan, finishMulligan,
  setPendingPlacement, cancelPendingPlacement, canAddPlacement,
  commitPlacementsAndReveal, heroAttackOrSkipAnimated, canHeroAttackHero,
  canHeroAttackUnit, actionInitiationTurn,
  getHeroActiveInfo, useHeroActive, useHeroActiveWithAnim,
  actionNeedsTarget, getActionTargets, revealModsFromDeck,
  setPlayerPendingAction, clearPlayerPendingAction,
  getInfluenceCardTemplates, getInfluencePlayCount, needsInfluenceTarget, getInfluenceTargets,
  canPlayInfluenceCard, playInfluenceCard, playInfluenceCardWithAnim, INFLUENCE_PER_CARD_LIMIT,
  botMulligan, botPlacement, applyBotPlacementDecision,
  botModPlay, botInfluencePlay, botHeroAttack,
  getActivatableStealth, getStealthTargets, activateStealthUnit,
  // БУГ.2: placement prompt + opts для Order.
  determinePlacementPrompt, setPendingOrderOpts,
  // БУГ.3: приоритет атаки Глобала.
  needsAttackPriorityPicker, setUnitAttackPriority, GLOBAL_PRIORITIES,
  // 2d: приоритет лечения для хилеров.
  needsHealPriorityPicker, setUnitHealPriority, HEAL_PRIORITIES,
  // ВОЙ.1: Война Корпораций — снап-механика.
  declareWarSnap, respondWarSnap,
  type GlobalPriorityDef,
  type HealPriorityDef,
  type InfluenceCardTpl,
  type Match
} from '../pvp/engine-shim';
// ПвП.6.1: Colyseus-комната передаётся через singleton (MatchmakingScreen
// её туда кладёт после успешного match). При PvP-режиме подписываемся на
// onStateChange и обновляем match через адаптер.
import { getCurrentRoom, setCurrentRoom } from '../pvp/client';
import { colyseusToMatch, makeEmptyPvpMatch } from '../pvp/match-adapter';
import { playCombatEvents } from '../pvp/anim-replay';
import { useGameStore } from '../store/useGameStore';
import { HeroAvatar } from '../components/HeroAvatar';
import { GameCard } from '../components/GameCard';
import { ScriptsIcon } from '../components/ScriptsIcon';
import { getCardData, type CardData } from '../lib/cardData';
import { isInTelegram } from '../telegram/useTelegram';
import { Animator } from '../animation/Animator';
import { AnimationLayer } from '../animation/AnimationLayer';
import { play as playSfx } from '../audio/sfx';
import { PlayerName } from '../components/PlayerName';
import { UserAvatar } from '../components/UserAvatar';
import { finishMatch, abandonMatchLog, getProfile, type MatchOutcomeCode, type MatchFinishResult } from '../api/client';
import {
  IcoTaunt, IcoSilence, IcoImmune, IcoSuppression, IcoPierce,
  IcoInfected1, IcoInfected2, IcoInfected3
} from '../icons/keywordIcons';
import { useTr, tr as trStatic } from '../i18n/useTr';
import { trHeroName, trCardName, trLeague } from '../i18n/serverStrings';
import { useScreenTransition } from '../transitions';
import { LeagueTransitionModal, extractLeagueEvent, type LeagueEventData } from './LeagueTransition';
import { LevelUpModal, isMilestone } from './LevelUp';
import styles from './BattleScreen.module.css';

type Faction = 'deus' | 'syntetics' | 'global' | 'livlife' | 'neutral';

function factionShort(f: Faction): string {
  switch (f) {
    case 'syntetics': return 'syn';
    case 'global': return 'gsec';
    case 'livlife': return 'liv';
    case 'neutral': return 'neut';
    default: return 'deus';
  }
}
function factionLabelLong(f: Faction): string {
  // ФТУЕ.7: используем статический tr — функция вне React-компонента.
  const isEn = trStatic('ru', 'en') === 'en';
  if (isEn) {
    return ({
      deus: 'Deus Systems', syntetics: 'Synthetics', global: 'Global Security',
      livlife: 'Liv Life', neutral: 'Street'
    } as Record<Faction, string>)[f];
  }
  return ({
    deus: 'Деус Системс', syntetics: 'Синтетикс', global: 'Глобал Секьюрити',
    livlife: 'Лив Лайф', neutral: 'Улица'
  } as Record<Faction, string>)[f];
}
// Для обратной совместимости — оставляем FACTION_LABEL_LONG если где-то используется.
const FACTION_LABEL_LONG: Record<Faction, string> = {
  deus: 'Deus Systems',
  syntetics: 'Synthetics',
  global: 'Global Security',
  livlife: 'Liv Life',
  neutral: 'Улица'
};

function phaseLabel(phase: string): string {
  const isEn = trStatic('ru', 'en') === 'en';
  if (isEn) {
    switch (phase) {
      case PHASE.MULLIGAN: return 'mulligan';
      case PHASE.DRAW: return 'draw';
      case PHASE.PLACEMENT: return 'placement';
      case PHASE.REVEAL: return 'reveal';
      case PHASE.HERO_FIRST: return 'hero 1';
      case PHASE.HERO_SECOND: return 'hero 2';
      case PHASE.END_TURN: return 'end turn';
      case PHASE.GAME_OVER: return 'match over';
      default: return phase;
    }
  }
  switch (phase) {
    case PHASE.MULLIGAN: return 'мулиган';
    case PHASE.DRAW: return 'тяга';
    case PHASE.PLACEMENT: return 'размещение';
    case PHASE.REVEAL: return 'раскрытие';
    case PHASE.HERO_FIRST: return 'герой 1';
    case PHASE.HERO_SECOND: return 'герой 2';
    case PHASE.END_TURN: return 'конец хода';
    case PHASE.GAME_OVER: return 'матч окончен';
    default: return phase;
  }
}
function phaseNumber(phase: string): string {
  switch (phase) {
    case PHASE.DRAW: return '1';
    case PHASE.PLACEMENT: return '2';
    case PHASE.REVEAL: return '3';
    case PHASE.HERO_FIRST: return '4';
    case PHASE.HERO_SECOND: return '5';
    case PHASE.END_TURN: return '6';
    default: return '·';
  }
}

const HERO_NICKNAMES: Record<string, string> = {
  deus_tanaki: 'Танаки',
  deus_kirosa: 'Кироса',
  deus_nikosi: 'Никоси',
  deus_sina: 'Сина',
  neutral_lider_ulicy: 'Лидер'
};
const HERO_NICKNAMES_EN: Record<string, string> = {
  deus_tanaki: 'Tanaki',
  deus_kirosa: 'Kirosa',
  deus_nikosi: 'Nikosi',
  deus_sina: 'Sina',
  neutral_lider_ulicy: 'Leader'
};
function shortHeroName(hero: any): string | null {
  if (!hero?.id) return null;
  if (HERO_NICKNAMES[hero.id]) return HERO_NICKNAMES[hero.id];
  const parts = String(hero.name_ru || '').trim().split(/\s+/);
  if (parts.length > 1) return parts[parts.length - 1];
  return null;
}

// ────────── SVG-иконки ──────────
function IcoAtk() {
  return (
    <svg viewBox="0 0 16 16" width="9" height="9" fill="currentColor">
      <path d="M3 13l1 0 8-8 0-2-1-1-2 0-8 8 0 1zm9-12l1 1-1 1-1-1zM2 14l4 0 0 1-4 0z"/>
    </svg>
  );
}
function IcoArm() {
  return (
    <svg viewBox="0 0 16 16" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M8 1 L14 4 V8 C14 11.5 11 14 8 15 C5 14 2 11.5 2 8 V4 Z"/>
    </svg>
  );
}
function IcoHp() {
  return (
    <svg viewBox="0 0 16 16" width="9" height="9" fill="currentColor">
      <path d="M8 14s-5-3.5-5-7.5C3 4 4.5 3 6 3c1 0 1.5.5 2 1.5C8.5 3.5 9 3 10 3c1.5 0 3 1 3 3.5 0 4-5 7.5-5 7.5z"/>
    </svg>
  );
}
function IcoDeck() {
  return (
    <svg viewBox="0 0 16 16" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="3" y="4" width="9" height="11" rx="1"/>
      <path d="M5 2 H14 V13"/>
    </svg>
  );
}
function IcoDiscard() {
  return (
    <svg viewBox="0 0 16 16" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="3" y="4" width="9" height="11" rx="1"/>
      <line x1="5" y1="9" x2="10" y2="9"/>
    </svg>
  );
}

// ────────── Юнит на клетке ──────────
// Новая раскладка (по фидбеку юзера + диздок §6.4):
//   ┌─────────────────────────┐
//   │ имя (top strip)   [бейдж] │ ← top-right: ПРОВ / МОЛЧ / Заражён
//   │                            │
//   │       арт                  │
//   │                            │
//   │  ⚔ N  │  ◇ N  │  ♥ N       │ ← bottom: 3 стат-чипа в ряд
//   └─────────────────────────┘
// БУГ.14: цвет кружка редкости (на арте каждой карты).
// Соответствует дизайн-системе v0.1 (data-source/КОРПЫ · Дизайн-система v0.1.html):
//   common=#A8AEBE, rare=#3DF0FF, epic=#C93DFF, legendary=#FFB13D, special=#9CFF3D.
function rarityDotColor(rarity: string): string {
  switch (rarity) {
    case 'rare':              return '#3DF0FF';
    case 'epic':              return '#C93DFF';
    case 'legend':
    case 'legendary':         return '#FFB13D';
    case 'special':           return '#9CFF3D';
    case 'common':
    default:                  return '#A8AEBE';
  }
}

function UnitMini({ unit, data, faction, ghost }: {
  unit: any; data: CardData | null; faction: Faction; ghost?: boolean;
}) {
  const tr = useTr();
  const isEnLocal = tr('ru', 'en') === 'en';
  const atk = unit.current?.atk ?? unit.base?.atk ?? data?.atk ?? 0;
  const armor = unit.current?.armor ?? unit.base?.armor ?? data?.armor ?? 0;
  const hp = unit.current?.hp ?? unit.base?.hp ?? data?.hp ?? 0;
  const maxHp = unit.base?.hp ?? data?.hp ?? hp;
  const hpRatio = maxHp > 0 ? Math.max(0, Math.min(1, hp / maxHp)) : 1;
  const fShort = factionShort(faction);
  const rarity = (unit.rarity || data?.rarity || 'common');
  const name = unit.name_ru || data?.name_ru || unit.id;

  // БУГ.9: priority бейдж — на каждом юните на поле. Логика как в GameCard:
  //   global+attackPriority → выбор игрока; global без — random fallback;
  //   priority_override → override; иначе дефолт фракции.
  const priorityId = unit.attackPriority
    || unit.priority_override
    || (unit.faction === 'deus'      ? 'weakest'
       : unit.faction === 'syntetics' ? 'random'
       : unit.faction === 'livlife'   ? 'lowest_current_hp'
       : unit.faction === 'global'    ? 'random'
       : 'weakest');
  const priorityShort = (() => {
    switch (priorityId) {
      case 'strongest':         return isEnLocal ? 'STR' : 'СИЛ';
      case 'weakest':           return isEnLocal ? 'WK'  : 'СЛАБ';
      case 'wounded':           return isEnLocal ? 'WND' : 'РАН';
      case 'lowest_hp':
      case 'lowest_current_hp': return isEnLocal ? 'LO HP' : 'НИЗК HP';
      case 'healer':            return isEnLocal ? 'HEAL' : 'ХИЛ';
      case 'random':            return isEnLocal ? 'RND' : 'СЛУЧ';
      default:                  return priorityId.toUpperCase().slice(0, 5);
    }
  })();

  // Статусы из effective keywords + специальных полей engine.
  // Этап 80.4: берём ПЕРВЫЙ НЕПУСТОЙ источник. Раньше `current?.keywords ||
  // ...` брал пустой runtime-массив [] (он truthy) и затирал data.keywords —
  // из-за этого taunt/pierce/etc не показывались на юните, хотя в бою
  // работали (Корп охранник провоцировал, но бейджа Провокации не было).
  const _kwCur = unit.current?.keywords;
  const _kwUnit = unit.keywords;
  const effKeywords: string[] =
    (Array.isArray(_kwCur) && _kwCur.length) ? _kwCur
    : (Array.isArray(_kwUnit) && _kwUnit.length) ? _kwUnit
    : (data?.keywords || []);
  const isTaunt       = effKeywords.includes('taunt');
  const isStealth     = effKeywords.includes('stealth');
  const isImmune      = effKeywords.includes('immune_first_hit');
  const isSuppression = effKeywords.includes('suppression');
  const isPierce      = effKeywords.includes('pierce');
  const isSilenced    = !!unit.current?.silenced
                    || !!(unit.silenceSources && unit.silenceSources.size > 0);
  const infectedStacks = unit.infectedStacks || 0;
  // Mod-счётчик Синтетикс — длина массива unit.modifications. Adapter
  // в PvP синкает modifications через ArraySchema<ModInstance>; PvE
  // engine хранит как массив объектов на card.modifications.
  const modCount = Array.isArray(unit.modifications) ? unit.modifications.length : 0;

  // Выбор Infected-иконки по стаку.
  const InfectedIco = infectedStacks === 1 ? IcoInfected1
                    : infectedStacks === 2 ? IcoInfected2
                    : IcoInfected3;

  return (
    <div className={`${styles.unit} ${styles[`f-${fShort}`]} ${styles[`r-${rarity}`]} ${ghost ? styles.unitGhost : ''} ${isStealth ? styles.uStealth : ''}`}>
      <div className={styles.uArt}>
        <img
          src={`/img/cards/${unit.id}.jpg`}
          alt=""
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          loading="eager"
          decoding="async"
        />
        {/* Большой Immunity overlay — щит на весь арт. */}
        {isImmune && (
          <div className={styles.uImmuneOverlay} title={trStatic('Иммунитет к первому удару', 'Immune to first hit')}>
            <IcoImmune width="65%" height="65%" />
          </div>
        )}
        {/* Подавление — top-left на арте (Глобал кейворд). */}
        {isSuppression && (
          <div className={styles.uSuppressBadge} title={trStatic('Подавление', 'Suppression')}>
            <IcoSuppression width="12" height="12" />
          </div>
        )}
        {/* БУГ.14: кружок редкости — top-left на арте юнита (дизайн-система v0.1). */}
        <div
          className={styles.rarityDot}
          style={{ background: rarityDotColor(rarity) }}
          title={trStatic(`Редкость: ${rarity}`, `Rarity: ${rarity}`)}
        />
        {/* БУГ.9: priority бейдж — bottom-right на арте, короткий тег
            (СИЛ/СЛАБ/ХИЛ/СЛУЧ). Видно на каждом юните без открытия деталей. */}
        <div className={styles.uPriorityBadge} title={trStatic('Приоритет атаки', 'Attack priority')}>
          ◆ {priorityShort}
        </div>
      </div>

      {/* Имя сверху */}
      <div className={styles.uNameTop} title={name}>{name}</div>

      {/* Бейджи статусов — top-right (Провокация/Молчание стопкой) */}
      {(isTaunt || isSilenced || isPierce) && (
        <div className={styles.uBadges}>
          {isTaunt && (
            <span className={`${styles.uBadge} ${styles.uBadgeTaunt}`} title={trStatic('Провокация', 'Taunt')}>
              <IcoTaunt width="10" height="10" />
            </span>
          )}
          {isPierce && (
            <span className={styles.uBadge} style={{ background: 'rgba(10,20,30,0.85)', border: '1px solid #9fb3c8' }} title={trStatic('Пробитие', 'Pierce')}>
              <IcoPierce width="10" height="10" />
            </span>
          )}
          {isSilenced && (
            <span className={`${styles.uBadge} ${styles.uBadgeSilence}`} title={trStatic('Молчание', 'Silence')}>
              <IcoSilence width="10" height="10" />
            </span>
          )}
        </div>
      )}

      {/* Стак Заражён — bottom-strip на арте. По одному значку на стак,
          ряд из 1-3 элементов. */}
      {infectedStacks > 0 && (
        <div className={styles.uInfectedStrip} title={`Заражён x${infectedStacks}`}>
          {Array.from({ length: Math.min(3, infectedStacks) }, (_, i) => (
            <InfectedIco key={i} width="12" height="12" />
          ))}
        </div>
      )}

      {/* Mod-счётчик Синтетикс — bottom-right на арте. Показывает количество
          установленных Модов (макс. 3-4 до Перегрузки). */}
      {modCount > 0 && (
        <div
          style={{
            position: 'absolute',
            right: 4,
            bottom: 36,
            background: 'rgba(20, 0, 40, 0.85)',
            border: '1px solid var(--syn, #b06bff)',
            color: 'var(--syn, #b06bff)',
            borderRadius: 3,
            padding: '1px 4px',
            fontSize: 10,
            fontWeight: 'bold',
            fontFamily: 'monospace',
            lineHeight: '12px',
            zIndex: 3,
          }}
          title={`Установлено Модов: ${modCount}`}
        >
          ⚙ {modCount}
        </div>
      )}

      {/* HP bar — над статами */}
      <div className={styles.uHpBar}>
        <div
          className={`${styles.uHpFill} ${hpRatio < 0.25 ? styles.hpLow : hpRatio < 0.5 ? styles.hpMid : ''}`}
          style={{ width: `${hpRatio * 100}%` }}
        />
      </div>

      {/* 3 стата в ряд: ATK | ARM | HP */}
      <div className={styles.uStatsRow}>
        <span className={`${styles.uStat} ${styles.uStatAtk} ${atk === 0 ? styles.zero : ''}`}>{atk}</span>
        <span className={`${styles.uStat} ${styles.uStatArm} ${armor === 0 ? styles.zero : ''}`}>{armor}</span>
        <span className={`${styles.uStat} ${styles.uStatHp} ${hpRatio < 0.34 ? styles.hpLow : hpRatio < 0.67 ? styles.hpMid : ''}`}>{hp}</span>
      </div>

      {ghost && <div className={styles.ghostMark}>⏳</div>}
    </div>
  );
}

// ────────── Клетка поля ──────────
function FieldCell({
  unit, faceDown, faction, pendingCard, isValidDrop, isHoverDrop, dropZone, slotIdx,
  side, lineIdx,
  isAttackDropTarget, isAttackHover, isActionPickTarget,
  isStealthActivatable, isStealthArmed,
  onUnitTap
}: {
  unit: any; faceDown?: boolean; faction: Faction; pendingCard?: any;
  isValidDrop?: boolean; isHoverDrop?: boolean; dropZone: string; slotIdx: number;
  // АНИМ.11: side и lineIdx нужны для data-* атрибутов, по которым
  // animator находит rect ячейки (для MOVE).
  side: 'my' | 'enemy';
  lineIdx: 0 | 1;
  isAttackDropTarget?: boolean;
  isAttackHover?: boolean;
  isActionPickTarget?: boolean;  // unit-cell highlighted cyan для action picker
  isStealthActivatable?: boolean; // мой стелс, можно тапнуть для активации
  isStealthArmed?: boolean;       // мой стелс, уже выбран как «armed»
  onUnitTap?: (unit: any) => void;
}) {
  // АНИМ.11: общие data-* атрибуты для slot-lookup. Ставятся на ВСЕХ
  // вариантах (pending/empty/face-down/active) чтобы fromRect MOVE
  // нашёлся независимо от состояния ячейки.
  const slotAttrs = {
    'data-side': side,
    'data-line': lineIdx,
    'data-slot': slotIdx,
  };
  if (pendingCard) {
    const data = getCardData(pendingCard.id);
    return (
      <div className={`${styles.cell} ${styles.cellPending}`}
           data-drop-zone={dropZone}
           data-instance-id={pendingCard.instanceId}
           {...slotAttrs}>
        <UnitMini unit={pendingCard} data={data} faction={faction} ghost />
      </div>
    );
  }
  if (!unit) {
    return (
      <div
        className={`${styles.cell} ${styles.cellEmpty} ${isValidDrop ? styles.cellValid : ''} ${isHoverDrop ? styles.cellTarget : ''}`}
        data-drop-zone={isValidDrop ? dropZone : ''}
        {...slotAttrs}
      />
    );
  }
  if (faceDown) {
    // Face-down (скрытая карта врага в Ф2) — НЕ кликабельна и не таргетируется.
    // АНИМ.5-fix-facedown-rect: data-instance-id нужен чтобы анимация
    // reveal в Ф3 могла найти позицию юнита (anim приходит до того как
    // schema успеет revealed=true).
    return (
      <div
        className={`${styles.cell} ${styles.cellHidden}`}
        data-instance-id={unit?.instanceId}
        {...slotAttrs}
      >
        <div className={styles.hiddenMark}>?</div>
      </div>
    );
  }
  const data = getCardData(unit.id);
  const clickable = !!onUnitTap;
  // Подсветка стелса: armed = яркая (фракционный цвет + жирная рамка),
  // activatable = пунктирная амбер-рамка с подсказкой.
  const stealthStyle: React.CSSProperties = isStealthArmed
    ? { outline: '2px solid var(--neon-cy)', outlineOffset: -2 }
    : isStealthActivatable
      ? { outline: '2px dashed var(--neon-am)', outlineOffset: -2 }
      : {};
  return (
    <div
      className={`${styles.cell} ${isAttackDropTarget ? styles.cellAttackTarget : ''} ${isAttackHover ? styles.cellAttackHover : ''} ${isActionPickTarget ? styles.cellActionPick : ''}`}
      data-attack-drop={isAttackDropTarget ? `unit-${unit.instanceId}` : undefined}
      data-instance-id={unit.instanceId}
      {...slotAttrs}
      onClick={clickable ? () => onUnitTap!(unit) : undefined}
      style={clickable ? { cursor: 'pointer', ...stealthStyle } : stealthStyle}
    >
      <UnitMini unit={unit} data={data} faction={faction} />
      {isStealthActivatable && !isStealthArmed && (
        <div style={{
          position: 'absolute', top: 2, right: 2,
          fontSize: 11, fontWeight: 700, color: 'var(--neon-am)',
          background: 'rgba(0,0,0,0.85)', padding: '1px 4px',
          letterSpacing: '0.05em'
        }}>👁⚔</div>
      )}
      {isStealthArmed && (
        <div style={{
          position: 'absolute', top: 2, right: 2,
          fontSize: 9, fontWeight: 700, color: 'var(--neon-cy)',
          background: 'rgba(0,0,0,0.8)', padding: '1px 4px',
          letterSpacing: '0.05em'
        }}>◉</div>
      )}
    </div>
  );
}

// ────────── Линия ──────────
function LaneRow({
  cells, isEnemy, faction, lineNum, pendingMap, dragValidLine, hoverCell, dropZoneBase,
  attackDropMode, hoverAttackId, isValidHeroTarget,
  actionPickTargetIds, onActionPick,
  myStealthActivatable, myStealthArmedId,
  onUnitTap
}: {
  cells: any[]; isEnemy: boolean; faction: Faction; lineNum: 1 | 2;
  pendingMap: Map<string, any>; dragValidLine: boolean;
  hoverCell: { line: 0 | 1; slot: number } | null; dropZoneBase: string;
  attackDropMode?: boolean;
  hoverAttackId?: number;
  // Проверка валидности юнита как цели Героя (Provocation/Stealth/etc).
  // Если не задан — fallback на простую проверку stealth.
  isValidHeroTarget?: (unit: any) => boolean;
  actionPickTargetIds?: Set<number>;
  onActionPick?: (unit: any) => void;
  // ID моих стелс-юнитов, которые можно активировать сейчас.
  myStealthActivatable?: Set<number>;
  // ID активного «armed» стелс-юнита (если игрок выбрал его).
  myStealthArmedId?: number | null;
  onUnitTap?: (unit: any) => void;
}) {
  const lineIdx = (lineNum === 1 ? 0 : 1) as 0 | 1;
  return (
    <div className={styles.laneRow}>
      <div className={`${styles.laneTag} ${isEnemy ? styles.tagEnemy : styles.tagMe}`}>
        {lineNum}Л
      </div>
      <div className={styles.lane}>
        {cells.map((unit, idx) => {
          const key = `${lineIdx}-${idx}`;
          const pending = pendingMap.get(key);
          const isEmpty = !unit && !pending;
          const valid = dragValidLine && isEmpty;
          const hover = !!hoverCell && hoverCell.line === lineIdx && hoverCell.slot === idx && valid;
          // Drop-target для атаки Героем: только живой ВРАЖЕСКИЙ юнит,
          // удовлетворяющий правилам диздок §6.5 (Stealth/Taunt/untargetable).
          // ВАЖНО: используем isValidHeroTarget — это callback из родителя,
          // который знает про Provocation на этой стороне (нужны все юниты
          // фракции чтобы проверить Taunt-правило).
          const isDropTarget = attackDropMode === true && isEnemy && unit && unit.alive
            && unit.revealed !== false
            && (isValidHeroTarget ? isValidHeroTarget(unit) : !unit.keywords?.includes('stealth'));
          const isHover = isDropTarget && hoverAttackId === unit?.instanceId;
          // ActionPick: подсветить валидную цель Действия (cyan).
          const isPickTarget = !!actionPickTargetIds && unit && actionPickTargetIds.has(unit.instanceId);
          // В режиме pick — клик по unit выполняет pick (а не открывает деталь).
          const tapHandler = isPickTarget && onActionPick
            ? () => onActionPick(unit)
            : onUnitTap;
          return (
            <FieldCell
              key={key}
              unit={unit}
              faceDown={isEnemy && unit && unit.revealed === false}
              faction={faction}
              pendingCard={pending}
              isValidDrop={valid}
              isHoverDrop={hover}
              dropZone={`${dropZoneBase}-${lineIdx}`}
              slotIdx={idx}
              side={isEnemy ? 'enemy' : 'my'}
              lineIdx={lineIdx}
              isAttackDropTarget={isDropTarget}
              isAttackHover={isHover}
              isActionPickTarget={isPickTarget}
              // Подсветка моего стелс-юнита: жёлтое свечение если можно
              // активировать; cyan-bright если уже выбран как «armed».
              isStealthActivatable={!!myStealthActivatable && unit && myStealthActivatable.has(unit.instanceId)}
              isStealthArmed={!!unit && myStealthArmedId === unit.instanceId}
              onUnitTap={tapHandler}
            />
          );
        })}
      </div>
    </div>
  );
}

// ────────── Hero bar ──────────
function HeroBar({
  player, faction, isEnemy, onHeroTap, onDeckTap, onDiscardTap, onStandTap,
  isAttackDropTarget, isAttackHover,
  showAttackBtn, attackBtnArmed, onAttackBtnClick
}: {
  player: any; faction: Faction; isEnemy: boolean;
  onHeroTap: () => void;
  onDeckTap: () => void;
  onDiscardTap: () => void;
  onStandTap?: () => void;
  isAttackDropTarget?: boolean;
  isAttackHover?: boolean;
  // Только для моего pbar: показывать кнопку АТАКА (если моя HERO-фаза,
  // Герой ещё не бил). attackBtnArmed = true когда уже нажата (cells
  // подсвечены, нужно отменить или выбрать цель).
  showAttackBtn?: boolean;
  attackBtnArmed?: boolean;
  onAttackBtnClick?: () => void;
}) {
  const hero = player.hero;
  const atk = hero.current?.atk ?? hero.base?.atk ?? 0;
  const armor = hero.current?.armor ?? hero.base?.armor ?? 0;
  const hp = hero.current?.hp ?? hero.base?.hp ?? 0;
  const maxHp = hero.base?.hp ?? hp;
  const hpRatio = maxHp > 0 ? hp / maxHp : 1;
  const hpClass = hpRatio < 0.25 ? styles.hpLow : hpRatio < 0.5 ? styles.hpMid : '';
  const fShort = factionShort(faction);
  const deckCount = player.deck?.length ?? 0;
  const discardCount = player.discard?.length ?? 0;
  const handCount = player.hand?.length ?? 0;
  const heroName = trHeroName(hero.id, hero.name_ru || hero.id);
  const heroNick = shortHeroName(hero);

  // Атака выполняется через ⚔ АТАКА кнопку → выбор цели тапом.
  return (
    <div
      className={`${styles.pbar} ${isEnemy ? styles.pbarEnemy : styles.pbarMe} ${styles[`f-${fShort}`]} ${isAttackDropTarget ? styles.pbarAttackTarget : ''} ${isAttackHover ? styles.pbarAttackHover : ''}`}
    >
      <button
        className={`${styles.av} ${styles[`av-${fShort}`]} ${styles.avTap}`}
        onClick={onHeroTap}
        title={trStatic('Способности Героя', 'Hero abilities')}
        // ПвП.8-fix-hero-rect: BattleAnimator.cardEl ищет DOM по
        // data-instance-id. Hero в engine не имеет instanceId, поэтому
        // без fallback'а ghost-анимация для Hero (как attacker или target)
        // не находит DOM-элемент → fallback wait без overlay. Стабильный
        // ID: моя сторона -1, вражеская -2 (никогда не конфликтует с
        // unit'ами — у тех instanceId > 0).
        data-instance-id={hero.instanceId || (isEnemy ? -2 : -1)}
      >
        <HeroAvatar id={hero.id} size={36} faction={faction} shape="square" />
      </button>
      <button className={styles.pbarInfo} onClick={onHeroTap}>
        <div className={styles.pbarHeroName}>{heroName}</div>
        <div className={styles.pbarSub}>
          {heroNick && heroNick !== heroName
            ? `«${heroNick}»`
            : factionLabelLong(faction)}
          {faction === 'deus' && player.influence != null && (
            <span className={styles.pbarInfl} title={trStatic('Влияние', 'Influence')}> · ◆ {player.influence}</span>
          )}
        </div>
      </button>
      <div className={styles.pbarStatsCol}>
        <div className={styles.pbarStatsRow}>
          <span className={`${styles.statChip} ${styles.statAtk}`} title={trStatic('Атака Героя', 'Hero attack')}>
            <IcoAtk />{atk}
          </span>
          <span className={`${styles.statChip} ${styles.statArm}`} title={trStatic('Броня', 'Armor')}>
            <IcoArm />{armor}
          </span>
          <span className={`${styles.statChip} ${styles.statHp} ${hpClass}`} title={trStatic('Здоровье', 'Health')}>
            <IcoHp />{hp}
          </span>
        </div>
        <div className={styles.pbarStatsRow}>
          <span className={styles.statChip} title={trStatic('В руке', 'In hand')}>✋{handCount}</span>
          <button
            className={`${styles.statChip} ${styles.statChipBtn}`}
            onClick={onDeckTap}
            title={trStatic('Колода — посмотреть', 'Deck — peek')}
            disabled={deckCount === 0}
            data-deck={isEnemy ? 'enemy' : 'my'}
          >
            <IcoDeck />{deckCount}
          </button>
          <button
            className={`${styles.statChip} ${styles.statChipBtn}`}
            onClick={onDiscardTap}
            title={trStatic('Сброс — посмотреть', 'Discard — peek')}
            disabled={discardCount === 0}
            data-discard={isEnemy ? 'enemy' : 'my'}
          >
            <IcoDiscard />{discardCount}
          </button>
        </div>
      </div>
      <div className={styles.actives}>
        <button className={`${styles.act} ${styles.actReady}`} disabled title={trStatic('Активка (БОЙ.1.3)', 'Active (BOY.1.3)')}>◆</button>
        {/* Стенд Влияния — только для Деус-игрока. Показывает 3 карты
            оффера для розыгрыша в Ф2. */}
        {faction === 'deus' && onStandTap && (
          <button
            className={`${styles.act} ${styles.actStand}`}
            onClick={onStandTap}
            title={trStatic(
              `Стенд Влияния — оффер ${player.influenceOffer?.length ?? 0} карт${
                (player.influenceOffer?.length === 1) ? 'а' : ''
              }`,
              `Influence stand — offer ${player.influenceOffer?.length ?? 0} card${
                (player.influenceOffer?.length === 1) ? '' : 's'
              }`
            )}
          >
            <span className={styles.actStandLbl}>{trStatic('СТЕНД', 'STAND')}</span>
            <span className={styles.actStandNum}>{player.influenceOffer?.length ?? 0}</span>
          </button>
        )}
        {/* Кнопка АТАКА (только моя HERO-фаза, Герой ещё не бил).
            Тап → подсветка валидных целей; повторный тап = отмена. */}
        {showAttackBtn && (
          <button
            className={`${styles.act} ${styles.actAttack} ${attackBtnArmed ? styles.actAttackArmed : ''}`}
            onClick={onAttackBtnClick}
            title={attackBtnArmed ? trStatic('Отменить выбор цели', 'Cancel target selection') : trStatic('Атаковать Героем — выбрать цель', 'Attack with Hero — pick target')}
          >
            <span className={styles.actAttackLbl}>{attackBtnArmed ? trStatic('ОТМЕНА', 'CANCEL') : trStatic('АТАКА', 'ATTACK')}</span>
            <span className={styles.actAttackIco}>{attackBtnArmed ? '✕' : '⚔'}</span>
          </button>
        )}
      </div>
    </div>
  );
}

// ────────── Карта в руке ──────────
// Tap (chip-click) для открытия деталей или отмены pending — через
// React `onClick`. Drag для размещения — через native touch+mouse
// listeners (см. registerDragHandler). Drag активируется ТОЛЬКО при
// движении >6px вертикально — для чистого tap'а движение нулевое,
// и onClick срабатывает естественно.
function HandCard({
  card, faction, isPending, isDragging, registerDragHandler, onTap
}: {
  card: any; faction: Faction; isPending: boolean; isDragging: boolean;
  registerDragHandler: (el: HTMLDivElement | null, card: any, isPending: boolean) => void;
  onTap: (card: any, isPending: boolean) => void;
}) {
  const data = getCardData(card.id);
  const fShort = factionShort(faction);
  const rarity = (card.rarity || data?.rarity || 'common');
  const atk = card.base?.atk ?? data?.atk ?? 0;
  const armor = card.base?.armor ?? data?.armor ?? 0;
  const hp = card.base?.hp ?? data?.hp ?? 0;
  const isUnit = (card.type || data?.type) === 'unit';
  const isAction = (card.type || data?.type) === 'action';
  const name = card.name_ru || data?.name_ru || card.id;

  return (
    <div
      ref={(el) => registerDragHandler(el, card, isPending)}
      className={`${styles.hcard} ${styles[`f-${fShort}`]} ${styles[`r-${rarity}`]} ${isPending ? styles.hcardPending : ''} ${isDragging ? styles.hcardDragging : ''}`}
      onClick={() => onTap(card, isPending)}
    >
      <div className={styles.hcardArt}>
        <img
          src={`/img/cards/${card.id}.jpg`}
          alt=""
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          loading="eager"
          decoding="async"
        />
        {/* БУГ.14: кружок редкости — top-left. */}
        <div
          className={styles.rarityDot}
          style={{ background: rarityDotColor(rarity) }}
          title={trStatic(`Редкость: ${rarity}`, `Rarity: ${rarity}`)}
        />
      </div>
      <div className={styles.hcardName}>{name}</div>
      {isUnit && (
        <div className={styles.hcardStats}>
          <span className={styles.hcardStat} style={{ color: 'var(--neon-am)' }}>{atk}</span>
          <span className={styles.hcardStat} style={{ color: 'var(--neon-cy)' }}>{armor}</span>
          <span className={styles.hcardStat} style={{ color: 'var(--neon-li)' }}>{hp}</span>
        </div>
      )}
      {isAction && <div className={styles.hcardStats}><span className={styles.hcardType}>{trStatic('ДЕЙСТВ.', 'ACTION')}</span></div>}
      {!isUnit && !isAction && <div className={styles.hcardStats}><span className={styles.hcardType}>{trStatic('МОД', 'MOD')}</span></div>}
      {isPending && <div className={styles.hcardPendingMark}>✓</div>}
    </div>
  );
}

// ────────── Мулиган-карта: стат-чипы + кнопка ? для деталей ──────────
function MulliganCard({
  card, faction, isSelected, onToggle, onDetail
}: {
  card: any; faction: Faction; isSelected: boolean;
  onToggle: () => void; onDetail: () => void;
}) {
  const data = getCardData(card.id);
  const fShort = factionShort(faction);
  const rarity = (card.rarity || data?.rarity || 'common');
  const atk = card.base?.atk ?? data?.atk ?? 0;
  const armor = card.base?.armor ?? data?.armor ?? 0;
  const hp = card.base?.hp ?? data?.hp ?? 0;
  const isUnit = (card.type || data?.type) === 'unit';
  const isAction = (card.type || data?.type) === 'action';
  const name = card.name_ru || data?.name_ru || card.id;

  return (
    <div
      className={`${styles.mulHcard} ${styles[`f-${fShort}`]} ${styles[`r-${rarity}`]} ${isSelected ? styles.mulSelected : ''}`}
      onClick={onToggle}
    >
      <div className={styles.hcardArt}>
        <img
          src={`/img/cards/${card.id}.jpg`}
          alt=""
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />
        {/* БУГ.14: кружок редкости в мулигане тоже. */}
        <div
          className={styles.rarityDot}
          style={{ background: rarityDotColor(rarity) }}
          title={trStatic(`Редкость: ${rarity}`, `Rarity: ${rarity}`)}
        />
      </div>
      <div className={styles.hcardName}>{name}</div>
      {isUnit && (
        <div className={styles.hcardStats}>
          <span className={styles.hcardStat} style={{ color: 'var(--neon-am)' }}>{atk}</span>
          <span className={styles.hcardStat} style={{ color: 'var(--neon-cy)' }}>{armor}</span>
          <span className={styles.hcardStat} style={{ color: 'var(--neon-li)' }}>{hp}</span>
        </div>
      )}
      {isAction && <div className={styles.hcardStats}><span className={styles.hcardType}>{trStatic('ДЕЙСТВ.', 'ACTION')}</span></div>}
      {!isUnit && !isAction && <div className={styles.hcardStats}><span className={styles.hcardType}>{trStatic('МОД', 'MOD')}</span></div>}
      {isSelected && <div className={styles.mulCheck}>✕</div>}
      <button
        className={styles.mulDetailBtn}
        onClick={(e) => { e.stopPropagation(); onDetail(); }}
        title={trStatic('Открыть детали', 'Open details')}
      >🔍</button>
    </div>
  );
}

// ────────── Мулиган-оверлей ──────────
function MulliganModal({
  hand, faction, onDone, onCardDetail, deadlineAt
}: {
  hand: any[]; faction: Faction;
  onDone: (selectedIndices: number[]) => void;
  onCardDetail: (card: any) => void;
  // МУЛ.1: timestamp истечения 20с таймера; null = без таймера (PvE).
  deadlineAt?: number | null;
}) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // ПвП.fix-button: внутренний флаг «я уже нажал ГОТОВО, жду соперника».
  // В PvE сразу onDone → finishMulligan, модалка закрывается; флаг не
  // активируется. В PvP onDone отправляет на сервер, модалка остаётся
  // открытой пока сервер не сменит фазу.
  const [acked, setAcked] = useState(false);

  // Этап 71: стингер при старте мулигана (рука показана, пошёл отсчёт).
  useEffect(() => { playSfx('phase'); }, []);

  // МУЛ.1: countdown 20с с moment'а phase=MULLIGAN.
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  useEffect(() => {
    if (!deadlineAt) { setRemainingMs(null); return; }
    const tick = () => setRemainingMs(Math.max(0, deadlineAt - Date.now()));
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [deadlineAt]);

  const toggle = (idx: number) => {
    if (acked) return;
    const next = new Set(selected);
    if (next.has(idx)) next.delete(idx);
    else {
      if (next.size >= 3) return;
      next.add(idx);
    }
    setSelected(next);
  };

  const count = selected.size;
  const isEnTr = trStatic('ru', 'en') === 'en';
  const btnText = acked
    ? (isEnTr ? '⌛ WAITING FOR OPPONENT' : '⌛ ЖДЁМ СОПЕРНИКА')
    : count === 0
      ? (isEnTr ? 'KEEP AS IS' : 'ОСТАВИТЬ КАК ЕСТЬ')
      : (isEnTr
        ? `SWAP ${count} ${count === 1 ? 'CARD' : 'CARDS'}`
        : `СМЕНИТЬ ${count} ${count === 1 ? 'КАРТУ' : count < 5 ? 'КАРТЫ' : 'КАРТ'}`);

  const remainingSec = remainingMs != null ? Math.ceil(remainingMs / 1000) : null;
  const urgent = remainingSec != null && remainingSec <= 5;

  return (
    <div className={styles.mulOverlay}>
      <div className={styles.mulCard}>
        <div className={styles.mulHeader}>
          <div className={styles.mulTitle}>{trStatic('МУЛИГАН', 'MULLIGAN')}</div>
          <div className={styles.mulSub}>
            {trStatic('тап — выбрать для замены · 🔍 — детали', 'tap — mark for swap · 🔍 — details')}
            {remainingSec != null && (
              <>
                {' · '}
                <span style={{
                  color: urgent ? 'var(--neon-rd)' : 'var(--neon-am)',
                  fontWeight: 700,
                }}>
                  ⏱ {remainingSec}с
                </span>
              </>
            )}
          </div>
        </div>
        <div className={styles.mulGrid}>
          {hand.map((c, idx) => (
            <MulliganCard
              key={c.instanceId}
              card={c}
              faction={faction}
              isSelected={selected.has(idx)}
              onToggle={() => toggle(idx)}
              onDetail={() => onCardDetail(c)}
            />
          ))}
        </div>
        <button
          className={`${styles.mulDoneBtn} ${count > 0 && !acked ? styles.mulDoneActive : ''}`}
          disabled={acked}
          onClick={() => {
            if (acked) return;
            setAcked(true);
            onDone(Array.from(selected));
          }}
        >
          {btnText}
        </button>
        <div className={styles.mulHint}>
          {trStatic(
            'Мулиган бывает только в начале матча. Выбранные карты вернутся в низ колоды, и ты возьмёшь столько же сверху.',
            'Mulligan only at the start of the match. Selected cards go to the bottom of the deck, you draw the same number from the top.'
          )}
        </div>
      </div>
    </div>
  );
}

// ────────── Лог боя ──────────
// Engine ведёт `match.log.entries: string[]` (append-only). Тут просто
// показываем последние 80 записей с цветовой подсветкой по префиксу.
function LogModal({ logEntries, onClose }: {
  logEntries: string[];
  onClose: () => void;
}) {
  // Самые свежие — сверху.
  const entries = [...logEntries].reverse().slice(0, 100);
  return (
    <div className={styles.cdmOverlay} onClick={onClose}>
      <div className={styles.logBox} onClick={(e) => e.stopPropagation()}>
        <div className={styles.logHeader}>
          <div className={styles.logTitle}>{trStatic('ЛОГ БОЯ', 'BATTLE LOG')}</div>
          <div className={styles.logCount}>{logEntries.length} {trStatic('ru', 'en') === 'en' ? 'entries' : 'записей'}</div>
          <button
            className={styles.logCopyBtn}
            onClick={async () => {
              const text = logEntries.join('\n');
              try {
                await navigator.clipboard.writeText(text);
                alert(trStatic('Лог скопирован', 'Log copied'));
              } catch {
                // Fallback: создаём textarea и select
                const ta = document.createElement('textarea');
                ta.value = text;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.select();
                try { document.execCommand('copy'); alert(trStatic('Лог скопирован', 'Log copied')); }
                catch { alert(trStatic('Не удалось скопировать', 'Failed to copy')); }
                ta.remove();
              }
            }}
            title={trStatic('Скопировать весь лог в буфер', 'Copy full log to clipboard')}
          >📋 {trStatic('КОПИЯ', 'COPY')}</button>
          <button className={styles.pileClose} onClick={onClose}>×</button>
        </div>
        <div className={styles.logList}>
          {entries.length === 0 && (
            <div className={styles.logEmpty}>{trStatic('— пусто —', '— empty —')}</div>
          )}
          {entries.map((line, i) => {
            // Цветовая подсветка по содержанию.
            const isTurn = /^═══ Ход/.test(line) || /═══ Матч/.test(line);
            const isPhase = /^— Фаза/.test(line);
            const isWin = /^🏆|^💀/.test(line);
            const isHero = /герой|Герой/.test(line);
            const isAttack = /\d+ урона|урон/.test(line);
            const isDeath = /умер|погиб|сброс/.test(line);
            let cls = styles.logLine;
            if (isTurn) cls = `${styles.logLine} ${styles.logTurn}`;
            else if (isPhase) cls = `${styles.logLine} ${styles.logPhase}`;
            else if (isWin) cls = `${styles.logLine} ${styles.logWin}`;
            else if (isDeath) cls = `${styles.logLine} ${styles.logDeath}`;
            else if (isAttack) cls = `${styles.logLine} ${styles.logAttack}`;
            else if (isHero) cls = `${styles.logLine} ${styles.logHero}`;
            return <div key={entries.length - i - 1} className={cls}>{line}</div>;
          })}
        </div>
        <div className={styles.pileHint}>
          {trStatic(
            'Полная история событий матча. Самые свежие сверху.',
            'Full match event history. Newest on top.'
          )}
        </div>
      </div>
    </div>
  );
}

// ────────── Стенд Влияния (Деус) ──────────
// Показывает offer (до 3 карт). Игрок видит cost, имя, описание и
// может тапнуть «Сыграть». Целевые карты пока показывают пометку
// «нужна цель — БОЙ.1.3» и disabled (target picker — будущий этап).
function InfluenceStandModal({
  match, player, offerIds, influence, isPlacement,
  onClose, onPlay, onCardDetail
}: {
  match: Match;
  player: any;
  offerIds: string[];
  influence: number;
  isPlacement: boolean;
  onClose: () => void;
  onPlay: (cardId: string) => void;
  onCardDetail: (tpl: InfluenceCardTpl) => void;
}) {
  const templates = getInfluenceCardTemplates('deus', offerIds);

  return (
    <div className={styles.cdmOverlay} onClick={onClose}>
      <div className={styles.standBox} onClick={(e) => e.stopPropagation()}>
        <div className={styles.standHeader}>
          <div className={styles.standTitle}>{trStatic('СТЕНД ВЛИЯНИЯ', 'INFLUENCE STAND')}</div>
          <div className={styles.standInfl}>◆ {influence} <span className={styles.standInflSub}>/ 10</span></div>
          <button className={styles.pileClose} onClick={onClose}>×</button>
        </div>
        <div className={styles.standSub}>
          {isPlacement
            ? trStatic(
              `Выбери карту чтобы разыграть в Ф2. Лимит ${INFLUENCE_PER_CARD_LIMIT} копии за матч.`,
              `Pick a card to play in P2. Limit ${INFLUENCE_PER_CARD_LIMIT} copies per match.`
            )
            : trStatic(
              'Играть карты Влияния можно только в Фазу 2 (Размещение).',
              'Influence cards can only be played in Phase 2 (Placement).'
            )}
        </div>
        {templates.length === 0 && (
          <div className={styles.standEmpty}>
            {trStatic('— оффер пуст —', '— offer is empty —')}<br/>
            {trStatic('Стенд обновляется в начале каждого хода.', 'Stand refreshes at the start of each turn.')}
          </div>
        )}
        <div className={styles.standGrid}>
          {templates.map((tpl) => {
            const cost = tpl.influence_cost ?? 0;
            const used = getInfluencePlayCount(player, tpl.id);
            const left = INFLUENCE_PER_CARD_LIMIT - used;
            const can = canPlayInfluenceCard(match, 0, tpl.id);
            const targetNeeded = needsInfluenceTarget(tpl.id);
            const enough = influence >= cost;
            // БОЙ.1.3-fix (Этап 80): таргетные карты ТОЖЕ играбельны — по тапу
            // открывается выбор цели на поле (как у Действий). Раньше были disabled.
            const enabled = isPlacement && can.ok && enough;
            const abilityText = tpl.ability_text?.active || tpl.ability_short || '';
            return (
              <div key={tpl.id} className={styles.standCard}>
                <div className={styles.standCardHead}>
                  <span className={styles.standCardName}>{tpl.name_ru}</span>
                  <span className={`${styles.standCost} ${!enough ? styles.standCostBad : ''}`}>◆ {cost}</span>
                </div>
                <div className={styles.standCardText}>{abilityText}</div>
                <div className={styles.standCardMeta}>
                  <span>осталось копий: <b>{left}</b> / {INFLUENCE_PER_CARD_LIMIT}</span>
                  {targetNeeded && enough && (
                    <span className={styles.standCardTargetWarn}>🎯 выбор цели на поле</span>
                  )}
                  {!enough && (
                    <span className={styles.standCardTargetWarn}>⚠ не хватает Влияния</span>
                  )}
                </div>
                <div className={styles.standCardBtns}>
                  <button
                    className={styles.standDetailBtn}
                    onClick={() => onCardDetail(tpl)}
                  >🔍 Подробнее</button>
                  <button
                    className={`${styles.standPlayBtn} ${enabled ? styles.standPlayBtnActive : ''}`}
                    disabled={!enabled}
                    onClick={() => onPlay(tpl.id)}
                  >
                    {!enough ? '◆?' : targetNeeded ? '► ЦЕЛЬ' : '► СЫГРАТЬ'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ────────── Модалка деталей Влияния (text-only) ──────────
function InfluenceDetailModal({ tpl, onClose }: { tpl: InfluenceCardTpl; onClose: () => void }) {
  const tr = useTr();
  const at = tpl.ability_text || {};
  const blocks: Array<{ label: string; text: string; color: string }> = [];
  if (at.active)      blocks.push({ label: tr('Эффект', 'Effect'),         text: at.active, color: 'var(--neon-am)' });
  if (at.aura)        blocks.push({ label: tr('Аура', 'Aura'),             text: at.aura, color: 'var(--neon-li)' });
  if (at.trigger)     blocks.push({ label: tr('Триггер', 'Trigger'),       text: at.trigger, color: 'var(--neon-cy)' });
  if (at.order)       blocks.push({ label: tr('Приказ', 'Order'),          text: at.order, color: 'var(--neon-mg)' });
  if (at.deathrattle) blocks.push({ label: tr('Посмертие', 'Deathrattle'), text: at.deathrattle, color: 'var(--neon-rd)' });

  return (
    <div className={styles.cdmOverlay} onClick={onClose}>
      <div className={styles.heroDetailBox} onClick={(e) => e.stopPropagation()}>
        <button className={styles.cdmClose} onClick={onClose}>×</button>
        <div className={styles.heroDetailHeader}>
          <div className={styles.influenceCostBig}>◆ {tpl.influence_cost ?? 0}</div>
          <div className={styles.heroDetailInfo}>
            <div className={styles.heroDetailName}>{tpl.name_ru}</div>
            <div className={styles.heroDetailArch}>{tr('Карта Влияния', 'Influence card')} · Deus Systems</div>
          </div>
        </div>
        {blocks.length > 0 ? (
          <div className={styles.heroDetailBlocks}>
            {blocks.map((b, i) => (
              <div key={i} className={styles.heroDetailBlock}>
                <span className={styles.heroDetailLbl} style={{ color: b.color }}>{b.label}</span>
                <span className={styles.heroDetailText}>{b.text}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.heroDetailEmpty}>{tpl.ability_short || tr('— описание отсутствует —', '— no description —')}</div>
        )}
      </div>
    </div>
  );
}

// ────────── Модалка деталей карты ──────────
// actionCtx — добавляет кнопку «СЫГРАТЬ» / «ОТМЕНИТЬ» внизу,
// если это карта-Действие в моей руке во время Ф2.
function CardDetailModal({
  card, runtimeCard, onClose, actionCtx, initiationWarn
}: {
  card: CardData;
  /** Engine-instance карты (если есть) — для runtime-полей вроде
   *  attackPriority. Если не передан — GameCard покажет дефолт фракции. */
  runtimeCard?: any;
  onClose: () => void;
  actionCtx?: {
    isPending: boolean;
    needsTarget: boolean;
    canPlay: boolean;
    onPlay: () => void;
    onCancel: () => void;
  };
  // Предупреждение про Initiation Cooldown — если карта рарити не активна
  // на текущем ходу. Юнит ляжет «спящим», Действие СГОРИТ при розыгрыше.
  initiationWarn?: {
    currentTurn: number;
    activatesOnTurn: number;
    isAction: boolean;  // true → СГОРИТ; false → юнит спит
  };
}) {
  return (
    <div className={styles.cdmOverlay} onClick={onClose}>
      <div className={styles.cdmBox} onClick={(e) => e.stopPropagation()}>
        <button className={styles.cdmClose} onClick={onClose}>×</button>
        <div style={{ width: 320, margin: '0 auto' }}>
          {/* БУГ.10: если есть runtimeCard (engine instance) — мержим его
              поля поверх static template, чтобы attackPriority и т.п.
              были видны в детальном виде карты. */}
          <GameCard
            card={runtimeCard ? { ...card, ...runtimeCard } as CardData : card}
            variant="full"
          />
        </div>
        {/* Установленные Моды на юните (Синтетикс) — показываем какие именно. */}
        {Array.isArray(runtimeCard?.modifications) && runtimeCard.modifications.length > 0 && (
          <div style={{ margin: '10px auto 0', maxWidth: 320, padding: '8px 10px', background: 'var(--bg-0, #07070B)', boxShadow: 'inset 0 0 0 0.5px var(--syn, #46E3C2)', clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))' }}>
            <div style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--syn, #46E3C2)', marginBottom: 4 }}>
              ⚙ {trStatic('Установленные Моды', 'Installed Mods')} ({runtimeCard.modifications.length})
            </div>
            {runtimeCard.modifications.map((m: any, i: number) => (
              <div key={i} style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: 'var(--t-hi, #F4F6FB)', padding: '2px 0' }}>
                • {m.name_ru || m.name || getCardData(m.id)?.name_ru || m.id}
              </div>
            ))}
          </div>
        )}
        {initiationWarn && (
          <div className={styles.cdmInitWarn}>
            {trStatic('ru', 'en') === 'en' ? (
              <>
                ⚠ <b>Initiation Cooldown.</b> Card is active from <b>Turn {initiationWarn.activatesOnTurn}</b>.{' '}
                Current turn {initiationWarn.currentTurn}.{' '}
                {initiationWarn.isAction
                  ? 'Action will BURN with no effect.'
                  : "Unit will lay sleeping (won't attack, abilities don't work)."}
              </>
            ) : (
              <>
                ⚠ <b>Initiation Cooldown.</b> Карта активна с <b>Хода {initiationWarn.activatesOnTurn}</b>.{' '}
                Сейчас ход {initiationWarn.currentTurn}.{' '}
                {initiationWarn.isAction
                  ? 'Действие СГОРИТ без эффекта.'
                  : 'Юнит ляжет на поле спящим (не атакует, способности не работают).'}
              </>
            )}
          </div>
        )}
        {actionCtx && (
          <div className={styles.cdmActions}>
            {actionCtx.isPending ? (
              <button
                className={`${styles.cdmActionBtn} ${styles.cdmActionCancel}`}
                onClick={() => { actionCtx.onCancel(); onClose(); }}
              >
                ✕ {trStatic('ОТМЕНИТЬ', 'CANCEL')}
              </button>
            ) : (
              <button
                className={`${styles.cdmActionBtn} ${actionCtx.canPlay ? styles.cdmActionPlay : ''}`}
                disabled={!actionCtx.canPlay}
                onClick={() => { actionCtx.onPlay(); onClose(); }}
                title={actionCtx.needsTarget ? trStatic('Сыграть → выбрать цель', 'Play → pick target') : trStatic('Сыграть', 'Play')}
              >
                {actionCtx.needsTarget
                  ? trStatic('► СЫГРАТЬ → выбрать цель', '► PLAY → pick target')
                  : trStatic('► СЫГРАТЬ', '► PLAY')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ────────── Модалка просмотра колоды / сброса ──────────
// Простой список карт с компактной мини-плиткой (арт + имя + статы).
function PileModal({
  title, cards, faction, isDeckShuffled, onClose, onCardDetail
}: {
  title: string;
  cards: any[];
  faction: Faction;
  isDeckShuffled?: boolean;
  onClose: () => void;
  onCardDetail: (card: any) => void;
}) {
  const fShort = factionShort(faction);
  // Сортировка: легендарки → эпики → редкие → обычные.
  // В PvP массив колоды — Array(N).fill(null) (карты скрыты сервером),
  // фильтруем null чтобы не упасть на a.rarity.
  const order: Record<string, number> = { legendary: 0, epic: 1, rare: 2, common: 3, special: 4 };
  const visibleCards = cards.filter(Boolean);
  const sorted = [...visibleCards].sort((a, b) => {
    const oa = order[a.rarity || 'common'] ?? 9;
    const ob = order[b.rarity || 'common'] ?? 9;
    if (oa !== ob) return oa - ob;
    return String(a.name_ru || '').localeCompare(String(b.name_ru || ''), 'ru');
  });
  // PvP-кейс: колода в схеме = только размер, карты не пришли клиенту.
  const isPvpDeckHidden = cards.length > 0 && visibleCards.length === 0;

  return (
    <div className={styles.pileOverlay} onClick={onClose}>
      <div className={styles.pileBox} onClick={(e) => e.stopPropagation()}>
        <div className={styles.pileHeader}>
          <div className={styles.pileTitle}>{title}</div>
          <div className={styles.pileCount}>{cards.length} {pluralCards(cards.length)}</div>
          <button className={styles.pileClose} onClick={onClose}>×</button>
        </div>
        {cards.length === 0 ? (
          <div className={styles.pileEmpty}>{trStatic('— пусто —', '— empty —')}</div>
        ) : isPvpDeckHidden ? (
          <div className={styles.pileEmpty}>
            {trStatic('Колода перетасована — карты скрыты', 'Deck shuffled — cards hidden')}
          </div>
        ) : (
          <div className={styles.pileGrid}>
            {sorted.map((c) => {
              const data = getCardData(c.id);
              const rarity = c.rarity || data?.rarity || 'common';
              const atk = c.base?.atk ?? data?.atk ?? 0;
              const armor = c.base?.armor ?? data?.armor ?? 0;
              const hp = c.base?.hp ?? data?.hp ?? 0;
              const isUnit = (c.type || data?.type) === 'unit';
              const isAction = (c.type || data?.type) === 'action';
              return (
                <div
                  key={c.instanceId || c.id}
                  className={`${styles.pileCard} ${styles[`f-${fShort}`]} ${styles[`r-${rarity}`]}`}
                  onClick={() => onCardDetail(c)}
                >
                  <div className={styles.hcardArt}>
                    <img
                      src={`/img/cards/${c.id}.jpg`}
                      alt=""
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                  </div>
                  <div className={styles.hcardName}>{c.name_ru || data?.name_ru || c.id}</div>
                  {isUnit && (
                    <div className={styles.hcardStats}>
                      <span className={styles.hcardStat} style={{ color: 'var(--neon-am)' }}>{atk}</span>
                      <span className={styles.hcardStat} style={{ color: 'var(--neon-cy)' }}>{armor}</span>
                      <span className={styles.hcardStat} style={{ color: 'var(--neon-li)' }}>{hp}</span>
                    </div>
                  )}
                  {isAction && <div className={styles.hcardStats}><span className={styles.hcardType}>{trStatic('ДЕЙСТВ.', 'ACTION')}</span></div>}
                  {!isUnit && !isAction && <div className={styles.hcardStats}><span className={styles.hcardType}>{trStatic('МОД', 'MOD')}</span></div>}
                </div>
              );
            })}
          </div>
        )}
        <div className={styles.pileHint}>
          {isDeckShuffled
            ? trStatic(
              'Колода замешана. Здесь — что в ней лежит, отсортировано по редкости (для удобства). Реальный порядок тяги — случайный.',
              'Deck is shuffled. Shown here sorted by rarity (for clarity). Actual draw order is random.'
            )
            : trStatic('Тап на карту — подробности.', 'Tap a card — details.')}
        </div>
      </div>
    </div>
  );
}

function pluralCards(n: number): string {
  const isEn = trStatic('ru', 'en') === 'en';
  if (isEn) return n === 1 ? 'card' : 'cards';
  const n10 = n % 10, n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return 'карта';
  if ([2, 3, 4].includes(n10) && ![12, 13, 14].includes(n100)) return 'карты';
  return 'карт';
}

// ────────── Модалка деталей Героя ──────────
function HeroDetailModal({
  hero, onClose, activeCtx
}: {
  hero: any;
  onClose: () => void;
  // Контекст для активки Героя — отображается кнопка «АКТИВКА».
  // Только для МОЕГО Героя в Ф2 (диздок §6.4 — активки играются в Ф2),
  // и если активка ещё не использована за матч.
  activeCtx?: {
    canUse: boolean;     // false если фаза не та, или активка уже была, или Герой мёртв
    reason: string;      // если canUse=false — почему
    onUse: () => void;   // вызвать активку
  };
}) {
  const tr = useTr();
  const at = hero.ability_text || {};
  const blocks: Array<{ label: string; text: string; color: string }> = [];
  if (at.active)      blocks.push({ label: tr('Активка', 'Active'),         text: at.active, color: 'var(--neon-am)' });
  if (at.aura)        blocks.push({ label: tr('Аура', 'Aura'),              text: at.aura, color: 'var(--neon-li)' });
  if (at.trigger)     blocks.push({ label: tr('Триггер', 'Trigger'),        text: at.trigger, color: 'var(--neon-cy)' });
  if (at.order)       blocks.push({ label: tr('Приказ', 'Order'),           text: at.order, color: 'var(--neon-mg)' });
  if (at.deathrattle) blocks.push({ label: tr('Посмертие', 'Deathrattle'),  text: at.deathrattle, color: 'var(--neon-rd)' });

  return (
    <div className={styles.cdmOverlay} onClick={onClose}>
      <div className={styles.heroDetailBox} onClick={(e) => e.stopPropagation()}>
        <button className={styles.cdmClose} onClick={onClose}>×</button>
        <div className={styles.heroDetailHeader}>
          <HeroAvatar id={hero.id} size={64} faction={hero.faction} shape="square" />
          <div className={styles.heroDetailInfo}>
            <div className={styles.heroDetailName}>{trHeroName(hero.id, hero.name_ru)}</div>
            {hero.archetype && <div className={styles.heroDetailArch}>{hero.archetype}</div>}
            <div className={styles.heroDetailStats}>
              <span className={`${styles.statChip} ${styles.statAtk}`}><IcoAtk />{hero.base?.atk ?? 0}</span>
              <span className={`${styles.statChip} ${styles.statArm}`}><IcoArm />{hero.base?.armor ?? 0}</span>
              <span className={`${styles.statChip} ${styles.statHp}`}><IcoHp />{hero.base?.hp ?? 0}</span>
            </div>
          </div>
        </div>
        {blocks.length > 0 ? (
          <div className={styles.heroDetailBlocks}>
            {blocks.map((b, i) => (
              <div key={i} className={styles.heroDetailBlock}>
                <span className={styles.heroDetailLbl} style={{ color: b.color }}>{b.label}</span>
                <span className={styles.heroDetailText}>{b.text}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.heroDetailEmpty}>{tr('— у Героя нет описанных способностей —', '— Hero has no described abilities —')}</div>
        )}
        {activeCtx && (
          <div className={styles.cdmActions} style={{ marginTop: 14 }}>
            <button
              className={`${styles.cdmActionBtn} ${activeCtx.canUse ? styles.cdmActionPlay : ''}`}
              disabled={!activeCtx.canUse}
              onClick={() => { activeCtx.onUse(); onClose(); }}
              title={activeCtx.canUse ? tr('Применить активку Героя', 'Use Hero active') : activeCtx.reason}
            >
              {activeCtx.canUse ? tr('◆ ПРИМЕНИТЬ АКТИВКУ', '◆ USE ACTIVE') : `◆ ${activeCtx.reason}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ────────── БУГ.3: Attack priority picker ──────────
//
// Открывается при размещении Глобал-юнита (диздок §7.3). Игрок выбирает
// один из 4 приоритетов атаки: strongest / weakest / healer / random.
// Выбор фиксируется на card.attackPriority до конца игры.
function AttackPriorityPickerModal({
  card, onPick, onCancel
}: {
  card: any;
  onPick: (priority: GlobalPriorityDef['id']) => void;
  onCancel: () => void;
}) {
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(7, 7, 11, 0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 20,
        backdropFilter: 'blur(8px)'
      }}
    >
      {/* БУГ.5: убрали onClick={onCancel} на backdrop — на touch-устройствах
          tap по кнопке мог дать одновременный click и на backdrop, отменяя
          выбор. Закрытие теперь только через явную кнопку «Отмена». */}
      <div
        style={{
          maxWidth: 420, width: '100%',
          background: 'var(--bg-2, #101018)',
          boxShadow: 'inset 0 0 0 1px var(--gsec, #FF8A1F), 0 0 40px rgba(255, 138, 31, 0.4)',
          padding: '20px 18px',
          clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))'
        }}
      >
        <div style={{
          fontFamily: "'Chakra Petch', sans-serif",
          fontSize: 16, fontWeight: 700, letterSpacing: '0.1em',
          color: 'var(--gsec, #FF8A1F)', marginBottom: 4
        }}>{tr('ПРИОРИТЕТ АТАКИ', 'ATTACK PRIORITY')}</div>
        <div style={{
          fontFamily: "'Chakra Petch', sans-serif",
          fontSize: 12, letterSpacing: '0.06em',
          color: 'var(--t-md, #A0A4B4)', marginBottom: 8
        }}>{card?.name_ru || card?.name || '?'}</div>
        <div style={{
          fontFamily: "'Inter', sans-serif", fontSize: 11,
          color: 'var(--t-lo, #60657A)', marginBottom: 14,
          fontStyle: 'italic', lineHeight: 1.4
        }}>{tr(
          'Выбор фиксируется до конца боя.',
          'Choice is locked until end of match.'
        )}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
          {GLOBAL_PRIORITIES.map((p) => (
            <button
              key={p.id}
              // БУГ.5-fix4: onPointerDown удалён — preventDefault не
              // предотвращает synthetic click, и оба хендлера срабатывали.
              // Ref-guard в onPick защищает от двойного вызова.
              onClick={() => onPick(p.id)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                gap: 3, padding: '12px 14px',
                background: 'var(--bg-0, #07070B)',
                border: 0,
                boxShadow: 'inset 0 0 0 0.5px var(--gsec, #FF8A1F)',
                color: 'var(--gsec, #FF8A1F)',
                fontFamily: "'Chakra Petch', sans-serif",
                cursor: 'pointer',
                clipPath: 'polygon(0 0, calc(100% - 5px) 0, 100% 5px, 100% 100%, 5px 100%, 0 calc(100% - 5px))',
                textAlign: 'left',
                touchAction: 'manipulation'
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.06em' }}>
                {isEn ? p.label_en : p.label_ru}
              </span>
              <span style={{
                fontSize: 11, color: 'var(--t-md, #A0A4B4)',
                fontFamily: "'Inter', sans-serif",
                fontWeight: 400, letterSpacing: 0
              }}>
                {isEn ? p.hint_en : p.hint_ru}
              </span>
            </button>
          ))}
        </div>
        <button
          onClick={onCancel}
          style={{
            width: '100%', padding: 12,
            background: 'transparent', border: 0,
            boxShadow: 'inset 0 0 0 0.5px var(--t-md)',
            color: 'var(--t-md, #A0A4B4)',
            fontFamily: "'Chakra Petch', sans-serif",
            fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase',
            cursor: 'pointer',
            clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))'
          }}
        >{tr('Отмена', 'Cancel')}</button>
      </div>
    </div>
  );
}

// ────────── 2d: Heal priority picker ──────────
//
// Открывается при размещении хилера (5 livlife-медиков + 2 глобал-медика).
// Игрок выбирает, КОГО лечить: самый раненый / сильный / лекарь / случайный.
// Выбор фиксируется на card.healPriority до конца игры. Зелёный акцент (хил).
function HealPriorityPickerModal({
  card, onPick, onCancel
}: {
  card: any;
  onPick: (priority: HealPriorityDef['id']) => void;
  onCancel: () => void;
}) {
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(7, 7, 11, 0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 20,
        backdropFilter: 'blur(8px)'
      }}
    >
      <div
        style={{
          maxWidth: 420, width: '100%',
          background: 'var(--bg-2, #101018)',
          boxShadow: 'inset 0 0 0 1px var(--ll, #2BE0A6), 0 0 40px rgba(43, 224, 166, 0.4)',
          padding: '20px 18px',
          clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))'
        }}
      >
        <div style={{
          fontFamily: "'Chakra Petch', sans-serif",
          fontSize: 16, fontWeight: 700, letterSpacing: '0.1em',
          color: 'var(--ll, #2BE0A6)', marginBottom: 4
        }}>{tr('ПРИОРИТЕТ ЛЕЧЕНИЯ', 'HEAL PRIORITY')}</div>
        <div style={{
          fontFamily: "'Chakra Petch', sans-serif",
          fontSize: 12, letterSpacing: '0.06em',
          color: 'var(--t-md, #A0A4B4)', marginBottom: 8
        }}>{card?.name_ru || card?.name || '?'}</div>
        <div style={{
          fontFamily: "'Inter', sans-serif", fontSize: 11,
          color: 'var(--t-lo, #60657A)', marginBottom: 14,
          fontStyle: 'italic', lineHeight: 1.4
        }}>{tr(
          'Кого лечить в первую очередь. Выбор фиксируется до конца боя.',
          'Who to heal first. Choice is locked until end of match.'
        )}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
          {HEAL_PRIORITIES.map((p) => (
            <button
              key={p.id}
              onClick={() => onPick(p.id)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                gap: 3, padding: '12px 14px',
                background: 'var(--bg-0, #07070B)',
                border: 0,
                boxShadow: 'inset 0 0 0 0.5px var(--ll, #2BE0A6)',
                color: 'var(--ll, #2BE0A6)',
                fontFamily: "'Chakra Petch', sans-serif",
                cursor: 'pointer',
                clipPath: 'polygon(0 0, calc(100% - 5px) 0, 100% 5px, 100% 100%, 5px 100%, 0 calc(100% - 5px))',
                textAlign: 'left',
                touchAction: 'manipulation'
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.06em' }}>
                {isEn ? p.label_en : p.label_ru}
              </span>
              <span style={{
                fontSize: 11, color: 'var(--t-md, #A0A4B4)',
                fontFamily: "'Inter', sans-serif",
                fontWeight: 400, letterSpacing: 0
              }}>
                {isEn ? p.hint_en : p.hint_ru}
              </span>
            </button>
          ))}
        </div>
        <button
          onClick={onCancel}
          style={{
            width: '100%', padding: 12,
            background: 'transparent', border: 0,
            boxShadow: 'inset 0 0 0 0.5px var(--t-md)',
            color: 'var(--t-md, #A0A4B4)',
            fontFamily: "'Chakra Petch', sans-serif",
            fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase',
            cursor: 'pointer',
            clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))'
          }}
        >{tr('Отмена', 'Cancel')}</button>
      </div>
    </div>
  );
}

// ────────── ПвП.fix-move-action: MoveDestinationModal ──────────
//
// Открывается после выбора target юнита для Действий «Перегруппировка» /
// «Передислокация» (Глобал). Показывает сетку 2×4 ячеек МОЕЙ стороны
// поля: занятые — серые-disabled, свободные — кликабельные. По выбору —
// onPick(line, slot). Отмена закрывает modal без эффекта (pendingAction
// остаётся не установленным).
function MoveDestinationModal({
  card, target, me, onPick, onCancel
}: {
  card: any;
  target: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  me: any;
  onPick: (line: 0 | 1, slot: number) => void;
  onCancel: () => void;
}) {
  const trModal = useTr();
  const lines: { line: 0 | 1; cells: any[] }[] = [
    { line: 0, cells: me.field?.[0] || [null, null, null, null] },
    { line: 1, cells: me.field?.[1] || [null, null, null, null] },
  ];
  return (
    <div className={styles.cdmOverlay} onClick={onCancel}>
      <div className={styles.priorityPickerBox} onClick={(e) => e.stopPropagation()}>
        <button className={styles.cdmClose} onClick={onCancel}>×</button>
        <div className={styles.priorityPickerTitle}>
          {trModal('Куда переместить', 'Where to move')}: <b>{target.name_ru || target.id}</b>
        </div>
        <div className={styles.priorityPickerHint}>
          {trModal('Карта:', 'Card:')} {card.name_ru || card.id}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          {lines.map(({ line, cells }) => (
            <div key={line} style={{ display: 'flex', gap: 6 }}>
              <div style={{ width: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#8aa' }}>
                {line + 1}Л
              </div>
              {cells.map((u, slot) => {
                const isTarget = u && u.instanceId === target.instanceId;
                const free = !u || isTarget; // ячейка самого target — тоже free (он переедет с неё)
                return (
                  <button
                    key={slot}
                    disabled={!free}
                    onClick={() => onPick(line, slot)}
                    style={{
                      flex: 1,
                      padding: '12px 8px',
                      background: free ? 'rgba(60, 180, 220, 0.15)' : 'rgba(80, 80, 80, 0.3)',
                      border: free ? '1px solid #3DF0FF' : '1px solid #555',
                      color: free ? '#3DF0FF' : '#666',
                      borderRadius: 4,
                      cursor: free ? 'pointer' : 'not-allowed',
                      fontFamily: 'monospace',
                      fontSize: 12,
                    }}
                  >
                    {free
                      ? (isTarget ? `${trModal('тек.', 'cur')} #${slot + 1}` : `#${slot + 1}`)
                      : (u.name_ru ? u.name_ru.slice(0, 10) : '—')}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ────────── 2d: MultiSelectModal (мультиселект юнитов) ──────────
//
// Великая жертва: выбрать до maxCount своих юнитов (тап = toggle). По
// «Подтвердить» → onConfirm(selectedIds). Локальный selection-стейт.
function MultiSelectModal({
  card, candidates, maxCount, exactCount, hintRu, hintEn, onConfirm, onCancel
}: {
  card: any; candidates: any[]; maxCount: number; exactCount?: number;
  hintRu: string; hintEn: string;
  onConfirm: (ids: number[]) => void; onCancel: () => void;
}) {
  const tr = useTr();
  const [selected, setSelected] = useState<number[]>([]);
  const ACC = 'var(--deus, #B16CFF)';
  const toggle = (id: number) => {
    setSelected((prev) => prev.includes(id)
      ? prev.filter((x) => x !== id)
      : (prev.length < maxCount ? [...prev, id] : prev));
  };
  // exactCount задан (reveal-pick: ровно N) → подтверждение только при точном
  // числе; иначе (Великая жертва: до maxCount) — при ≥1.
  const ok = exactCount != null ? selected.length === exactCount : selected.length > 0;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(7,7,11,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20, backdropFilter: 'blur(8px)' }}>
      <div style={{ maxWidth: 420, width: '100%', background: 'var(--bg-2, #101018)', boxShadow: `inset 0 0 0 1px ${ACC}, 0 0 40px rgba(177,108,255,0.4)`, padding: '20px 18px', clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))' }}>
        <div style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 16, fontWeight: 700, letterSpacing: '0.1em', color: ACC, marginBottom: 4 }}>{(card?.name_ru || card?.name || '?').toUpperCase()}</div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: 'var(--t-md, #A0A4B4)', marginBottom: 10, lineHeight: 1.4 }}>{tr(hintRu, hintEn)} ({selected.length}/{maxCount})</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14, maxHeight: '40vh', overflowY: 'auto' }}>
          {candidates.length === 0 && <div style={{ color: 'var(--t-lo)', fontSize: 11 }}>{tr('— нет вариантов —', '— no options —')}</div>}
          {candidates.map((u: any) => {
            const on = selected.includes(u.instanceId);
            const cur = u.current || {};
            const isUnit = (u.type || getCardData(u.id)?.type) === 'unit';
            return (
              <button key={u.instanceId} onClick={() => toggle(u.instanceId)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: on ? 'rgba(177,108,255,0.18)' : 'var(--bg-0, #07070B)', border: 0, boxShadow: on ? `inset 0 0 0 1px ${ACC}` : 'inset 0 0 0 0.5px var(--line, rgba(160,170,200,.18))', color: 'var(--t-hi, #F4F6FB)', fontFamily: "'Inter', sans-serif", fontSize: 12, cursor: 'pointer', clipPath: 'polygon(0 0, calc(100% - 5px) 0, 100% 5px, 100% 100%, 5px 100%, 0 calc(100% - 5px))', textAlign: 'left', touchAction: 'manipulation' }}>
                <span style={{ fontFamily: "'Chakra Petch', sans-serif" }}>{on ? '☑ ' : '☐ '}{u.name_ru || u.name}</span>
                <span style={{ color: 'var(--t-md)', fontSize: 11 }}>{isUnit ? `${cur.atk ?? u.atk ?? 0}/${cur.armor ?? 0}/${cur.hp ?? u.hp ?? 0}` : tr('Мод', 'Mod')}</span>
              </button>
            );
          })}
        </div>
        <button disabled={!ok} onClick={() => onConfirm(selected)}
          style={{ width: '100%', padding: 12, marginBottom: 6, background: ok ? ACC : 'var(--bg-0)', border: 0, color: ok ? '#0a0a0f' : 'var(--t-lo)', fontFamily: "'Chakra Petch', sans-serif", fontSize: 13, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: ok ? 'pointer' : 'not-allowed', clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))' }}>
          {tr('Подтвердить', 'Confirm')} ({selected.length}{exactCount != null ? `/${exactCount}` : ''})
        </button>
        <button onClick={onCancel}
          style={{ width: '100%', padding: 10, background: 'transparent', border: 0, boxShadow: 'inset 0 0 0 0.5px var(--t-md)', color: 'var(--t-md, #A0A4B4)', fontFamily: "'Chakra Petch', sans-serif", fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', cursor: 'pointer', clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))' }}>
          {tr('Отмена', 'Cancel')}
        </button>
      </div>
    </div>
  );
}

// ────────── БУГ.2: Order target picker ──────────
//
// Открывается когда игрок дропает на ячейку карту, у которой Order
// требует enemy-target (сейчас: global_saper_onix). Показывает список
// вражеских юнитов на поле как кнопки. По тапу — onPick(unit).
// Отмена — onPick(null) → размещение отменяется, карта остаётся в руке.
function OrderTargetPickerModal({
  card, candidates, purpose, onPick, onCancel
}: {
  card: any;
  candidates: any[];
  purpose: string;
  onPick: (unit: any | null) => void;
  onCancel: () => void;
}) {
  const tr = useTr();
  const purposeText = purpose === 'armor_break_1'
    ? tr('Сними 1 max брони у вражеского юнита.', 'Strip 1 max armor from an enemy unit.')
    : purpose === 'fikser_source'
    ? tr('Выбери своего юнита с Модом — источник переноса.', 'Pick your unit with a Mod — transfer source.')
    : purpose === 'fikser_target'
    ? tr('Кому передать Мод?', 'Who receives the Mod?')
    : purpose === 'fikser_unit'
    ? tr('Какого юнита передвинуть?', 'Which unit to move?')
    : purpose === 'heal_ally'
    ? tr('Кого вылечить на 2 hp?', 'Who to heal for 2 hp?')
    : tr('Выбери цель.', 'Pick a target.');
  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(7, 7, 11, 0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 20,
        backdropFilter: 'blur(8px)'
      }}
    >
      {/* БУГ.5: backdrop click больше не отменяет выбор — закрытие только
          через явную кнопку «Отмена». */}
      <div
        style={{
          maxWidth: 420, width: '100%',
          background: 'var(--bg-2, #101018)',
          boxShadow: 'inset 0 0 0 1px var(--neon-rd, #FF3D8F), 0 0 40px rgba(255, 61, 143, 0.4)',
          padding: '20px 18px',
          clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))'
        }}
      >
        <div style={{
          fontFamily: "'Chakra Petch', sans-serif",
          fontSize: 16, fontWeight: 700, letterSpacing: '0.1em',
          color: 'var(--neon-rd, #FF3D8F)', marginBottom: 4
        }}>{tr('ВЫБОР ЦЕЛИ', 'PICK TARGET')}</div>
        <div style={{
          fontFamily: "'Chakra Petch', sans-serif",
          fontSize: 12, letterSpacing: '0.06em',
          color: 'var(--t-md, #A0A4B4)', marginBottom: 14
        }}>{card?.name_ru || card?.name || '?'}</div>
        <div style={{
          fontFamily: "'Inter', sans-serif", fontSize: 12,
          color: 'var(--t-md, #A0A4B4)', marginBottom: 14, lineHeight: 1.4
        }}>{purposeText}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
          {candidates.length === 0 && (
            <div style={{ color: 'var(--t-lo)', fontSize: 11 }}>
              {tr('— нет валидных целей —', '— no valid targets —')}
            </div>
          )}
          {candidates.map((u: any) => {
            const cur = u.current || {};
            const armor = cur.armor ?? 0;
            const hp = cur.hp ?? u.hp ?? 0;
            const atk = cur.atk ?? u.atk ?? 0;
            return (
              <button
                key={u.instanceId}
                // БУГ.5-fix4: только onClick, без onPointerDown (см. priority picker).
                onClick={() => onPick(u)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 12px',
                  background: 'var(--bg-0, #07070B)',
                  border: 0,
                  boxShadow: 'inset 0 0 0 0.5px var(--line, rgba(160,170,200,.18))',
                  color: 'var(--t-hi, #F4F6FB)',
                  fontFamily: "'Inter', sans-serif", fontSize: 12,
                  cursor: 'pointer',
                  clipPath: 'polygon(0 0, calc(100% - 5px) 0, 100% 5px, 100% 100%, 5px 100%, 0 calc(100% - 5px))',
                  textAlign: 'left',
                  touchAction: 'manipulation'
                }}
              >
                <span style={{ fontFamily: "'Chakra Petch', sans-serif", letterSpacing: '0.03em' }}>
                  {u.name_ru || u.name}
                </span>
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                  color: 'var(--t-md, #A0A4B4)'
                }}>
                  {atk}/{armor}/{hp}
                </span>
              </button>
            );
          })}
        </div>
        <button
          onClick={() => onPick(null)}
          style={{
            width: '100%', padding: 12,
            background: 'transparent', border: 0,
            boxShadow: 'inset 0 0 0 0.5px var(--t-md)',
            color: 'var(--t-md, #A0A4B4)',
            fontFamily: "'Chakra Petch', sans-serif",
            fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase',
            cursor: 'pointer',
            clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))'
          }}
        >{tr('Отмена', 'Cancel')}</button>
      </div>
    </div>
  );
}

// ────────── End-of-match modal (АНИМ.3 v2) ──────────
//
// 5 фраз на каждый исход — выбираются стабильно по match_id (или 0 если ещё
// нет finishResult). Это даёт каждому матчу свою фразу, но детерминированно.
const WIN_SUBS_RU = [
  'Ты сломил врага',
  'Сеть твоя',
  'Корпа в нокауте',
  'Ты выжал противника',
  'Чисто. Без логов.'
];
const WIN_SUBS_EN = [
  'You broke the enemy',
  'The net is yours',
  'Corp is down',
  'You squeezed them out',
  'Clean. No logs.'
];
const LOSE_SUBS_RU = [
  'Враг оказался жёстче',
  'Тебя переиграли',
  'Сегодня не твой день',
  'Сорвалось',
  'Не дотянул'
];
const LOSE_SUBS_EN = [
  'The enemy was tougher',
  'You got outplayed',
  'Not your day',
  'Slipped',
  "Didn't reach"
];

function EndOfMatchModal({
  winnerIdx, myFaction, turns, onLeave,
  finishLoading, finishResult, finishError, scriptsEarned,
  logEntries, foundIn, warStake,
  warMultiplier, warEndReason, opponent
}: {
  winnerIdx: 0 | 1 | 'draw' | null;
  myFaction: Faction;
  turns: number;
  onLeave: () => void;
  finishLoading: boolean;
  finishResult: MatchFinishResult | null;
  finishError: string | null;
  scriptsEarned: number;
  logEntries?: string[];
  // АНИМ.3: тип матча и ставка (для USDT-блока в Корп-войнах).
  foundIn?: 'casual' | 'rated' | 'war' | 'pve' | string;
  warStake?: number | null;
  // ВОЙ.1: реальный множитель войны (1/2/4) и причина окончания
  // ('hp' = убитый Герой; 'retreat' = отступление на снап).
  warMultiplier?: number;
  warEndReason?: string;
  // Этап 68.3: соперник (для показа его косметики в конце PvP-матча).
  opponent?: {
    nick: string;
    accentColor?: string | null;
    avatarId?: string | null;
    titleCode?: string | null;
    titleText?: string | null;
  } | null;
}) {
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  // Этап 67.2: звук исхода матча — один раз при показе модалки.
  useEffect(() => {
    if (winnerIdx === 0) playSfx('victory');
    else if (winnerIdx === 1) playSfx('defeat');
    else if (winnerIdx === 'draw') playSfx('draw_end');
  }, [winnerIdx]);
  const [copyBtnLabel, setCopyBtnLabel] = useState<string>('');
  const copyLabel = copyBtnLabel || tr('📋 СКОПИРОВАТЬ ЛОГ БОЯ', '📋 COPY BATTLE LOG');
  const copyLog = async () => {
    const text = (logEntries || []).join('\n');
    if (!text) {
      setCopyBtnLabel(tr('— пусто —', '— empty —'));
      setTimeout(() => setCopyBtnLabel(''), 1500);
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopyBtnLabel(tr('✓ СКОПИРОВАНО', '✓ COPIED'));
    } catch {
      // Fallback через textarea+execCommand.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        setCopyBtnLabel(tr('✓ СКОПИРОВАНО', '✓ COPIED'));
      } catch {
        setCopyBtnLabel(tr('✗ ОШИБКА', '✗ ERROR'));
      }
      ta.remove();
    }
    setTimeout(() => setCopyBtnLabel(''), 1500);
  };
  // АНИМ.3: выбор фразы по match_id (детерминированно: один матч = одна фраза).
  const subIdx = finishResult ? (finishResult.match_id % 5) : 0;

  let title = tr('МАТЧ ОКОНЧЕН', 'MATCH OVER');
  let sub = '';
  let color = 'var(--neon-cy)';
  let isDefeatTitle = false;
  if (winnerIdx === 0) {
    title = tr('ПОБЕДА', 'VICTORY');
    sub = isEn ? WIN_SUBS_EN[subIdx] : WIN_SUBS_RU[subIdx];
    color = 'var(--neon-li)';
  } else if (winnerIdx === 1) {
    // АНИМ.3: лор-слово «ОБНУЛИЛИ» вместо нейтрального «ПОРАЖЕНИЕ».
    // EN-вариант — фирменный жаргон киберпанка «ZEROED».
    title = tr('ОБНУЛИЛИ', 'ZEROED');
    sub = isEn ? LOSE_SUBS_EN[subIdx] : LOSE_SUBS_RU[subIdx];
    color = 'var(--neon-rd)';
    isDefeatTitle = true;
  } else if (winnerIdx === 'draw') {
    title = tr('РАВНЫЙ ИСХОД', 'EVEN OUTCOME');
    sub = tr('Оба Героя пали одновременно', 'Both Heroes fell at once');
    color = 'var(--neon-am)';
  }

  // ВОЙ.1: USDT-блок для Корп-войн со ставкой. Расчёт с реальным
  // мультипликатором + учётом отступления (retreat = 50% от ставки).
  //   stake = warStake (начальная)
  //   accepted = stake * multiplier (текущая принятая)
  //   hp-win: победитель забирает свою стейк + всю стейк врага (− 10% комиссия)
  //   retreat-win: инициатор забирает свою + 50% врага (− 10% комиссия)
  // Заморозка = stake * 4 (вилка ×4), возврат = frozen - проигранная часть.
  // Сервер делает реальные wallet-движения; здесь visual breakdown.
  const showUsdt = foundIn === 'war' && warStake != null && warStake > 0;
  const usdtWin = winnerIdx === 0;
  const wMult = warMultiplier ?? 1;
  const stake = warStake ?? 0;
  const acceptedStake = stake * wMult;
  const isRetreat = warEndReason === 'retreat';
  // Что я заплатил/выиграл сам по своей ставке.
  // hp-win me: вернулась вся accepted ($acceptedStake)
  // hp-loss me: потерял $acceptedStake
  // retreat-win me (инициатор): моя ставка вернулась полностью
  // retreat-loss me (я отступил): -50% acceptedStake (вторая половина возвращается)
  let myStakeChange = 0; // положительное = возврат сверх ничего; отрицательное = потеря
  let trophyFromEnemy = 0; // только при выигрыше
  let commission = 0;
  if (showUsdt && wMult > 0) {
    if (isRetreat) {
      // 50%-механика отступления.
      if (usdtWin) {
        myStakeChange = 0; // моя ставка не сгорела
        trophyFromEnemy = acceptedStake * 0.5;
      } else {
        myStakeChange = -acceptedStake * 0.5;
      }
    } else {
      // HP — полная победа/поражение.
      if (usdtWin) {
        myStakeChange = 0;
        trophyFromEnemy = acceptedStake;
      } else if (winnerIdx === 1) {
        myStakeChange = -acceptedStake;
      } // draw — оба ничего (комиссия может быть 5% по диздоку, но не реализуем)
    }
    commission = trophyFromEnemy * 0.10;
  }
  const usdtFrozen = stake * 4;
  return (
    <div className={styles.endOverlay}>
      <div className={styles.endBox} style={{ ['--endC' as any]: color }}>
        <div
          className={`${styles.endTitle} ${isDefeatTitle ? styles.endTitleDefeat : ''}`}
          style={{ color }}
        >{title}</div>
        <div className={styles.endSub}>{sub}</div>

        {/* Этап 68.3: соперник с его косметикой (цвет ника / аватар / титул). */}
        {opponent && opponent.nick && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 8, margin: '2px 0 12px'
          }}>
            <span style={{ fontSize: 9, color: 'var(--t-lo)', letterSpacing: '0.14em' }}>
              {tr('ПРОТИВНИК', 'OPPONENT')}
            </span>
            <UserAvatar avatarId={opponent.avatarId} size={26} />
            <PlayerName
              name={opponent.nick}
              accentColor={opponent.accentColor}
              title={(opponent.titleCode || opponent.titleText)
                ? { title_code: opponent.titleCode || '', title_text_ru: opponent.titleText || '' }
                : null}
              size="sm"
              inline
            />
          </div>
        )}

        {/* ВОЙ.1: USDT-блок для Корп-войн (только если ставка была).
            Показываем мультипликатор + причину окончания + breakdown
            (моя ставка / трофей с врага / комиссия). На сервере wallet-
            движения ещё не подключены (Этап 2 ВОЙ.2) — это visual preview. */}
        {showUsdt && (
          <div className={styles.endUsdt}>
            <div className={styles.endUsdtLabel}>
              ⚔ {tr('ВОЙНА КОРПОРАЦИЙ', 'CORP WARS')}
              {' · '}
              ×{wMult}
              {' · '}
              {isRetreat
                ? tr('отступление', 'retreat')
                : tr('убийство', 'kill')}
            </div>
            <div className={styles.endUsdtRow}>
              <span className={styles.endUsdtRowLbl}>
                {tr('Принятая ставка', 'Accepted stake')}
              </span>
              <span className={styles.endUsdtRowVal} style={{ color: '#2DB386' }}>
                ${acceptedStake.toFixed(2)}
              </span>
            </div>
            <div className={styles.endUsdtRow}>
              <span className={styles.endUsdtRowLbl}>
                {myStakeChange < 0
                  ? tr('Потеря с моей ставки', 'Loss from my stake')
                  : tr('Моя ставка', 'My stake')}
              </span>
              <span className={`${styles.endUsdtRowVal} ${myStakeChange < 0 ? styles.loss : ''}`}>
                {myStakeChange < 0
                  ? `−$${Math.abs(myStakeChange).toFixed(2)}`
                  : tr('возвращена', 'returned')}
              </span>
            </div>
            {trophyFromEnemy > 0 && (
              <>
                <div className={styles.endUsdtRow}>
                  <span className={styles.endUsdtRowLbl}>
                    {tr('Трофей с противника', 'Trophy from opponent')}
                  </span>
                  <span className={`${styles.endUsdtRowVal} ${styles.trophy}`}>
                    +${trophyFromEnemy.toFixed(2)}
                  </span>
                </div>
                <div className={styles.endUsdtRow}>
                  <span className={styles.endUsdtRowLbl}>
                    {tr('Комиссия 10%', 'Commission 10%')}
                  </span>
                  <span className={`${styles.endUsdtRowVal} ${styles.loss}`}>
                    −${commission.toFixed(2)}
                  </span>
                </div>
              </>
            )}
            <div className={styles.endUsdtRow} style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid rgba(160,170,200,.15)' }}>
              <span className={styles.endUsdtRowLbl} style={{ fontWeight: 700 }}>
                {tr('Итого', 'Total')}
              </span>
              <span
                className={`${styles.endUsdtRowVal} ${usdtWin ? styles.trophy : styles.loss}`}
                style={{ fontWeight: 700 }}
              >
                {(trophyFromEnemy - commission + myStakeChange) >= 0 ? '+' : ''}
                ${(trophyFromEnemy - commission + myStakeChange).toFixed(2)}
              </span>
            </div>
            <div className={styles.endUsdtRow} style={{ opacity: 0.6, fontSize: 9 }}>
              <span className={styles.endUsdtRowLbl}>
                {tr('Заморозки разморожено', 'Frozen unfrozen')}
              </span>
              <span className={styles.endUsdtRowVal}>
                ${(usdtFrozen + myStakeChange).toFixed(2)} / ${usdtFrozen.toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* АНИМ.3: Embedded Level Up strip — если есть новые уровни. */}
        {finishResult && finishResult.account.level_ups.length > 0 && (
          <div className={styles.endLevelStrip}>
            <span className={styles.endLevelStripIco}>⚡</span>
            <span className={styles.endLevelStripText}>
              {tr('Новый уровень!', 'Level up!')}
              <b>{finishResult.account.level_ups.join(', ')}</b>
            </span>
          </div>
        )}

        <div className={styles.endStats}>
          <div className={styles.endStat}>
            <span className={styles.endStatLbl}>{tr('ходов', 'turns')}</span>
            <span className={styles.endStatVal}>{turns}</span>
          </div>
          <div className={styles.endStat}>
            <span className={styles.endStatLbl}>{tr('корпа', 'corp')}</span>
            <span className={styles.endStatVal}>{factionLabelLong(myFaction)}</span>
          </div>
        </div>

        {/* Награды от сервера. */}
        <div className={styles.endRewards}>
          <span className={styles.endRewardsLbl}>{tr('награды', 'rewards')}</span>
          {finishLoading && (
            <span className={styles.endRewardsVal}>⏳ {tr('загрузка…', 'loading…')}</span>
          )}
          {finishError && (
            <span className={styles.endRewardsVal} style={{ color: 'var(--neon-rd)' }}>
              ⚠ {finishError}
            </span>
          )}
          {!finishLoading && !finishError && finishResult && (
            <div className={styles.endRewardsList}>
              {scriptsEarned > 0 && (
                <div className={styles.endRewardItem}>
                  <span className={styles.endRewardIco}><ScriptsIcon size={16} /></span>
                  <span>+{scriptsEarned}</span>
                </div>
              )}
              {finishResult.bp && finishResult.bp.gained > 0 && (
                <div className={styles.endRewardItem}>
                  <span className={styles.endRewardIco} style={{ color: 'var(--neon-am)' }}>★</span>
                  <span>+{finishResult.bp.gained} <b>{tr('БП-XP', 'BP-XP')}</b> · {tr('ур.', 'lvl ')}{finishResult.bp.level}</span>
                </div>
              )}
              {finishResult.account.xp_delta !== 0 && (
                <div className={styles.endRewardItem}>
                  <span className={styles.endRewardIco} style={{ color: finishResult.account.xp_delta > 0 ? 'var(--neon-li)' : 'var(--neon-rd)' }}>↑</span>
                  <span>
                    {finishResult.account.xp_delta > 0 ? '+' : ''}{finishResult.account.xp_delta} <b>{tr('опыта', 'XP')}</b>
                  </span>
                </div>
              )}
              {/* АНИМ.3: level_ups показываются в endLevelStrip выше, не дублируем здесь. */}
              {finishResult.account.rewards.length > 0 && (
                <>
                  {finishResult.account.rewards.map((r, i) => (
                    <div key={i} className={styles.endRewardItem}>
                      <span className={styles.endRewardIco} style={{ color: 'var(--neon-li)' }}>✓</span>
                      <span>{r}</span>
                    </div>
                  ))}
                </>
              )}
              {/* АНИМ.2: league events (promoted/demoted/firewall*) теперь
                  показываются ОТДЕЛЬНОЙ модалкой LeagueTransitionModal после
                  тапа «Дальше». Дублирование здесь убрано. */}
              {scriptsEarned === 0 &&
               (!finishResult.bp || finishResult.bp.gained === 0) &&
               finishResult.account.xp_delta === 0 &&
               finishResult.account.rewards.length === 0 &&
               !finishResult.league?.promoted && !finishResult.league?.demoted &&
               !finishResult.league?.firewallGained &&
               !finishResult.league?.firewallUsedAntiDemote && !finishResult.league?.firewallUsedAntiYoyo && (
                <span style={{ color: 'var(--t-mute)', fontSize: 10 }}>{tr('— без наград (PvE-повтор) —', '— no rewards (PvE repeat) —')}</span>
              )}
            </div>
          )}
        </div>

        {/* РЕП.1: кнопка просмотра реплея (если match_id уже получен). */}
        {finishResult?.match_id && (
          <button
            onClick={() => {
              // Открываем replay в новой "сессии" — заменяем текущий URL чтобы
              // back-кнопка из replay вернула в Архив (не в законченный бой).
              window.location.href = `/replay/${finishResult.match_id}`;
            }}
            style={{
              padding: '10px',
              background: 'rgba(61, 240, 255, 0.1)',
              color: 'var(--neon-cy)',
              border: 0,
              boxShadow: 'inset 0 0 0 0.5px var(--neon-cy)',
              fontSize: 11,
              letterSpacing: '0.1em',
              fontFamily: 'inherit',
              fontWeight: 700,
              cursor: 'pointer',
              marginBottom: 8,
              clipPath: 'polygon(0 0, calc(100% - 5px) 0, 100% 5px, 100% 100%, 5px 100%, 0 calc(100% - 5px))',
            }}
          >
            📼 {tr('ПОСМОТРЕТЬ РЕПЛЕЙ', 'WATCH REPLAY')}
          </button>
        )}

        {/* Кнопка копирования лога — для дебага. Над основной кнопкой. */}
        {logEntries && logEntries.length > 0 && (
          <button
            onClick={copyLog}
            style={{
              padding: '8px',
              background: 'transparent',
              color: 'var(--t-md)',
              border: '0.5px solid var(--t-md)',
              fontSize: 10,
              letterSpacing: '0.08em',
              fontFamily: 'inherit',
              cursor: 'pointer',
              marginBottom: 8
            }}
          >
            {copyLabel}
          </button>
        )}

        <button className={styles.endBtn} onClick={onLeave}>
          {winnerIdx === 0 ? tr('⟳ ЕЩЁ ОДИН БОЙ', '⟳ ONE MORE BATTLE') : tr('⟵ В МЕНЮ', '⟵ TO MENU')}
        </button>
      </div>
    </div>
  );
}

// ────────── Главный компонент ──────────
export function BattleScreen() {
  const navigate = useNavigate();
  const tx = useScreenTransition();
  const location = useLocation();
  const playerStore = useGameStore(s => s.player);
  const decks = useGameStore(s => s.decks);

  // Получаем выбранную колоду из MatchmakingScreen (location.state) →
  // её фракция и Герой идут в createMatchForBattle.
  const battleState = (location.state as {
    deckId?: number;
    deckName?: string;
    warStake?: number | null;
    opponent?: string;
    opponentFaction?: string;
    foundIn?: string;
    // ПвП.6.1: маркер режима PvP. MatchmakingScreen ставит после успешного
    // joinOrCreate. PvE-флоу этот ключ не передаёт → ветка ниже использует
    // локальный движок и бота как раньше.
    pvpMode?: boolean;
  } | null) || null;
  // ПвП.6.1: PvP-режим определяется по флагу из MatchmakingScreen +
  // наличию активной Colyseus-комнаты (на всякий случай — без комнаты
  // пытаться в PvP бессмысленно, fallback в /pvp).
  const isPvP = !!battleState?.pvpMode;
  const selectedDeck = battleState?.deckId != null
    ? decks.find(d => d.id === battleState.deckId)
    : null;
  const startFaction: Faction = (selectedDeck?.faction || playerStore.faction || 'deus') as Faction;
  const startHeroId = selectedDeck?.heroId || null;

  // ФИКС: раньше playerDeck не передавался в engine → игрок играл
  // рандомной колодой фракции (включая Epic/Legendary, которых у него
  // нет). Теперь разворачиваем deck.cards → массив шаблонов карт через
  // getCardData (повторяем `count` раз каждую запись).
  const startDeck = useMemo(() => {
    if (!selectedDeck?.cards?.length) return null;
    const list: any[] = [];
    for (const entry of selectedDeck.cards) {
      const tpl = getCardData(entry.cardId);
      if (!tpl) continue;
      for (let i = 0; i < entry.count; i++) list.push(tpl);
    }
    return list.length > 0 ? list : null;
  }, [selectedDeck]);

  // Задача «ошибка вместо фабричной колоды»: в PvE играем ТОЛЬКО реальной
  // колодой игрока. Если колода не разрешилась (нет/битая/<24) — бой НЕ
  // начинаем фабричной, а уводим на /pvp с ошибкой (см. useEffect ниже).
  // В PvP колода приходит с сервера → guard не трогает live-флоу.
  const pveDeckInvalid = !isPvP && (!startDeck || startDeck.length < 24);

  // ПвП.6.1: setMatch нужен — в PvP мы заменяем весь match-объект из
  // Colyseus state на каждом onStateChange (не мутируем). В PvE как раньше
  // — match мутируется движком, refresh() форсит ререндер.
  const [match, setMatch] = useState<Match>(() => {
    if (isPvP || pveDeckInvalid) {
      // PvP: шелл переписывается первым onStateChange. PvE с битой колодой:
      // бой не начинаем фабричной — useEffect ниже уводит на /pvp с ошибкой.
      return makeEmptyPvpMatch() as Match;
    }
    return createMatchForBattle(
      startFaction, startHeroId,
      playerStore.nick || trStatic('Игрок', 'Player'),
      startDeck,
      useGameStore.getState().progress.league
    );
  });
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  const refresh = forceUpdate;

  // Задача «ошибка вместо фабричной колоды»: PvE-бой с невалидной колодой
  // не стартуем — уводим на /pvp с понятной ошибкой (вместо тихой фабричной).
  useEffect(() => {
    if (pveDeckInvalid) {
      navigate('/pvp', {
        replace: true,
        state: { matchmakingError: true, deckLoadFailed: true, blamedSelf: true },
      });
    }
  }, [pveDeckInvalid, navigate]);

  // ПвП.6.1: ссылка на Colyseus-комнату из singleton (выставлена
  // MatchmakingScreen.tsx после успешного joinOrCreate).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pvpRoomRef = useRef<any>(null);

  // АНИМ.3: подписка на server-state в PvP-режиме (бутафория).
  //
  // На каждый onStateChange:
  // 1. Применяем финальный snap СРАЗУ через setMatch (не блокируем).
  // 2. Если combatEventsTick изменился — запускаем playCombatEvents
  //    fire-and-forget. Overlay-события играются параллельно с уже-
  //    отрисованным final state'ом.
  //
  // Никакого freeze, никакого while loop, никакого animatingRef. Если
  // playCombatEvents упадёт — overlay просто не нарисуется, игра идёт.
  const lastCombatTickRef = useRef<number>(-1);
  useEffect(() => {
    if (!isPvP) return;
    const r = getCurrentRoom();
    if (!r) {
      navigate('/pvp', { replace: true });
      return;
    }
    pvpRoomRef.current = r;

    const update = () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const snap: any = (r.state as any).toJSON?.();
        if (!snap) return;

        animatorRef.current.updateRectCache();

        const newTick: number = snap.combatEventsTick || 0;
        // АНИМ.7-fix-shared-ref: КОПИЯ array. Colyseus toJSON() может
        // возвращать reference на внутренний ArraySchema-buffer; на
        // следующем sync он мутируется (clear → push), и worker увидит
        // ту мутированную версию (пустую) когда дойдёт до неё.
        const events: string[] = [...(snap.combatEvents || [])];
        const hasNewBatch = newTick > lastCombatTickRef.current && lastCombatTickRef.current >= 0 && events.length > 0;
        const fullMatch = colyseusToMatch(snap, r.sessionId) as Match;

        // АНИМ.7: MatchOver gate.
        if (fullMatch.matchOver && hasNewBatch) {
          const heldMatch = { ...fullMatch, matchOver: false } as Match;
          setMatch(heldMatch);
          console.log(`[pvp] combatEventsTick: ${lastCombatTickRef.current} → ${newTick}, ${events.length} events → enqueue (matchOver-gated)`);
          animatorRef.current.enqueueBatch(events);
          animatorRef.current.scheduleAfterIdle(() => {
            console.log('[pvp] queue idle → apply matchOver');
            setMatch(fullMatch);
          });
        } else {
          setMatch(fullMatch);
          if (hasNewBatch) {
            console.log(`[pvp] combatEventsTick: ${lastCombatTickRef.current} → ${newTick}, ${events.length} events → enqueue`);
            animatorRef.current.enqueueBatch(events);
          }
        }
        // АНИМ.7-fix-tick-race: НЕ обновляем lastTick если tick новый,
        // но events пустые. Это значит Colyseus прислал tick++ отдельным
        // delta ДО combatEvents update. На следующем sync events придут
        // с тем же tick — и нам нужно НЕ пропустить их.
        const tickAdvanced = newTick > lastCombatTickRef.current;
        if (!tickAdvanced || events.length > 0) {
          lastCombatTickRef.current = newTick;
        }
      } catch (e) {
        console.warn('[pvp] state update failed', e);
      }
    };
    update();
    r.onStateChange(update);
    r.onError((code: number, msg?: string) => {
      // eslint-disable-next-line no-console
      console.error('[pvp] room error', code, msg);
    });
    r.onLeave(() => setCurrentRoom(null));
    // 2d reveal-pick (PvP): сервер вскрыл случайные Моды из колоды (анти-чит RNG)
    // → открываем picker. Выбор уйдёт назад в play_action (revealed+picked).
    r.onMessage('mod_reveal', (msg: any) => {
      const mods = (msg?.mods || []) as any[];
      if (mods.length === 0) return;
      const exact = Math.min(msg.pickCount || 1, mods.length);
      const cd = getCardData(msg.cardId);
      setMultiSelectPick({
        card: { instanceId: msg.cardInstanceId, id: msg.cardId, name_ru: cd?.name_ru || msg.cardId },
        candidates: mods, maxCount: msg.pickCount || 1, exactCount: exact,
        mode: 'mod-reveal',
        revealedInstanceIds: mods.map((m) => m.instanceId),
        hintRu: `Вскрыто ${mods.length} — выбери ${exact} в руку (остальные в сброс)`,
        hintEn: `Revealed ${mods.length} — pick ${exact} to hand (rest discarded)`,
      });
    });
    return () => {
      const room = pvpRoomRef.current;
      if (room) {
        setCurrentRoom(null);
        room.leave().catch(() => {});
        pvpRoomRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPvP]);

  // Hand-bar auto-scroll: при росте руки скроллим до конца чтобы новая
  // карта была видна. Без этого новые карты уезжали в overflow-x:auto
  // справа — игрок видел только первые 5 и думал что добор не работает.
  // Особенно заметно когда в руке только Действия (не уходят с поля).
  const handBarRef = useRef<HTMLDivElement>(null);
  const prevHandLenRef = useRef(0);

  // АНИМ.1: Animator — единый на матч. Engine зовёт его методы в PvE,
  // playCombatEvents проигрывает события в PvP. AnimationLayer подписан.
  // АНИМ.4-fix-race: registerPlayer один раз, enqueueBatch обеспечивает
  // последовательное проигрывание (без параллельных play которые дрались
  // за animator.current).
  const animatorRef = useRef<Animator>(new Animator());
  // АНИМ-fix-perspective: ref на myServerIdx — обновляется при изменении
  // match (см. ниже). setPlayer вызывается ОДИН раз с стабильным closure,
  // но читает актуальный idx через ref.
  const myServerIdxRef = useRef<0 | 1>(0);
  useEffect(() => {
    animatorRef.current.setPlayer((raw, anim) =>
      playCombatEvents(raw, anim, myServerIdxRef.current));
  }, []);
  // АНИМ.4-fix-rect-cache: после КАЖДОГО render обновляем кэш позиций
  // юнитов. Чтобы playCombatEvents в очереди мог найти позиции даже
  // если он играет с задержкой и юниты успели появиться или умереть.
  // Прокидываем rerender в animator — engine между мутациями state'а
  // зовёт anim.rerender() чтобы UI отрисовал промежуточные состояния
  // (получение урона, смерть и т.п.).
  animatorRef.current.rerenderFn = forceUpdate;

  // АНИМ-fix-perspective: обновляем myServerIdxRef из match._pvpMyIdx
  // на каждом ререндере. playCombatEvents читает текущее значение
  // через ref в closure, выставленном в setPlayer выше.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  myServerIdxRef.current = ((match as any)?._pvpMyIdx ?? 0) as 0 | 1;

  // UI state
  // detailCard: data — для GameCard.full; handCard (optional) — оригинальный
  // экземпляр из руки игрока, нужен чтобы знать что это Action игрока и
  // показать кнопку «СЫГРАТЬ» в модалке.
  const [detailCard, setDetailCard] = useState<{
    data: CardData;
    handCard?: any;
    // БУГ.10: engine-instance (юнит на поле или карта в руке) — нужен
    // чтобы видеть runtime-поля типа attackPriority в детальном виде.
    runtimeCard?: any;
  } | null>(null);
  const [detailHero, setDetailHero] = useState<any | null>(null);
  // Просмотр колоды/сброса игрока ИЛИ сброса врага.
  // ownerIdx: 0 = я, 1 = враг. Колода врага скрыта (cheating); сброс
  // публичный (юзер видел как карты туда уходили).
  const [pile, setPile] = useState<{ kind: 'deck' | 'discard'; ownerIdx: 0 | 1 } | null>(null);
  // Стенд Влияния — открыт ли (только для Деус-игрока).
  const [standOpen, setStandOpen] = useState(false);
  // Детали карты Влияния (отдельная модалка чтобы не лезть в GameCard).
  const [influenceDetail, setInfluenceDetail] = useState<InfluenceCardTpl | null>(null);
  // Лог боя — открыт ли. Кнопка ≡ в HUD.
  const [logOpen, setLogOpen] = useState(false);
  // Toast'ы для последних событий движка (Приказы, Триггеры, Влияние,
  // смерти и т.п.). Подписаны на match.log.onAdd. Каждый виден 2.5с,
  // потом исчезает. Видно сразу что произошло.
  const [logToasts, setLogToasts] = useState<{ id: number; text: string; cls: string }[]>([]);
  const toastIdRef = useRef(0);

  // БОЙ.1.6: результат /api/matches/finish — пока null, пока запрос летит.
  const [finishResult, setFinishResult] = useState<MatchFinishResult | null>(null);
  const [finishError, setFinishError] = useState<string | null>(null);
  const [finishLoading, setFinishLoading] = useState(false);
  // Защита от двойного вызова finishMatch при matchOver.
  const finishFiredRef = useRef(false);
  // АНИМ.2-4: cascade модалок после EndOfMatchModal.
  // Порядок: EndOfMatch → [milestone LevelUp] → [LeagueTransition] → /pvp.
  // Обычные level-ups показываются только embedded-strip в EndOfMatch.
  // Отдельная LevelUpModal — только на milestone (10/25/50/100).
  const [leagueEvent, setLeagueEvent] = useState<LeagueEventData | null>(null);
  const [levelUpData, setLevelUpData] = useState<{ levelUps: number[]; rewards: string[] } | null>(null);
  const [endOfMatchClosed, setEndOfMatchClosed] = useState(false);
  // Время старта матча — для durationSec.
  const matchStartRef = useRef<number>(Date.now());

  // ТАЙ.1: countdown текущей фазы. Читаем match.phaseDeadlineAt (unix-мс,
  // ставится в engine timing.startPhase, синкается через схему в PvP).
  // tick 200мс достаточно для плавного отсчёта без перегрева.
  const [phaseRemainingMs, setPhaseRemainingMs] = useState<number | null>(null);
  const pveTimeoutFiredRef = useRef<number>(0); // на deadline = X сработали или нет
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const deadline = (match as any)?.phaseDeadlineAt as number | null | undefined;
    if (!deadline) {
      setPhaseRemainingMs(null);
      pveTimeoutFiredRef.current = 0;
      return;
    }
    const tick = () => {
      const rem = deadline - Date.now();
      setPhaseRemainingMs(Math.max(0, rem));
    };
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(match as any)?.phaseDeadlineAt]);
  // ТАЙ.1: рамка экрана пульсирует красным когда <= 5с до конца фазы.
  const phaseUrgent = phaseRemainingMs != null && phaseRemainingMs <= 5000 && phaseRemainingMs > 0;
  // Этап 71: тик последних 5 секунд (по смене цифры 5-4-3-2-1, в такт пульсу).
  const lastTickSecRef = useRef<number>(-1);
  useEffect(() => {
    if (phaseRemainingMs == null || phaseRemainingMs <= 0 || phaseRemainingMs > 5000) {
      lastTickSecRef.current = -1;
      return;
    }
    const sec = Math.ceil(phaseRemainingMs / 1000);
    if (sec !== lastTickSecRef.current) {
      lastTickSecRef.current = sec;
      playSfx('clockTick');
    }
  }, [phaseRemainingMs]);

  // АНИМ.4-fix-rect-cache: после каждого render обновляем кэш позиций.
  // useLayoutEffect — синхронно после DOM commit, ДО paint. Свежевышедшие
  // юниты уже в DOM. Это покрывает случай когда событие attack ссылается
  // на юнита который только-только появился (бот свежевыставил).
  // (useEffect/useLayoutEffect без deps зовётся каждый render.)
  useEffect(() => {
    animatorRef.current.updateRectCache();
  });

  // Подписка на лог матча — пушим новые записи как toast'ы (фильтруем
  // шумные phase-markers).
  useEffect(() => {
    if (!match.log?.onAdd) return;
    const unsub = match.log.onAdd((line: string) => {
      // Скрываем phase/turn markers, draw-events, тайминги, бюджет — чтобы
      // не спамить. Оставляем интересные события.
      if (/^═══|^— Фаза|^\(Фаза|^🪙 |^🎯 Ф2|тянет карту|разместил карту/.test(line)) return;
      const id = ++toastIdRef.current;
      let cls = 'logToastNeutral';
      if (/✨/.test(line))               cls = 'logToastInflu';
      else if (/💥|урон|⚡/.test(line))  cls = 'logToastDmg';
      else if (/умер|погиб|🏆|💀/.test(line)) cls = 'logToastDeath';
      else if (/🛡|👁/.test(line))        cls = 'logToastKey';
      setLogToasts((t) => [...t.slice(-4), { id, text: line, cls }]);
      setTimeout(() => {
        setLogToasts((t) => t.filter((x) => x.id !== id));
      }, 2800);
    });
    return () => { try { unsub?.(); } catch {} };
  }, [match]);
  // Action target-pick режим: после тапа «СЫГРАТЬ» на целевом Действии
  // юзер выбирает цель на поле. targetIds — set instanceId валидных целей.
  const [actionPick, setActionPick] = useState<{
    card: any;
    targetIds: Set<number>;
    // Этап 63 — обобщённый board-tap: если задан onPick, тап по подсвеченному
    // юниту вызывает его (order-target картой: Фиксер / Хирург Vita / Сапёр —
    // тык по карте-цели прямо на поле). Без onPick — обычный розыгрыш Действия.
    onPick?: (unit: any) => void;
    bannerRu?: string;
    bannerEn?: string;
  } | null>(null);

  // ПвП.fix-move-action: для Перегруппировки/Передислокации — после
  // выбора цели открывается второй pick: куда переместить (свободная
  // ячейка моей стороны). Modal с 2×4 сеткой.
  const [actionMovePick, setActionMovePick] = useState<{
    card: any;
    target: any;
  } | null>(null);

  // БУГ.2: placement-target-pick — после drop карты на ячейку, если
  // её Order требует target (order_target='enemy_choose'), открываем
  // overlay со списком валидных целей. По выбору сохраняем opts.target
  // в pendingOrderOpts и фиксируем размещение. Отмена — карта возвращается
  // в руку (placement не происходит).
  const [orderTargetPick, setOrderTargetPick] = useState<{
    card: any;
    line: 0 | 1;
    slot: number;
    candidates: any[];
    purpose: string;
  } | null>(null);

  // 2d Фиксер (syn_fixer): каскад placement-picker'а.
  //   line 0 (перенос Мода): source (свой юнит с Модом) → target (свой юнит ≠ source).
  //   line 1 (движение): unit (любой юнит) → slot (своб. ячейка на стороне владельца).
  // По завершении пишем opts в pendingOrderOpts + фиксируем placement.
  const [fikserPick, setFikserPick] = useState<{
    card: any; line: 0 | 1; slot: number;
    step: 'source' | 'target' | 'unit' | 'slot';
    sourceInstanceId?: number; sourceModIndex?: number;
    unitInstanceId?: number;
  } | null>(null);

  // 2d: мультиселект. Великая жертва — до 3 юнитов (mode='sacrifice');
  // Дозаправка/Конструктор — reveal-pick Модов ровно exactCount (mode='mod-reveal').
  const [multiSelectPick, setMultiSelectPick] = useState<{
    card: any; candidates: any[]; maxCount: number; exactCount?: number;
    hintRu: string; hintEn: string;
    mode: 'sacrifice' | 'mod-reveal';
    revealedInstanceIds?: number[];
  } | null>(null);

  // БУГ.3: attack-priority picker — каждый Глобал-юнит при размещении
  // требует выбора приоритета атаки (диздок §7.3). Выбор фиксируется до
  // конца игры (card.attackPriority). После выбора → возможно ещё target
  // picker (если карта требует enemy_choose) → setPendingPlacement.
  const [attackPriorityPick, setAttackPriorityPick] = useState<{
    card: any;
    line: 0 | 1;
    slot: number;
  } | null>(null);
  // БУГ.5-fix4: ref-guard от двойного вызова onPick (тосты дублировались
  // в TG WebView когда onPointerDown + onClick оба срабатывали).
  // Сбрасывается при следующем открытии picker'а.
  const pickingPriorityRef = useRef(false);
  const pickingTargetRef = useRef(false);

  // 2d: heal-priority picker — хилеры (5 livlife + 2 глобал-медика) при
  // размещении выбирают, КОГО лечить. Глобал-медики идут каскадом:
  // picker атаки → picker лечения → размещение.
  const [healPriorityPick, setHealPriorityPick] = useState<{
    card: any;
    line: 0 | 1;
    slot: number;
  } | null>(null);
  const pickingHealPriorityRef = useRef(false);

  // Drag state (рефы, без re-render для перфа)
  // Универсальный drag-state: либо карта из руки (kind='card'), либо
  // Герой для атаки (kind='hero'). Один в один момент.
  const dragState = useRef<{
    kind: 'card' | 'hero' | null;
    card: any | null;          // для kind='card'
    cardIsPending: boolean;    // для kind='card'
    started: boolean;
    cancelled: boolean;
    recentlyDragged: boolean;
    startX: number; startY: number;
    curX: number; curY: number;
    hoverCell: { line: 0 | 1; slot: number } | null;  // для kind='card' (юнит/действие → пустая ячейка)
    // Этап 56 (#7): для карты-МОДА drop-цель — мой ЮНИТ (занятая ячейка).
    hoverModTarget: { instanceId: number; line: 0 | 1; slot: number } | null;
    hoverAttack: { kind: 'unit' | 'hero'; instanceId?: number } | null; // для kind='hero'
    cleanup: (() => void) | null;
  }>({
    kind: null,
    card: null, cardIsPending: false, started: false, cancelled: false,
    recentlyDragged: false,
    startX: 0, startY: 0, curX: 0, curY: 0,
    hoverCell: null, hoverModTarget: null, hoverAttack: null, cleanup: null
  });
  const [, bumpDrag] = useReducer((x: number) => x + 1, 0);

  // Регистрация native event listeners на каждый hcard через ref-callback.
  // Используем touch + mouse — pointerevents в TG WebView показали себя
  // нестабильно.
  const cardHandlersRef = useRef(new Map<HTMLDivElement, () => void>());

  const isPlacementRef = useRef(false);
  const attackModeRef = useRef(false);
  const matchPhase = match.phase;
  isPlacementRef.current = matchPhase === PHASE.PLACEMENT;

  // Запуск drag'а. kind='card' — карта из руки на пустую ячейку
  // (только в Ф2). kind='hero' — Герой на врага (только в Ф4/Ф5,
  // когда моя очередь).
  const startDrag = (
    kind: 'card' | 'hero',
    card: any, isPending: boolean,
    clientX: number, clientY: number,
    isTouch: boolean
  ) => {
    if (kind === 'card' && !isPlacementRef.current) return;
    if (kind === 'hero' && !attackModeRef.current) return;
    const state = dragState.current;
    if (state.cleanup) state.cleanup();
    state.kind = kind;
    state.card = card;
    state.cardIsPending = isPending;
    state.started = false;
    state.cancelled = false;
    state.startX = clientX;
    state.startY = clientY;
    state.curX = clientX;
    state.curY = clientY;
    state.hoverCell = null;
    state.hoverModTarget = null;
    state.hoverAttack = null;

    const updateFromPos = (x: number, y: number) => {
      state.curX = x;
      state.curY = y;
      if (state.started) {
        const el = document.elementFromPoint(x, y) as HTMLElement | null;
        if (state.kind === 'card') {
          // Этап 56 (#7): карта-МОД ложится на МОЙ ЮНИТ (занятая ячейка), а не
          // на пустую. У ячейки юнита нет data-drop-zone, но есть data-side="my"
          // + data-instance-id — по ним и ищем цель.
          if (state.card?.type === 'mod') {
            const unitCell = el?.closest('[data-side="my"][data-instance-id]') as HTMLElement | null;
            if (unitCell) {
              const instanceId = parseInt(unitCell.getAttribute('data-instance-id') || '0', 10);
              const line = parseInt(unitCell.getAttribute('data-line') || '0', 10) as 0 | 1;
              const slot = parseInt(unitCell.getAttribute('data-slot') || '0', 10);
              state.hoverModTarget = instanceId ? { instanceId, line, slot } : null;
            } else {
              state.hoverModTarget = null;
            }
            state.hoverCell = null;
          } else {
            // Юнит/Действие — пустые мои ячейки.
            const cell = el?.closest('[data-drop-zone^="me-"]') as HTMLElement | null;
            if (cell) {
              const zone = cell.getAttribute('data-drop-zone') || '';
              const slot = parseInt(cell.getAttribute('data-slot') || '0', 10);
              const line = (zone === 'me-0' ? 0 : 1) as 0 | 1;
              state.hoverCell = { line, slot };
            } else {
              state.hoverCell = null;
            }
          }
        } else if (state.kind === 'hero') {
          // Drop zones для Героя — вражеские юниты + вражеский Герой
          // (если Авангард чист).
          const target = el?.closest('[data-attack-drop]') as HTMLElement | null;
          if (target) {
            const drop = target.getAttribute('data-attack-drop') || '';
            if (drop === 'hero') {
              state.hoverAttack = { kind: 'hero' };
            } else if (drop.startsWith('unit-')) {
              const id = parseInt(drop.slice(5), 10);
              state.hoverAttack = { kind: 'unit', instanceId: id };
            } else {
              state.hoverAttack = null;
            }
          } else {
            state.hoverAttack = null;
          }
        }
        bumpDrag();
      }
    };

    // ВАЖНО: tap (без drag) обрабатывает React onClick на самой карте.
    // Здесь finish() реагирует ТОЛЬКО на завершение drag'а — drop
    // pending placement на ячейку. Если drag не активировался —
    // ничего не делаем, и браузер натурально сгенерит click → onClick.
    const finish = () => {
      if (state.cleanup) state.cleanup();
      const wasDragging = state.started;
      if (wasDragging) {
        if (state.kind === 'card' && state.card?.type === 'mod' && state.hoverModTarget) {
          // Этап 56 (#7): карта-МОД брошена на мой юнит → pending-Мод с targetInstanceId.
          // Раньше occupied-ячейка не была drop-зоной → драг Мода «проваливался» молча.
          const cd = state.card;
          const okMod = canAddPlacement(match, 0, cd);
          if (okMod.ok) {
            setPendingPlacement(match, 0, { cardInstanceId: cd.instanceId, targetInstanceId: state.hoverModTarget.instanceId });
            const tid = ++toastIdRef.current;
            setLogToasts((t) => [...t.slice(-4), { id: tid, text: '◆ Мод нацелен — применится на ▶ ХОД', cls: 'logToastInflu' }]);
            setTimeout(() => setLogToasts((t) => t.filter((x) => x.id !== tid)), 2500);
            refresh();
          } else {
            const reasonText = okMod.reason === 'mod_requires_main'
              ? trStatic('сначала выставь юнит/Действие в этот ход', 'place a unit/action first this turn')
              : okMod.reason === 'one_mod_per_turn'
                ? trStatic('за ход — только 1 Мод', 'only 1 Mod per turn')
                : (okMod.reason || trStatic('недоступно', 'unavailable'));
            const tid = ++toastIdRef.current;
            setLogToasts((t) => [...t.slice(-4), { id: tid, text: `${trStatic('Мод нельзя сыграть', "Can't play Mod")}: ${reasonText}`, cls: 'logToastDeath' }]);
            setTimeout(() => setLogToasts((t) => t.filter((x) => x.id !== tid)), 3000);
          }
        } else if (state.kind === 'card' && state.hoverCell) {
          const cd = state.card;
          const placement = canAddPlacement(match, 0, cd);
          if (placement.ok) {
            // БУГ.3: для Глобал-юнита — сначала picker приоритета атаки.
            // После выбора cascade-логика дальше (target picker → placement).
            if (needsAttackPriorityPicker(cd)) {
              // БУГ.5-fix4: сбрасываем guard перед новым открытием picker'а.
              pickingPriorityRef.current = false;
              setAttackPriorityPick({
                card: cd,
                line: state.hoverCell.line,
                slot: state.hoverCell.slot
              });
            } else if (needsHealPriorityPicker(cd)) {
              // 2d: хилер (не-Глобал) — сразу picker приоритета лечения.
              pickingHealPriorityRef.current = false;
              setHealPriorityPick({
                card: cd,
                line: state.hoverCell.line,
                slot: state.hoverCell.slot
              });
            } else {
              proceedWithPlacement(cd, state.hoverCell.line, state.hoverCell.slot);
            }
          } else {
            console.warn('[battle] placement denied:', placement.reason);
          }
        } else if (state.kind === 'hero' && state.hoverAttack) {
          // Атака Героем по найденной цели.
          let target: any = null;
          if (state.hoverAttack.kind === 'hero') {
            target = enemy.hero;
          } else if (state.hoverAttack.kind === 'unit' && state.hoverAttack.instanceId) {
            const id = state.hoverAttack.instanceId;
            for (const line of [0, 1] as const) {
              for (const u of enemy.field[line]) {
                if (u?.instanceId === id) { target = u; break; }
              }
              if (target) break;
            }
          }
          if (target) {
            // Запустит heroAttackOrSkip + продвижение фазы + бот следующая фаза.
            onPlayerHeroAttack(target);
          }
        }
      }
      state.kind = null;
      state.card = null;
      state.cardIsPending = false;
      state.started = false;
      state.cancelled = false;
      state.hoverCell = null;
      state.hoverAttack = null;
      if (wasDragging) {
        state.recentlyDragged = true;
        setTimeout(() => { state.recentlyDragged = false; }, 250);
      }
      bumpDrag();
    };

    if (isTouch) {
      const onTouchMove = (ev: TouchEvent) => {
        const t = ev.touches[0];
        if (!t) return;
        // Всегда обновляем текущую позицию (чтобы finish мог проверить
        // итоговую дистанцию).
        state.curX = t.clientX;
        state.curY = t.clientY;
        const dx = t.clientX - state.startX;
        const dy = t.clientY - state.startY;
        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);

        if (!state.started && !state.cancelled) {
          // Решаем: drag (вертикально) или scroll (горизонтально)?
          // Touch-action: pan-x на карте — горизонтальный scroll руки
          // браузер берёт на себя. Вертикальное движение остаётся JS.
          if (absDy > 6 && absDy > absDx) {
            // Уверенно вертикально → drag.
            state.started = true;
          } else if (absDx > 6 && absDx >= absDy) {
            // Уверенно горизонтально → scroll руки, отменяем drag.
            state.cancelled = true;
            if (state.cleanup) state.cleanup();
            return;
          }
        }
        if (state.started) {
          ev.preventDefault();
          updateFromPos(t.clientX, t.clientY);
        }
      };
      const onTouchEnd = (_ev: TouchEvent) => finish();
      const onTouchCancel = (_ev: TouchEvent) => {
        // Браузер забрал жест (например, начал scroll). Не считаем это tap.
        state.cancelled = true;
        finish();
      };
      state.cleanup = () => {
        document.removeEventListener('touchmove', onTouchMove, { capture: true } as any);
        document.removeEventListener('touchend', onTouchEnd, { capture: true } as any);
        document.removeEventListener('touchcancel', onTouchCancel, { capture: true } as any);
        state.cleanup = null;
      };
      document.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
      document.addEventListener('touchend', onTouchEnd, { capture: true });
      document.addEventListener('touchcancel', onTouchCancel, { capture: true });
    } else {
      const onMouseMove = (ev: MouseEvent) => {
        const dx = ev.clientX - state.startX;
        const dy = ev.clientY - state.startY;
        if (!state.started && Math.hypot(dx, dy) > 5) {
          state.started = true;
        }
        updateFromPos(ev.clientX, ev.clientY);
      };
      const onMouseUp = (_ev: MouseEvent) => finish();
      state.cleanup = () => {
        document.removeEventListener('mousemove', onMouseMove, { capture: true } as any);
        document.removeEventListener('mouseup', onMouseUp, { capture: true } as any);
        state.cleanup = null;
      };
      document.addEventListener('mousemove', onMouseMove, { capture: true });
      document.addEventListener('mouseup', onMouseUp, { capture: true });
    }
    bumpDrag();
  };

  const registerDragHandler = (
    el: HTMLDivElement | null, card: any, isPending: boolean
  ) => {
    const map = cardHandlersRef.current;
    if (!el) return;
    const prev = map.get(el);
    if (prev) prev();

    // Карты-Действия НЕ кидаются drag'ом — у них нет «места» на поле,
    // они разыгрываются через тап → CardDetailModal → кнопка СЫГРАТЬ
    // (с opt. target-picker'ом). Drag только для type='unit' и 'mod'.
    const isAction = card?.type === 'action';

    const onTouchStart = (e: TouchEvent) => {
      if (!isPlacementRef.current) return;
      if (isAction) return; // только тап
      const t = e.touches[0];
      if (!t) return;
      startDrag('card', card, isPending, t.clientX, t.clientY, true);
    };
    const onMouseDown = (e: MouseEvent) => {
      if (!isPlacementRef.current) return;
      if (isAction) return;
      if (e.button !== 0) return;
      e.preventDefault();
      startDrag('card', card, isPending, e.clientX, e.clientY, false);
    };
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('mousedown', onMouseDown);

    map.set(el, () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('mousedown', onMouseDown);
      map.delete(el);
    });
  };

  // Hero attack — TAP-BASED режим:
  //   1) В моей HERO-фазе показывается кнопка ⚔ АТАКА в pbar.me.
  //   2) Тап → setHeroAttackArmed(true) → enemy units/hero подсвечиваются.
  //   3) Тап по подсвеченной цели = атака.
  //   4) Повторный тап ⚔ → ✕ ОТМЕНА (сбросить выбор).
  // Drag-n-drop версия удалена — оказалась нестабильна в TG WebView.
  const [heroAttackArmed, setHeroAttackArmed] = useState(false);

  // Активация Скрытного юнита (диздок §6.7):
  //   1) В моей HERO-фазе показываются кнопки «◆ АТАКА» рядом со стелс-юнитами.
  //   2) Тап → setStealthPick(unit) → подсветка валидных целей.
  //   3) Тап по подсвеченной цели → activateStealthUnit.
  //   4) Повторный тап на том же юните = отмена.
  const [stealthPick, setStealthPick] = useState<any>(null);

  // БОЙ.1.6: при matchOver — отправить результат на сервер ОДИН РАЗ,
  // получить награды, рефрешнуть профиль для синка XP/Scripts/etc.
  useEffect(() => {
    if (!match.matchOver || finishFiredRef.current) return;
    finishFiredRef.current = true;
    setFinishLoading(true);

    // Маппинг режима матчмейкинга → outcome code. Для PvE-боя (текущая
    // реализация: бот) используем тот же режим что выбрал игрок в очереди.
    // foundIn = 'casual' | 'rated' | 'war' (из MatchmakingScreen) | undefined.
    const queueMode = battleState?.foundIn as ('casual' | 'rated' | 'war' | undefined);
    const isWin = match.winnerIdx === 0;
    const isDraw = match.winnerIdx === 'draw';
    let outcome: MatchOutcomeCode;
    if (queueMode === 'rated') outcome = isWin ? 'rated_win' : 'rated_loss';
    else if (queueMode === 'war') outcome = isWin ? 'war_win' : 'war_loss';
    else outcome = isWin ? 'casual_win' : 'casual_loss';
    // Ничья → засчитываем как поражение в плане XP (нет схемы для draw).
    if (isDraw) {
      outcome = queueMode === 'rated' ? 'rated_loss'
              : queueMode === 'war' ? 'war_loss'
              : 'casual_loss';
    }

    // Скрипты за победу (упрощённо по диздоку §3.5):
    const scriptsEarned = isWin
      ? (queueMode === 'rated' ? 10 : queueMode === 'war' ? 0 : 1)
      : 0;

    const durationSec = Math.round((Date.now() - matchStartRef.current) / 1000);

    // ВОЙ.2: для war-побед считаем чистый выигрыш в центах. Сервер
    // делает то же самое в settleWarMatch — числа должны совпадать
    // (одинаковая формула: accepted × (0.5 если retreat, иначе 1) × 0.9).
    let usdtCentsEarned = 0;
    if (isWin && isPvP && battleState?.foundIn === 'war' && battleState?.warStake) {
      const stake = battleState.warStake;
      const mult = (match as any).warMultiplier || 1;
      const retreat = (match as any).warEndReason === 'retreat';
      const accepted = stake * mult;
      const gross = retreat ? accepted / 2 : accepted;
      const net = Math.floor(gross * 0.9 * 100);
      usdtCentsEarned = net;
    }

    (async () => {
      try {
        // ЛОГ.1: отправляем серверу лог боя для дебага. BattleScreen
        // только casual/rated/war — все логируем.
        const logEntries = match.log?.entries ? [...match.log.entries] : undefined;
        // ПвП.5b: в PvP передаём opponentId из адаптированного state
        // (server-side player.id из БД). В PvE — null (бот).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const opponentId: number | null = isPvP
          ? ((enemy as any)?.playerId || null)
          : null;
        // РЕП.1: roomId Colyseus-комнаты — нужен серверу чтобы забрать
        // replay из in-memory cache (сохранён там на matchOver). Без
        // этого match_history.replay_json остаётся NULL → реплей не доступен.
        const pvpRoomId = isPvP ? (getCurrentRoom()?.roomId || undefined) : undefined;
        console.log(
          `[replay] finishMatch: isPvP=${isPvP} pvpRoomId=${pvpRoomId || 'undefined'} ` +
          `outcome=${outcome} matchOver=${match.matchOver} winnerIdx=${match.winnerIdx}`
        );
        const r = await finishMatch({
          outcome,
          opponentId,
          faction: myFaction,
          // РЕП.2: фракция соперника в момент матча — для архива.
          opponentFaction: enemyFaction,
          heroId: me.hero.id,
          deckId: battleState?.deckId ?? null,
          durationSec,
          mmrDelta: 0,
          scriptsEarned,
          usdtCentsEarned,
          logEntries,
          pvpRoomId
        });
        setFinishResult(r);
        // Рефрешим профиль чтобы Scripts/XP/уровень обновились в сторе.
        try {
          const profile = await getProfile();
          useGameStore.getState().syncFromServer(profile);
        } catch {/* offline */}
      } catch (e: any) {
        setFinishError(e?.message || trStatic('не удалось отправить результат', 'failed to send result'));
      } finally {
        setFinishLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.matchOver]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (stealthPick) setStealthPick(null);
        else if (heroAttackArmed) setHeroAttackArmed(false);
        else if (actionPick) setActionPick(null);
        else if (detailCard) setDetailCard(null);
        else if (detailHero) setDetailHero(null);
        else tx.navigate('/pvp', { replace: true, variant: 'heavy_glitch' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tx, detailCard, detailHero, actionPick, heroAttackArmed, stealthPick]);

  // Cleanup при unmount.
  useEffect(() => {
    return () => {
      if (dragState.current.cleanup) dragState.current.cleanup();
      cardHandlersRef.current.forEach((off) => off());
      cardHandlersRef.current.clear();
      // ЛОГ.1.1: если матч НЕ был завершён через finishMatch — шлём
      // «черновик» лога с пометкой abandoned. Полезно когда экран
      // вылетел в чёрный экран или юзер закрыл приложение.
      // finishFiredRef.current = true означает что finishMatch уже
      // ушёл — в таком случае лог уже сохранён, дубликат не нужен.
      try {
        if (!finishFiredRef.current && match.log?.entries?.length > 0) {
          const durationSec = Math.round((Date.now() - matchStartRef.current) / 1000);
          const body = JSON.stringify({
            faction: match.players?.[0]?.hero?.faction || 'neutral',
            heroId: match.players?.[0]?.hero?.id || 'unknown',
            durationSec,
            deckId: battleState?.deckId ?? null,
            logEntries: match.log.entries,
            reason: 'unmount'
          });
          const base = ((import.meta as any).env?.VITE_API_URL || '').replace(/\/$/, '');
          const token = localStorage.getItem('corps.token') || '';
          // keepalive=true — запрос закроется даже после unmount компонента.
          fetch(`${base}/api/matches/abandon-log`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer ' + token
            },
            body,
            keepalive: true
          }).catch(() => {});
        }
      } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const me = match.players[0];
  const enemy = match.players[1];
  const myFaction = (me.hero.faction || 'deus') as Faction;
  const enemyFaction = (enemy.hero.faction || 'deus') as Faction;

  // Hand-bar auto-scroll к правому краю при росте руки. Если рука
  // переполнилась за пределы экрана (особенно когда в ней только
  // Действия — они не уходят с поля) — без скролла игрок не видит
  // что взялась карта в новый ход. Скроллим только при росте, иначе
  // юзер не сможет смотреть начало руки.
  useEffect(() => {
    const el = handBarRef.current;
    const len = me.hand.length;
    if (el && len > prevHandLenRef.current) {
      requestAnimationFrame(() => {
        try { el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' }); }
        catch { el.scrollLeft = el.scrollWidth; }
      });
    }
    prevHandLenRef.current = len;
  }, [me.hand.length]);
  const turn = match.currentTurn;
  const phase = phaseLabel(match.phase);
  const pNum = phaseNumber(match.phase);
  const isMulligan = match.phase === PHASE.MULLIGAN;
  const isPlacement = match.phase === PHASE.PLACEMENT;

  // ВОЙ.1: Война Корпораций — серверные поля + производные для UI.
  const warStakeUsd: number = (match as any)?.warStakeUsd || 0;
  const warMultiplier: number = (match as any)?.warMultiplier || 1;
  const warSnapPendingBy: string = (match as any)?.warSnapPendingBy || '';
  const warSnapPendingMine: boolean = !!(match as any)?.warSnapPendingIsMine;
  const warSnapPendingByEnemy: boolean = !!warSnapPendingBy && !warSnapPendingMine;
  const warSnapDeadlineAt: number | null = (match as any)?.warSnapDeadlineAt ?? null;
  const warCurrentStake = warStakeUsd * warMultiplier;
  // Можно объявить снап если: war-режим, не моя pending уже, нет вражеской,
  // не достигли ×4, матч не кончен, я (как игрок) активен.
  const canDeclareWarSnap = warStakeUsd > 0
    && !warSnapPendingBy
    && warMultiplier < 4
    && !match.matchOver
    && isPvP;
  // Адресат должен ответить только в Ф2 — модалка показывается только
  // в placement-фазе. Если pending во время Ф3-Ф5 → ждём моей Ф2.
  const showWarSnapModal = isPvP && warSnapPendingByEnemy
    && isPlacement && !match.matchOver;
  // Countdown до истечения 10с — сервер выставит warSnapDeadlineMs
  // только когда стартует таймер (в Ф2 адресата).
  const [warSnapRemaining, setWarSnapRemaining] = useState<number | null>(null);
  useEffect(() => {
    if (!warSnapDeadlineAt) { setWarSnapRemaining(null); return; }
    const tick = () => setWarSnapRemaining(Math.max(0, warSnapDeadlineAt - Date.now()));
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [warSnapDeadlineAt]);
  // Этап 71: стинг-алярм при появлении модалки снапа (ставку подняли).
  useEffect(() => {
    if (showWarSnapModal) playSfx('warSnap');
  }, [showWarSnapModal]);

  const pendingMap = new Map<string, any>();
  for (const pl of (match.pendingPlacements?.[0] || [])) {
    const card = me.hand.find((c: any) => c.instanceId === pl.cardInstanceId);
    if (!card) continue;
    if (pl.line != null && pl.slot != null) {
      pendingMap.set(`${pl.line}-${pl.slot}`, card);
    }
  }
  const pendingInstanceIds = new Set<number>(
    (match.pendingPlacements?.[0] || []).map((pl: any) => pl.cardInstanceId)
  );
  // pendingAction (Действие) — тоже отмечаем как pending в руке.
  const pendingActionId: number | null = me.pendingAction?.cardInstanceId ?? null;
  if (pendingActionId != null) pendingInstanceIds.add(pendingActionId);

  const onMulliganDone = (indices: number[]) => {
    if (isPvP) {
      // ПвП.6.3: шлём выбор серверу. Сервер ждёт ack от обоих игроков
      // (или бот в PvE-fallback уже ack'нут автоматом в startMatch).
      // После обоих ack'ов сервер вызовет finishMulligan, фаза станет
      // PLACEMENT, и onStateChange обновит UI.
      pvpRoomRef.current?.send('mulligan', { indices });
      return;
    }
    applyMulligan(match, 0, indices);
    // Bot тоже делает мулиган (botMulligan возвращает индексы карт на сброс
    // — обычно epic/legendary, которые не успевают активироваться в Initiation
    // Cooldown).
    applyMulligan(match, 1, botMulligan(match, 1));
    finishMulligan(match);
    refresh();
    // После старта Хода 1 — обычная Ф2 (PLACEMENT). Игрок размещает, потом ХОД.
  };

  // ────────── Оркестрация хода ──────────

  // Запускает бот-логику в HERO-фазе, если активный игрок — бот.
  // Используется после commit (когда движок переходит в HERO_FIRST) и
  // после атаки игрока (переход HERO_FIRST→HERO_SECOND или HERO_SECOND→
  // следующий ход).
  const advanceTurn = async () => {
    // ПвП.6.1: в PvP бот-логика работает на СЕРВЕРЕ (см. ПвП.4a).
    // Клиент только подписан на state — ничего не делаем здесь.
    if (isPvP) return;
    // Защита от бесконечного цикла — макс 20 итераций (с запасом).
    for (let i = 0; i < 20; i++) {
      if (match.matchOver) return;
      const phase = match.phase;
      if (phase === PHASE.PLACEMENT || phase === PHASE.MULLIGAN || phase === PHASE.GAME_OVER) {
        return;
      }
      if (phase === PHASE.HERO_FIRST || phase === PHASE.HERO_SECOND) {
        const attackerIdx = phase === PHASE.HERO_FIRST
          ? match.firstPlayerIdx
          : 1 - match.firstPlayerIdx;
        if (attackerIdx === 0) {
          // Очередь игрока — ждём тапа.
          refresh();
          return;
        }
        // Бот атакует. Небольшая пауза для «человечности».
        refresh();
        await new Promise((r) => setTimeout(r, 700));
        if (match.matchOver) return;
        const target = botHeroAttack(match, 1);
        // БОЙ.1.9: анимация атаки Героя (бота).
        await heroAttackOrSkipAnimated(match, target, animatorRef.current);
        refresh();
        continue;
      }
      // PHASE.DRAW / END_TURN — engine переключает на PLACEMENT автоматически.
      // Если оказались здесь — это краткий промежуток, выходим.
      return;
    }
  };

  // Игрок жмёт ▶ ХОД в Ф2. Оркестратор:
  //  1) Бот делает размещение (юниты/Действия + Мод + Влияние).
  //  2) commitPlacementsAndReveal → engine идёт в Ф3, потом HERO_FIRST.
  //  3) advanceTurn — если бот ходит первым, он бьёт сразу.
  const [isProcessing, setIsProcessing] = useState(false);
  const onEndTurn = async () => {
    if (match.phase !== PHASE.PLACEMENT || isProcessing) return;
    setIsProcessing(true);
    try {
      // ПвП.6.1: в PvP-режиме сервер сам делает: своё размещение/Мод/
      // Влияние (бота либо живого игрока), reveal, переход в Ф4/Ф5.
      // Мы шлём end_turn (внутри commitPlacementsAndReveal у нас shim
      // роутит в room.send) и ждём прихода state через onStateChange.
      if (isPvP) {
        await commitPlacementsAndReveal(match, animatorRef.current);
        // НЕ запускаем advanceTurn — в PvP бот на сервере, новый state
        // придёт через onStateChange.
        return;
      }
      // ── 1. Бот размещает ──
      const budget = 1 + (match.players[1].extraPlaysNextTurn || 0);
      for (let i = 0; i < budget; i++) {
        const decision = botPlacement(match, 1);
        if (!decision) break;
        applyBotPlacementDecision(match, 1, decision);
      }
      // Синтетикс — пытается надеть Мод.
      if (match.players[1].hero?.faction === 'syntetics') {
        const modDec = botModPlay(match, 1);
        if (modDec) {
          // applyBotPlacementDecision для мода идёт через тот же путь.
          // bot-random.botModPlay возвращает уже готовый payload (с targetInstanceId).
          setPendingPlacement(match, 1, modDec);
        }
      }
      // Деус — пытается разыграть карту Влияния.
      if (match.players[1].hero?.faction === 'deus') {
        const inf = botInfluencePlay(match, 1);
        if (inf) {
          playInfluenceCard(match, 1, inf.cardId, inf.targetCard);
        }
      }
      refresh();
      // ── 2. Ф3: одновременное вскрытие + авто-атаки с анимацией ──
      // BattleAnimator реализует engine'овский интерфейс anim.* и
      // нотифицирует AttackOverlay о текущем событии. Engine await'ит
      // его методы → между фазами есть паузы для отрисовки.
      await commitPlacementsAndReveal(match, animatorRef.current);
      refresh();
      if (match.matchOver) return;
      // ── 3. Ф4/Ф5 — пробегаем за бота, ждём игрока ──
      await advanceTurn();
    } catch (err) {
      console.error('[battle] end turn failed:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  // Список Действий, для которых после target нужен второй pick — куда
  // переместить (Глобал «Перегруппировка», Глобал «Передислокация»).
  // resolveAction в engine ждёт opts.destLine + opts.destSlot.
  const MOVE_ACTIONS = new Set([
    'global_act_peregruppirovka',
    'global_act_peredislokatsiya',
  ]);

  // Игрок выбрал цель для Действия (Action target-picker).
  const doActionPick = (unit: any) => {
    if (!actionPick) return;
    if (!actionPick.targetIds.has(unit.instanceId)) return;
    // Этап 63 — обобщённый board-tap: order-target карты (Фиксер / Хирург Vita /
    // Сапёр) передают кастомный onPick. Он сам решает что делать (коммит цели
    // или следующий шаг каскада «источник → цель»).
    if (actionPick.onPick) {
      actionPick.onPick(unit);
      return;
    }
    const card = actionPick.card;
    // Для move-Действий — second pick (куда переместить).
    if (MOVE_ACTIONS.has(card.id)) {
      setActionMovePick({ card, target: unit });
      setActionPick(null);
      return;
    }
    setPlayerPendingAction(match, 0, card.instanceId, unit.instanceId, {});
    setActionPick(null);
    refresh();
  };

  // Игрок выбрал ячейку назначения для move-Действия.
  const doMoveDestinationPick = (line: 0 | 1, slot: number) => {
    if (!actionMovePick) return;
    setPlayerPendingAction(
      match, 0,
      actionMovePick.card.instanceId,
      actionMovePick.target.instanceId,
      { destLine: line, destSlot: slot }
    );
    setActionMovePick(null);
    refresh();
  };

  // Игрок выбрал цель атаки Героем в свою HERO-фазу.
  const onPlayerHeroAttack = async (target: any | null) => {
    if (match.phase !== PHASE.HERO_FIRST && match.phase !== PHASE.HERO_SECOND) return;
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      // БОЙ.1.9: анимация атаки Героя. target=null — пропуск (без anim).
      await heroAttackOrSkipAnimated(match, target, animatorRef.current);
      refresh();
      if (match.matchOver) return;
      await advanceTurn();
    } finally {
      setIsProcessing(false);
    }
  };

  // Этап 63 — board-tap инфраструктура для order-target карт: вместо модалки-списка
  // игрок тапает нужный юнит прямо на поле (как «тык по карте»). Один commit-API
  // (setPendingOrderOpts → PvP-aware в engine-shim) → работает и в PvE, и в PvP.
  const fieldUnitsOf = (pl: any): any[] => {
    const out: any[] = [];
    for (const l of [0, 1] as const) {
      for (const u of (pl?.field?.[l] || [])) {
        if (u && u.type === 'unit' && u.alive !== false) out.push(u);
      }
    }
    return out;
  };
  // Открыть board-tap: подсветить candidates на поле + onPick на тап.
  const openBoardTap = (
    cd: any, candidates: any[], bannerRu: string, bannerEn: string,
    onPick: (unit: any) => void
  ) => {
    pickingTargetRef.current = false;  // сброс анти-дабл-тап guard перед новым выбором
    setActionPick({
      card: cd,
      targetIds: new Set(candidates.map((u: any) => u.instanceId)),
      bannerRu, bannerEn, onPick,
    });
  };
  // Финальный commit order-target: фиксируем opts + размещение (как старые модалки).
  const commitOrderTarget = (cd: any, line: 0 | 1, slot: number, opts: any) => {
    if (pickingTargetRef.current) return;
    pickingTargetRef.current = true;
    try {
      if (opts && Object.keys(opts).length > 0) {
        setPendingOrderOpts(match, 0, cd.instanceId, opts);
      }
      setPendingPlacement(match, 0, { cardInstanceId: cd.instanceId, line, slot });
      const tid = ++toastIdRef.current;
      setLogToasts((t) => [...t.slice(-4), { id: tid, text: `✓ ${cd.name_ru || 'Размещ'}`, cls: 'logToastInflu' }]);
      setTimeout(() => setLogToasts((t) => t.filter((x) => x.id !== tid)), 2500);
    } catch (e: any) {
      const tidErr = ++toastIdRef.current;
      setLogToasts((t) => [...t.slice(-4), { id: tidErr, text: `✗ ОШИБКА: ${e?.message || e}`.slice(0, 80), cls: 'logToastDeath' }]);
      setTimeout(() => setLogToasts((t) => t.filter((x) => x.id !== tidErr)), 5000);
    }
    setActionPick(null);
    refresh();
  };

  // БУГ.2/БУГ.3: общая cascade-логика placement.
  // После всех picker'ов (priority, target) фиксируем размещение в pendingPlacements.
  // Если карта требует target picker — открываем его, иначе сразу размещаем.
  const proceedWithPlacement = (cd: any, line: 0 | 1, slot: number) => {
    // 2d: Хирург Vita — выбор союзника для лечения на 2 hp. Движок
    // hirurgVitaOrder читает opts.target (без выбора — дефолт lowest_hp).
    // Переиспользуем orderTargetPick (он коммитит {target} + placement).
    if (cd.id === 'livlife_hirurg_vita') {
      // Этап 63 — board-tap: тап по своему юниту (кого вылечить +2 hp).
      const allies = fieldUnitsOf(match.players[0]);
      if (allies.length > 0) {
        openBoardTap(cd, allies,
          'Тапни своего юнита — кого вылечить (+2 hp)',
          'Tap your unit to heal (+2 hp)',
          (u) => commitOrderTarget(cd, line, slot, { target: u }));
        return;
      }
      // нет союзников на поле — обычный commit, движок применит дефолт lowest_hp.
    }
    const promptResult = determinePlacementPrompt(match, 0, cd, line);
    if (promptResult?.kind === 'prompt' && promptResult.prompt?.type === 'enemy-unit-target') {
      // Этап 63 — board-tap: тап по ВРАЖЕСКОМУ юниту (Сапёр Оникс и др.).
      // QA-фикс: если у prompt задан atkMax (Наёмник: atk ≤ 2) — показываем
      // только подходящих врагов.
      const atkMax = promptResult.prompt.atkMax;
      let candidates = fieldUnitsOf(match.players[1]);
      if (typeof atkMax === 'number') {
        candidates = candidates.filter((u: any) => (u.current?.atk ?? u.atk ?? 0) <= atkMax);
      }
      if (candidates.length > 0) {
        openBoardTap(cd, candidates,
          'Тапни вражеского юнита — цель',
          'Tap an enemy unit — target',
          (u) => commitOrderTarget(cd, line, slot, { target: u }));
      } else {
        // нет вражеских юнитов — обычный commit (движок применит дефолт/пропуск).
        commitOrderTarget(cd, line, slot, {});
      }
    } else if (promptResult?.kind === 'prompt' && promptResult.prompt?.type === 'fikser') {
      // Этап 63 — board-tap каскад Фиксера: тап юнита-ИСТОЧНИКА (с Модом) →
      // тап юнита-ЦЕЛИ (кому передать Мод). Только юниты НА ПОЛЕ.
      const me0 = match.players[0];
      const sources = fieldUnitsOf(me0).filter((u: any) => (u.modifications?.length ?? 0) > 0);
      if (sources.length === 0) {
        // нечего переносить — обычный commit (движок пропустит Приказ).
        commitOrderTarget(cd, line, slot, {});
      } else {
        openBoardTap(cd, sources,
          'Тапни своего юнита с Модом (источник)',
          'Tap your unit with a Mod (source)',
          (src) => {
            const targets = fieldUnitsOf(me0).filter((u: any) => u.instanceId !== src.instanceId);
            if (targets.length === 0) {
              // нет другой field-цели — коммитим без targetInstanceId, движок
              // выберет дефолт на ревиле (в т.ч. свежевыставленного в этот ход).
              commitOrderTarget(cd, line, slot, { sourceInstanceId: src.instanceId, sourceModIndex: 0 });
            } else {
              openBoardTap(cd, targets,
                'Тапни юнита — кому передать Мод',
                'Tap a unit — receive the Mod',
                (tgt) => commitOrderTarget(cd, line, slot, {
                  sourceInstanceId: src.instanceId, sourceModIndex: 0, targetInstanceId: tgt.instanceId,
                }));
            }
          });
      }
    } else {
      // БУГ.5-fix3: visible diagnostic — показываем что фактически попало
      // в pendingPlacements. Если карта на ячейке всё ещё не появляется
      // после этого тоста, значит проблема в render LaneRow / pendingMap.
      console.log('[placement] commit:', cd.id, 'at', line, '/', slot, 'priority:', cd.attackPriority);
      setPendingPlacement(match, 0, { cardInstanceId: cd.instanceId, line, slot });
      const pendingCount = (match.pendingPlacements?.[0] || []).length;
      const tid = ++toastIdRef.current;
      setLogToasts((t) => [...t.slice(-4), {
        id: tid,
        text: `✓ Размещ: л.${line + 1}/сл.${slot + 1} · pending=${pendingCount}`,
        cls: 'logToastInflu'
      }]);
      setTimeout(() => setLogToasts((t) => t.filter((x) => x.id !== tid)), 2500);
      refresh();
    }
  };

  const onLeave = () => {
    if (window.confirm(trStatic(
      'Сдаться и выйти из боя? Это засчитается как поражение.',
      'Surrender and leave the battle? Counts as a loss.'
    ))) {
      tx.navigate('/pvp', { replace: true, variant: 'heavy_glitch' });
    }
  };

  // ТАЙ.1: PvE — когда таймер фазы хитнул 0, автоматически продвигаем
  // фазу. В PvP сервер делает это сам (onPhaseTimeout в match-room.ts).
  // Используем ref'ы чтобы не пересоздавать setTimeout на каждом ререндере
  // при тиках phaseRemainingMs — реагируем только на смену deadline.
  useEffect(() => {
    if (isPvP) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const deadline = (match as any)?.phaseDeadlineAt as number | null | undefined;
    if (!deadline) return;
    if (pveTimeoutFiredRef.current === deadline) return;
    const wait = deadline - Date.now();
    if (wait < 0) {
      // Уже прошло — мб монтировались поздно. Триггерим через 100мс.
      pveTimeoutFiredRef.current = deadline;
    }
    const t = setTimeout(() => {
      if (match.matchOver) return;
      pveTimeoutFiredRef.current = deadline;
      if (match.phase === PHASE.PLACEMENT) {
        onEndTurn();
      } else if (match.phase === PHASE.HERO_FIRST || match.phase === PHASE.HERO_SECOND) {
        const firstIdx = match.firstPlayerIdx;
        const attackerIdx = match.phase === PHASE.HERO_FIRST ? firstIdx : 1 - firstIdx;
        if (attackerIdx === 0) {
          // Я атакую — таймер вышел, пасую.
          onPlayerHeroAttack(null);
        }
        // Если бот — он сам в advanceTurn играл, expiration не нужен.
      }
    }, Math.max(100, wait + 100));
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(match as any)?.phaseDeadlineAt, isPvP, match.phase, match.matchOver]);

  const screenVars: React.CSSProperties = {
    ['--my-c' as any]: `var(--${myFaction === 'global' ? 'gsec' : myFaction === 'syntetics' ? 'syn' : myFaction === 'livlife' ? 'liv' : myFaction === 'neutral' ? 'neu-2' : 'deus'})`,
    ['--enemy-c' as any]: `var(--${enemyFaction === 'global' ? 'gsec' : enemyFaction === 'syntetics' ? 'syn' : enemyFaction === 'livlife' ? 'liv' : enemyFaction === 'neutral' ? 'neu-2' : 'deus'})`,
  };

  const inTg = isInTelegram();
  const drag = dragState.current;
  const isDraggingNow = drag.started && drag.card != null;

  // Очередь игрока в HERO-фазе → режим атаки Героем.
  const myHeroPhaseAttack = !match.matchOver && (
    (match.phase === PHASE.HERO_FIRST && match.firstPlayerIdx === 0) ||
    (match.phase === PHASE.HERO_SECOND && (1 - match.firstPlayerIdx) === 0)
  );
  // Уже отбил Героем в эту фазу — повторно нельзя. Исключение — Двойник Никоси:
  // бонус-атака Героя (heroBonusAttackPending>0) разрешает ещё один удар.
  const heroExhausted = me.hero.heroAttackedThisTurn === true && (me.heroBonusAttackPending || 0) <= 0;
  const attackMode = myHeroPhaseAttack && !heroExhausted;
  attackModeRef.current = attackMode;
  const heroCanHitHero = attackMode && canHeroAttackHero(match, 0);

  // Action target-pick mode активен — поле подсвечивает валидные цели.
  const inActionPick = actionPick != null;

  // Стелс-юниты, которые можно активировать СЕЙЧАС (моя HERO-фаза).
  const myStealthUnits: any[] = myHeroPhaseAttack
    ? getActivatableStealth(match, 0)
    : [];
  // Целевой пул для текущего «armed» стелс-юнита.
  // Engine: getStealthTargets → { units: Card[], heroAllowed: boolean }.
  const stealthTargetIds = new Set<number>();
  let stealthCanHitHero = false;
  if (stealthPick) {
    const targets = getStealthTargets(match, 0, stealthPick);
    stealthCanHitHero = targets.heroAllowed;
    for (const u of targets.units) {
      if (u?.instanceId != null) stealthTargetIds.add(u.instanceId);
    }
  }

  // Активация Скрытного юнита по выбранной цели.
  const onStealthAttack = async (target: any) => {
    if (!stealthPick || isProcessing) return;
    setIsProcessing(true);
    try {
      await activateStealthUnit(match, 0, stealthPick, target);
      setStealthPick(null);
      refresh();
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div
      className={`${styles.phone} ${inTg ? styles.phoneTg : ''} ${phaseUrgent ? styles.phaseUrgent : ''}`}
      style={screenVars}
    >
      {/* HUD */}
      <div className={styles.hudHd}>
        <div className={styles.hudLeft}>
          <span className={styles.hudBrand}>{trStatic('КОРПЫ', 'CORPS')}</span>
        </div>
        <div className={styles.hudRight}>
          <button className={styles.icbtn} disabled title={trStatic('Эмоции (БОЙ.1.3)', 'Emotes (BOY.1.3)')}>😀</button>
          <button
            className={`${styles.icbtn} ${styles.icbtnLog}`}
            onClick={() => setLogOpen(true)}
            title={trStatic(
              `Лог боя (${match.log?.entries?.length ?? 0} записей)`,
              `Battle log (${match.log?.entries?.length ?? 0} entries)`
            )}
          >≡</button>
          <button className={`${styles.icbtn} ${styles.icbtnLeave}`} onClick={onLeave} title={trStatic('Сдаться', 'Surrender')}>✕</button>
        </div>
      </div>

      {/* Враг */}
      <HeroBar
        player={enemy}
        faction={enemyFaction}
        isEnemy
        onHeroTap={
          stealthPick && stealthCanHitHero
            ? () => onStealthAttack(enemy.hero)
            : heroAttackArmed && heroCanHitHero
              // В режиме АТАКА — клик по Герою врага = удар.
              ? () => { onPlayerHeroAttack(enemy.hero); setHeroAttackArmed(false); }
              : () => setDetailHero(enemy.hero)
        }
        onDeckTap={() => {/* deck врага скрыта — это закрытая информация */}}
        onDiscardTap={() => setPile({ kind: 'discard', ownerIdx: 1 })}
        // Подсветка вражеского pbar когда я нажал АТАКА и Авангард чист
        // или активирую Стелс-юнита и Героя бить можно.
        isAttackDropTarget={(heroAttackArmed && heroCanHitHero) || (!!stealthPick && stealthCanHitHero)}
      />

      {/* Поле */}
      <div className={styles.field}>
        {/* QA-фикс (баг вёрстки): подсказка про Скрытность БЫЛА здесь первым
            элементом ВНУТРИ сетки .field (grid-template-rows: 1fr 1fr auto 1fr 1fr).
            Лишний грид-элемент сдвигал все линии на строку вниз → враг 1-я линия
            падала в auto-строку «центр» (сжималась), моя 2-я линия — в лишнюю
            auto-строку (сжималась). Подсказку убрали (решение фаундера); как ими
            бить, всё равно подсказывает per-unit бейдж 👁⚔ на самом стелс-юните. */}
        {/* Тап по вражеской карте в HERO-режиме с armed — атака. Иначе детали. */}
        {(() => {
          // Валидная цель Героя по диздок §6.5: учитывает Stealth, Taunt,
          // untargetable. Engine'овский canHeroAttackUnit делает полную
          // проверку (с Provocation-rule).
          const isValidHeroTarget = (u: any) => canHeroAttackUnit(match, 0, u);
          // Валидная цель для активации Скрытного юнита (текущий stealthPick).
          const isValidStealthTarget = (u: any) => stealthTargetIds.has(u.instanceId);
          const enemyUnitTap = (u: any) => {
            // Приоритет 1: активный stealthPick — попытка ударить выбранную цель.
            if (stealthPick) {
              if (!isValidStealthTarget(u)) return;
              onStealthAttack(u);
              return;
            }
            // Приоритет 2: hero attack armed.
            if (heroAttackArmed) {
              if (!isValidHeroTarget(u)) return;
              onPlayerHeroAttack(u);
              setHeroAttackArmed(false);
              return;
            }
            const d = getCardData(u.id);
            // БУГ.10: u — engine instance вражеского юнита с runtime-полями.
            if (d) setDetailCard({ data: d, runtimeCard: u });
          };
          return <>
            <LaneRow
              cells={enemy.field[1]} isEnemy faction={enemyFaction} lineNum={2}
              pendingMap={new Map()} dragValidLine={false} hoverCell={null} dropZoneBase="enemy"
              attackDropMode={heroAttackArmed || !!stealthPick}
              isValidHeroTarget={
                stealthPick
                  ? (u) => isValidStealthTarget(u)
                  : isValidHeroTarget
              }
              actionPickTargetIds={inActionPick ? actionPick.targetIds : undefined}
              onActionPick={(u) => doActionPick(u)}
              onUnitTap={enemyUnitTap}
            />
            <LaneRow
              cells={enemy.field[0]} isEnemy faction={enemyFaction} lineNum={1}
              pendingMap={new Map()} dragValidLine={false} hoverCell={null} dropZoneBase="enemy"
              attackDropMode={heroAttackArmed || !!stealthPick}
              isValidHeroTarget={
                stealthPick
                  ? (u) => isValidStealthTarget(u)
                  : isValidHeroTarget
              }
              actionPickTargetIds={inActionPick ? actionPick.targetIds : undefined}
              onActionPick={(u) => doActionPick(u)}
              onUnitTap={enemyUnitTap}
            />
          </>;
        })()}

        <div className={styles.warBand}>
          {/* Лево: кто ходит первым в этом матче */}
          <div className={styles.wbSide}>
            <span className={styles.wbCoin}>🪙</span>
            <span className={styles.wbFirstLabel}>{trStatic('первый', 'first')}</span>
            <span className={`${styles.wbFirstName} ${match.firstPlayerIdx === 0 ? styles.wbFirstMe : styles.wbFirstEnemy}`}>
              {match.firstPlayerIdx === 0 ? trStatic('я', 'me') : enemy.name || trStatic('враг', 'enemy')}
            </span>
          </div>
          {/* Центр: Ход + фаза + таймер (если фаза с человеческим вводом) */}
          <div className={styles.wbCenter}>
            <span className={styles.wbTurn}>{trStatic('Ход', 'Turn')} {turn}</span>
            <span className={styles.wbPhase}>{trStatic('Ф', 'P')}{pNum} · {phase}</span>
            {/* ТАЙ.1: остаток фазы. Красный когда <=5с. */}
            {phaseRemainingMs != null && (
              <span className={`${styles.wbTimer} ${phaseUrgent ? styles.wbTimerUrgent : ''}`}>
                {Math.ceil(phaseRemainingMs / 1000)}с
              </span>
            )}
          </div>
          {/* ВОЙ.1: Война Корпораций. В war-режиме показываем widget со
              ставкой, текущим множителем и кнопкой «ПОДНЯТЬ ×N+1».
              Если pending снап МОЙ — «жду ответа врага».
              Если pending снап ВРАЖЕСКИЙ — кнопки скрыты, ответ через
              отдельную модалку (рендерится ниже только в Ф2). */}
          <div className={`${styles.wbSide} ${styles.wbSideRight}`}>
            {warStakeUsd > 0 ? (
              <button
                type="button"
                className={`${styles.wbWarBtn} ${canDeclareWarSnap ? styles.wbWarBtnActive : ''}`}
                disabled={!canDeclareWarSnap}
                title={warSnapPendingMine
                  ? trStatic('Жду ответа противника', 'Waiting for opponent')
                  : (canDeclareWarSnap
                    ? trStatic(`Поднять ставку ×${warMultiplier * 2}`, `Raise stake ×${warMultiplier * 2}`)
                    : trStatic('Снап недоступен', 'Snap unavailable'))}
                onClick={() => declareWarSnap(match)}
              >
                <span className={styles.wbWarLbl}>⚔ ВК</span>
                <span className={styles.wbWarStake}>×{warMultiplier} · ${warCurrentStake}</span>
                {warSnapPendingMine ? (
                  <span className={styles.wbWarPending}>
                    ⏳ {warSnapRemaining != null ? `${Math.ceil(warSnapRemaining / 1000)}с` : '—'}
                  </span>
                ) : warSnapPendingByEnemy ? (
                  <span className={styles.wbWarPendingEnemy}>
                    ↑ {trStatic('враг поднял', 'enemy raised')}
                  </span>
                ) : (
                  <span className={styles.wbWarRaise}>
                    {warMultiplier < 4 ? `↑×${warMultiplier * 2}` : trStatic('макс', 'max')}
                  </span>
                )}
              </button>
            ) : (
              <span className={styles.wbWarPlaceholder} title="Война Корпораций (только в режиме Войны)">— ВК —</span>
            )}
          </div>
        </div>

        {(() => {
          // ID-set моих стелс-юнитов, которые можно активировать в этой фазе.
          const myStealthIds = new Set<number>(myStealthUnits.map((u: any) => u.instanceId));
          // Тап по моему юниту: если это активируемый стелс → toggle.
          // Если ничего особого — открыть детали.
          const myUnitTap = (u: any) => {
            if (myStealthIds.has(u.instanceId)) {
              setStealthPick(stealthPick?.instanceId === u.instanceId ? null : u);
              return;
            }
            const d = getCardData(u.id);
            // БУГ.10: u — engine instance моего юнита.
            if (d) setDetailCard({ data: d, runtimeCard: u });
          };
          return <>
            <LaneRow
              cells={me.field[0]} isEnemy={false} faction={myFaction} lineNum={1}
              pendingMap={pendingMap}
              dragValidLine={isDraggingNow}
              hoverCell={drag.hoverCell} dropZoneBase="me"
              actionPickTargetIds={inActionPick ? actionPick.targetIds : undefined}
              onActionPick={(u) => doActionPick(u)}
              myStealthActivatable={myStealthIds}
              myStealthArmedId={stealthPick?.instanceId ?? null}
              onUnitTap={myUnitTap}
            />
            <LaneRow
              cells={me.field[1]} isEnemy={false} faction={myFaction} lineNum={2}
              pendingMap={pendingMap}
              dragValidLine={isDraggingNow}
              hoverCell={drag.hoverCell} dropZoneBase="me"
              actionPickTargetIds={inActionPick ? actionPick.targetIds : undefined}
              onActionPick={(u) => doActionPick(u)}
              myStealthActivatable={myStealthIds}
              myStealthArmedId={stealthPick?.instanceId ?? null}
              onUnitTap={myUnitTap}
            />
          </>;
        })()}
      </div>

      {/* Игрок */}
      <HeroBar
        player={me}
        faction={myFaction}
        isEnemy={false}
        onHeroTap={() => setDetailHero(me.hero)}
        onDeckTap={() => setPile({ kind: 'deck', ownerIdx: 0 })}
        onDiscardTap={() => setPile({ kind: 'discard', ownerIdx: 0 })}
        onStandTap={() => setStandOpen(true)}
        // Кнопка АТАКА в pbar.me — только моя HERO-фаза, Герой ещё не бил.
        showAttackBtn={attackMode}
        attackBtnArmed={heroAttackArmed}
        onAttackBtnClick={() => setHeroAttackArmed((v) => !v)}
      />

      {/* Рука + кнопка ХОД */}
      <div className={styles.handRow}>
        <div className={styles.hand} data-hand="my" ref={handBarRef}>
          {me.hand.length === 0 ? (
            <div className={styles.handEmpty}>рука пуста</div>
          ) : (
            me.hand.map((card: any) => {
              const isPending = pendingInstanceIds.has(card.instanceId);
              const isDraggingThis = drag.started && drag.card?.instanceId === card.instanceId;
              return (
                <HandCard
                  key={card.instanceId}
                  card={card}
                  faction={myFaction}
                  isPending={isPending}
                  isDragging={isDraggingThis}
                  registerDragHandler={registerDragHandler}
                  onTap={(c, pending) => {
                    // Глушим клик если только что был drag (recentlyDragged 250мс).
                    if (dragState.current.started || dragState.current.recentlyDragged) return;
                    if (pending) {
                      // Pending может быть unit-placement ИЛИ Action.
                      if (pendingActionId === c.instanceId) {
                        clearPlayerPendingAction(match, 0);
                      } else {
                        cancelPendingPlacement(match, 0, c.instanceId);
                      }
                      refresh();
                    } else {
                      const data = getCardData(c.id);
                      if (data) setDetailCard({ data, handCard: c });
                    }
                  }}
                />
              );
            })
          )}
        </div>
        {/* Кнопка ХОД в Ф2 / ПАСС в Ф4-5 / ⏳ во время обработки. */}
        {attackMode ? (
          <button
            className={`${styles.endTurn} ${styles.endTurnPass}`}
            disabled={isProcessing}
            title={trStatic('Пропустить атаку Героем в этой фазе', 'Skip Hero attack this phase')}
            onClick={() => onPlayerHeroAttack(null)}
          >
            ⏭<br/>{trStatic('ПАСС', 'PASS')}
          </button>
        ) : (() => {
          // ПвП.fix-button: в PvP после моего end_turn кнопка должна
          // показывать «жду соперника» (⌛), а не оставаться «▶ ХОД».
          // me.endTurnAcked = true → я уже нажал, ждём вторго.
          const iAcked = isPvP && (me as any)?.endTurnAcked;
          const showWait = iAcked && !match.matchOver;
          return (
            <button
              className={styles.endTurn}
              disabled={!isPlacement || isProcessing || showWait}
              title={
                showWait
                  ? trStatic('Жду соперника', 'Waiting for opponent')
                  : isProcessing
                    ? trStatic('Идёт ход…', 'Turn in progress…')
                    : trStatic('Завершить ход (Ф2 → Ф3 → Ф4/5)', 'End turn (P2 → P3 → P4/5)')
              }
              onClick={onEndTurn}
            >
              {showWait
                ? <>⌛<br/>{trStatic('жду', 'wait')}</>
                : isProcessing
                  ? <>⏳<br/>…</>
                  : <>▶<br/>{trStatic('ХОД', 'TURN')}</>}
            </button>
          );
        })()}
      </div>

      {/* Ghost карты в руке во время drag'а — летит за пальцем. */}
      {isDraggingNow && drag.card && (
        <div
          className={styles.dragGhost}
          style={{ left: drag.curX, top: drag.curY }}
        >
          <div className={`${styles.hcard} ${styles[`f-${factionShort(myFaction)}`]} ${styles[`r-${drag.card.rarity || 'common'}`]}`}>
            <div className={styles.hcardArt}>
              <img
                src={`/img/cards/${drag.card.id}.jpg`}
                alt=""
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            </div>
            <div className={styles.hcardName}>{drag.card.name_ru || drag.card.id}</div>
          </div>
        </div>
      )}

      {/* Мулиган */}
      {isMulligan && (
        <MulliganModal
          hand={me.hand}
          faction={myFaction}
          onDone={onMulliganDone}
          onCardDetail={(c) => {
            const d = getCardData(c.id);
            if (d) setDetailCard({ data: d });
          }}
          // МУЛ.1: PvP-таймер 20 сек. В PvE/sandbox — без таймера.
          deadlineAt={isPvP ? ((match as any)?.phaseDeadlineAt ?? null) : null}
        />
      )}

      {/* Detail modals — последовательность важна для z-order:
          pile / hero рисуются ПЕРВЫМИ, потом CardDetailModal поверх. */}
      {pile && (() => {
        const owner = pile.ownerIdx === 0 ? me : enemy;
        const ownerFaction = pile.ownerIdx === 0 ? myFaction : enemyFaction;
        const title = pile.ownerIdx === 0
          ? (pile.kind === 'deck' ? trStatic('Моя колода', 'My deck') : trStatic('Мой сброс', 'My discard'))
          : trStatic('Сброс врага', 'Enemy discard');
        return (
          <PileModal
            title={title}
            cards={pile.kind === 'deck' ? owner.deck : owner.discard}
            isDeckShuffled={pile.kind === 'deck'}
            faction={ownerFaction}
            onClose={() => setPile(null)}
            onCardDetail={(c) => {
              const d = getCardData(c.id);
              if (d) setDetailCard({ data: d });
            }}
          />
        );
      })()}
      {detailHero && (() => {
        // Активка только для МОЕГО Героя.
        const isMyHero = detailHero.instanceId === me.hero.instanceId;
        let activeCtx: { canUse: boolean; reason: string; onUse: () => void } | undefined;
        if (isMyHero) {
          const info = getHeroActiveInfo(match, 0);
          if (info.exists) {
            if (info.needsTarget) {
              activeCtx = {
                canUse: false,
                reason: trStatic('нужна цель — БОЙ.1.7', 'target needed — BOY.1.7'),
                onUse: () => {/* TODO: target picker */}
              };
            } else {
              activeCtx = {
                canUse: info.available,
                reason: info.reason || trStatic('недоступно', 'unavailable'),
                onUse: async () => {
                  // АНИМ.10: async-вариант с diff-snapshot — heal/buff/summon/
                  // damage от активки Героя теперь анимируются. В PvP роутинг
                  // через engine-shim не задействован тут (это PvE-путь).
                  const r = await useHeroActiveWithAnim(match, 0, undefined, animatorRef.current);
                  const tid = ++toastIdRef.current;
                  setLogToasts((t) => [...t.slice(-4), {
                    id: tid,
                    text: r.ok
                      ? trStatic('◆ Активка Героя применена', '◆ Hero active used')
                      : `✗ ${trStatic('Активка', 'Active')}: ${r.reason || 'fail'}`,
                    cls: r.ok ? 'logToastInflu' : 'logToastDeath'
                  }]);
                  setTimeout(() => setLogToasts((t) => t.filter((x) => x.id !== tid)), 3500);
                  if (r.ok) refresh();
                }
              };
            }
          }
        }
        return (
          <HeroDetailModal
            hero={detailHero}
            onClose={() => setDetailHero(null)}
            activeCtx={activeCtx}
          />
        );
      })()}
      {/* Стенд Влияния (Деус) */}
      {standOpen && myFaction === 'deus' && (
        <InfluenceStandModal
          match={match}
          player={me}
          offerIds={me.influenceOffer || []}
          influence={me.influence || 0}
          isPlacement={isPlacement}
          onClose={() => setStandOpen(false)}
          onPlay={async (cardId) => {
            const toast = (text: string, cls = 'logToastDeath') => {
              const tid = Date.now() + Math.floor(Math.random() * 1000);
              setLogToasts((t) => [...t.slice(-4), { id: tid, text, cls }]);
              setTimeout(() => setLogToasts((t) => t.filter((x) => x.id !== tid)), 2800);
            };
            // Этап 80: таргетная карта (Ликвидатор) — закрываем стенд и
            // выбираем цель на поле через общий actionPick (как у Действий).
            if (needsInfluenceTarget(cardId)) {
              const targets = getInfluenceTargets(match, 0, cardId);
              const targetIds = new Set<number>(targets.map((u: any) => u.instanceId));
              if (targetIds.size === 0) {
                toast(trStatic('Нет целей на поле', 'No targets on board'));
                return;
              }
              setStandOpen(false);
              setActionPick({
                card: { id: cardId, influenceCardId: cardId },
                targetIds,
                bannerRu: 'Выбери цель для карты Влияния',
                bannerEn: 'Pick a target for the Influence card',
                onPick: async (unit: any) => {
                  setActionPick(null);
                  const r = await playInfluenceCardWithAnim(match, 0, cardId, unit, animatorRef.current);
                  if (r.ok) refresh();
                  else { toast(`${trStatic('Не сыграть', "Can't play")}: ${r.reason || ''}`); refresh(); }
                }
              });
              return;
            }
            // Безтаргетная — играем сразу. При успехе ЗАКРЫВАЕМ стенд (иначе
            // модалка перекрывает поле и кажется, что «ничего не произошло»).
            const res = await playInfluenceCardWithAnim(
              match, 0, cardId, null, animatorRef.current
            );
            if (res.ok) {
              setStandOpen(false);
              refresh();
            } else {
              console.warn('[battle] influence play failed:', res.reason);
              toast(`${trStatic('Не сыграть', "Can't play")}: ${res.reason || ''}`);
            }
          }}
          onCardDetail={(tpl) => setInfluenceDetail(tpl)}
        />
      )}
      {influenceDetail && (
        <InfluenceDetailModal tpl={influenceDetail} onClose={() => setInfluenceDetail(null)} />
      )}
      {/* End-turn indicator (PvP-Ф2): противник нажал «ход», я ещё нет.
          Серверный endTurnAcked синкается в Player schema → match-adapter
          пробрасывает в match.players[i].endTurnAcked. */}
      {isPvP && isPlacement && !match.matchOver
        && (enemy as any)?.endTurnAcked && !(me as any)?.endTurnAcked && (
        <div className={styles.endTurnWait}>
          <span className={styles.endTurnWaitTop}>
            ⏳ {trStatic('Противник готов', 'Opponent ready')}
          </span>
          <span className={styles.endTurnWaitSub}>
            {trStatic('ждёт твой ход', 'waiting for your move')}
          </span>
        </div>
      )}

      {/* ВОЙ.1: модалка ответа на снап. Показывается врагу-адресату ТОЛЬКО
          в его Ф2 (placement). Сервер выставляет warSnapDeadlineMs когда
          стартует таймер 10с — здесь отсчитываем до 0. По истечении сервер
          сам пришлёт matchOver с warEndReason='retreat'. */}
      {showWarSnapModal && (
        <div className={styles.warSnapOverlay}>
          <div className={styles.warSnapBox}>
            <div className={styles.warSnapTitle}>
              ⚔ {trStatic('ВОЙНА КОРПОРАЦИЙ', 'CORP WARS')}
            </div>
            <div className={styles.warSnapText}>
              {trStatic('Противник поднял ставку', 'Opponent raised the stake')}
              <br />
              <span className={styles.warSnapStake}>
                ×{warMultiplier} → ×{Math.min(4, warMultiplier * 2)}
                {' · '}
                ${warCurrentStake} → ${Math.min(4, warMultiplier * 2) * warStakeUsd}
              </span>
            </div>
            <div className={styles.warSnapTimer}>
              ⏱ {warSnapRemaining != null ? `${Math.ceil(warSnapRemaining / 1000)}с` : '—'}
            </div>
            <div className={styles.warSnapBtns}>
              <button
                className={`${styles.warSnapBtn} ${styles.warSnapBtnAccept}`}
                onClick={() => respondWarSnap(match, true)}
              >
                ✓ {trStatic('ПРИНЯТЬ', 'ACCEPT')}
              </button>
              <button
                className={`${styles.warSnapBtn} ${styles.warSnapBtnRetreat}`}
                onClick={() => respondWarSnap(match, false)}
              >
                ✕ {trStatic('ОТСТУПИТЬ', 'RETREAT')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action target-pick banner */}
      {inActionPick && actionPick && (
        <div className={styles.pickBanner}>
          <div className={styles.pickBannerInfo}>
            {actionPick.bannerRu ? (
              // Этап 63 — board-tap order-target: показываем что именно тапать.
              <span className={styles.pickBannerLbl}>
                {trStatic(actionPick.bannerRu, actionPick.bannerEn || actionPick.bannerRu)}
              </span>
            ) : (
              <>
                <span className={styles.pickBannerLbl}>{trStatic('выбери цель для', 'pick target for')}</span>
                <span className={styles.pickBannerName}>{actionPick.card.name_ru || actionPick.card.id}</span>
                <span className={styles.pickBannerCount}>
                  {actionPick.targetIds.size} {trStatic('ru', 'en') === 'en'
                    ? (actionPick.targetIds.size === 1 ? 'target' : 'targets')
                    : (actionPick.targetIds.size === 1 ? 'цель' : 'цел')}
                </span>
              </>
            )}
          </div>
          <button className={styles.pickBannerCancel} onClick={() => setActionPick(null)}>
            ✕ {trStatic('отмена', 'cancel')}
          </button>
        </div>
      )}

      {/* Лог боя */}
      {logOpen && (
        <LogModal
          logEntries={match.log?.entries || []}
          onClose={() => setLogOpen(false)}
        />
      )}
      {/* CardDetailModal — самый верхний. Когда открыта из PileModal,
          поверх неё, не за. */}
      {detailCard && (() => {
        const hc = detailCard.handCard;
        const isMyAction = !!hc && isPlacement && (hc.type === 'action' || detailCard.data.type === 'action');
        // Initiation warning — для любой карты в моей руке, если её
        // активация позже текущего хода (диздок §6.6).
        let initiationWarn: { currentTurn: number; activatesOnTurn: number; isAction: boolean } | undefined;
        if (hc) {
          const rarity = hc.rarity || detailCard.data.rarity;
          const type = hc.type || detailCard.data.type;
          const isAction = type === 'action';
          // Для юнитов: initiation хранится на карте (resolveInitiation).
          // Для action: actionInitiationTurn(rarity) → бридж к engine.
          const activates = isAction
            ? actionInitiationTurn(rarity)
            : (hc.initiation || 0);
          if (activates > match.currentTurn) {
            initiationWarn = {
              currentTurn: match.currentTurn,
              activatesOnTurn: activates,
              isAction
            };
          }
        }
        let actionCtx: undefined | {
          isPending: boolean; needsTarget: boolean; canPlay: boolean;
          onPlay: () => void; onCancel: () => void;
        };
        if (isMyAction && hc) {
          const isPending = pendingActionId === hc.instanceId;
          const needsTarget = actionNeedsTarget(hc.id);
          // Свободные слоты бюджета.
          const can = canAddPlacement(match, 0, hc);
          actionCtx = {
            isPending,
            needsTarget,
            canPlay: isPending ? true : can.ok,
            onPlay: () => {
              // 2d: Великая жертва — мультиселект до 3 своих юнитов (atk≥2).
              if (hc.id === 'deus_velikaya_zhertva') {
                const cand: any[] = [];
                for (const l of [0, 1] as const) {
                  for (const u of (match.players[0].field?.[l] || [])) {
                    if (u && u.type === 'unit' && u.alive !== false && (u.current?.atk ?? u.atk ?? 0) >= 2) cand.push(u);
                  }
                }
                setMultiSelectPick({
                  card: hc, candidates: cand, maxCount: 3, mode: 'sacrifice',
                  hintRu: 'Выбери до 3 своих юнитов с атакой 2+ (будут уничтожены)',
                  hintEn: 'Pick up to 3 of your units with attack 2+ (will be destroyed)'
                });
                setDetailCard(null);
                return;
              }
              // 2d: Дозаправка/Конструктор — reveal-pick (вскрыть Моды из колоды → выбрать).
              if (hc.id === 'syn_act_dozapravka' || hc.id === 'syn_act_konstruktor_implantov') {
                const look = hc.id === 'syn_act_dozapravka' ? 3 : 4;
                const pick = hc.id === 'syn_act_dozapravka' ? 1 : 2;
                if (isPvP) {
                  // PvP: колода скрыта — вскрытие на сервере (анти-чит RNG).
                  // Сервер пришлёт 'mod_reveal' → откроем picker (см. onMessage).
                  getCurrentRoom()?.send('reveal_request', { cardInstanceId: hc.instanceId, count: look });
                  setDetailCard(null);
                  return;
                }
                // PvE: peek локального движка (без удаления — резолвер разложит в Ф3).
                const revealed = revealModsFromDeck(match, 0, look);
                if (revealed.length === 0) {
                  setPlayerPendingAction(match, 0, hc.instanceId, null, {});
                  setDetailCard(null); refresh();
                  return;
                }
                const exact = Math.min(pick, revealed.length);
                setMultiSelectPick({
                  card: hc, candidates: revealed, maxCount: pick, exactCount: exact,
                  mode: 'mod-reveal',
                  revealedInstanceIds: revealed.map((m: any) => m.instanceId),
                  hintRu: `Вскрыто ${revealed.length} — выбери ${exact} в руку (остальные в сброс)`,
                  hintEn: `Revealed ${revealed.length} — pick ${exact} to hand (rest discarded)`,
                });
                setDetailCard(null);
                return;
              }
              if (!needsTarget) {
                // Действие без цели — сразу set pendingAction.
                setPlayerPendingAction(match, 0, hc.instanceId, null, {});
                refresh();
              } else {
                // С целью → запускаем target-picker (баннер сверху +
                // подсветка валидных целей). Закрытие модалки делает CardDetailModal.
                const targets = getActionTargets(match, 0, hc.id);
                const targetIds = new Set<number>(
                  targets.map((u: any) => u.instanceId).filter((id: any) => typeof id === 'number')
                );
                if (targetIds.size === 0) {
                  console.warn('[battle] no valid targets for action', hc.id);
                  return;
                }
                setActionPick({ card: hc, targetIds });
              }
            },
            onCancel: () => {
              clearPlayerPendingAction(match, 0);
              refresh();
            }
          };
        }
        return (
          <CardDetailModal
            card={detailCard.data}
            runtimeCard={detailCard.runtimeCard || detailCard.handCard}
            onClose={() => setDetailCard(null)}
            actionCtx={actionCtx}
            initiationWarn={initiationWarn}
          />
        );
      })()}

      {/* Toast'ы событий движка (Приказы, Триггеры, ...) */}
      {logToasts.length > 0 && (
        <div className={styles.logToastStack}>
          {logToasts.map((t) => (
            <div key={t.id} className={`${styles.logToast} ${styles[t.cls]}`}>
              {t.text}
            </div>
          ))}
        </div>
      )}

      {/* АНИМ.1: overlay для боевых анимаций (Ф3+Ф4+Ф5). */}
      <AnimationLayer animator={animatorRef.current} />

      {/* ОТЛ.2: debug-баннер в углу — версия билда + последний батч событий.
          Чтобы юзер мог визуально подтвердить «прокаталась ли анимация».
          Без DevTools в Telegram Desktop это единственный способ. */}
      {isPvP && (() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const last = (window as any).__lastCombatBatch__ as { status: string; count: number; tick: number; ts: number } | undefined;
        const ageSec = last ? Math.floor((Date.now() - last.ts) / 1000) : null;
        const color = last?.status === 'start' ? '#3DF0FF'
          : last?.status === 'done' ? '#7df07d'
          : last?.status === 'timeout' ? '#ff8888'
          : '#888';
        return (
          <div style={{
            position: 'fixed',
            top: 4,
            right: 4,
            background: 'rgba(0, 0, 0, 0.7)',
            color: '#aaa',
            border: '1px solid #333',
            borderRadius: 3,
            padding: '2px 6px',
            fontSize: 9,
            fontFamily: 'monospace',
            zIndex: 9998,
            pointerEvents: 'none',
            lineHeight: 1.3,
          }}>
            v.{__GIT_COMMIT__} · tick {(match as any)?.phase3?.combatEventsTick ?? lastCombatTickRef.current}
            {last && (
              <>
                <br />
                <span style={{ color }}>
                  ◆ {last.status} {last.count}ev {ageSec}s ago
                </span>
              </>
            )}
          </div>
        );
      })()}

      {/* АНИМ.3: бутафория не блокирует — кнопка «разморозить» больше
          не нужна. Если ghost залип — debug-баннер top-right показывает
          это, всё остальное продолжает работать. */}

      {/* БУГ.3: picker приоритета атаки для Глобал-юнита. */}
      {attackPriorityPick && (
        <AttackPriorityPickerModal
          card={attackPriorityPick.card}
          onPick={(priority) => {
            // БУГ.5-fix4: защита от двойного вызова (onPointerDown+onClick
            // оба срабатывали — pendingPlacements получал двойной push,
            // engine ломался). Ref сбрасывается при следующем открытии.
            if (pickingPriorityRef.current) return;
            pickingPriorityRef.current = true;
            const pick = attackPriorityPick;
            const tid1 = ++toastIdRef.current;
            setLogToasts((t) => [...t.slice(-4), {
              id: tid1, text: `◆ Приоритет: ${priority}`, cls: 'logToastInflu'
            }]);
            setTimeout(() => setLogToasts((t) => t.filter((x) => x.id !== tid1)), 2200);
            try {
              setUnitAttackPriority(pick.card, priority);
              // 2d: глобал-медики (Янтарь/Хирург G-Sec) после выбора атаки
              // ещё выбирают приоритет ЛЕЧЕНИЯ перед размещением.
              if (needsHealPriorityPicker(pick.card)) {
                pickingHealPriorityRef.current = false;
                setHealPriorityPick({ card: pick.card, line: pick.line, slot: pick.slot });
              } else {
                proceedWithPlacement(pick.card, pick.line, pick.slot);
              }
            } catch (e: any) {
              const tidErr = ++toastIdRef.current;
              setLogToasts((t) => [...t.slice(-4), {
                id: tidErr,
                text: `✗ ОШИБКА: ${e?.message || e}`.slice(0, 80),
                cls: 'logToastDeath'
              }]);
              setTimeout(() => setLogToasts((t) => t.filter((x) => x.id !== tidErr)), 5000);
              console.error('[placement] crash in proceedWithPlacement:', e);
            }
            setAttackPriorityPick(null);
          }}
          onCancel={() => {
            setAttackPriorityPick(null);
          }}
        />
      )}

      {/* 2d: picker приоритета ЛЕЧЕНИЯ для хилеров. */}
      {healPriorityPick && (
        <HealPriorityPickerModal
          card={healPriorityPick.card}
          onPick={(priority) => {
            if (pickingHealPriorityRef.current) return;
            pickingHealPriorityRef.current = true;
            const pick = healPriorityPick;
            const tid1 = ++toastIdRef.current;
            setLogToasts((t) => [...t.slice(-4), {
              id: tid1, text: `✚ Лечит: ${priority}`, cls: 'logToastInflu'
            }]);
            setTimeout(() => setLogToasts((t) => t.filter((x) => x.id !== tid1)), 2200);
            try {
              setUnitHealPriority(pick.card, priority);
              proceedWithPlacement(pick.card, pick.line, pick.slot);
            } catch (e: any) {
              const tidErr = ++toastIdRef.current;
              setLogToasts((t) => [...t.slice(-4), {
                id: tidErr,
                text: `✗ ОШИБКА: ${e?.message || e}`.slice(0, 80),
                cls: 'logToastDeath'
              }]);
              setTimeout(() => setLogToasts((t) => t.filter((x) => x.id !== tidErr)), 5000);
              console.error('[placement] crash in proceedWithPlacement (heal):', e);
            }
            setHealPriorityPick(null);
          }}
          onCancel={() => {
            setHealPriorityPick(null);
          }}
        />
      )}

      {/* ПвП.fix-move-action: куда переместить юнита (Перегруппировка,
          Передислокация). Открывается после выбора target в actionPick. */}
      {actionMovePick && (
        <MoveDestinationModal
          card={actionMovePick.card}
          target={actionMovePick.target}
          me={me}
          onPick={doMoveDestinationPick}
          onCancel={() => setActionMovePick(null)}
        />
      )}

      {/* БУГ.2: target picker для Order'ов с order_target='enemy_choose'. */}
      {orderTargetPick && (
        <OrderTargetPickerModal
          card={orderTargetPick.card}
          candidates={orderTargetPick.candidates}
          purpose={orderTargetPick.purpose}
          onPick={(target) => {
            // БУГ.5-fix4: double-call guard (analogous to priority picker).
            if (pickingTargetRef.current) return;
            pickingTargetRef.current = true;
            const pick = orderTargetPick;
            try {
              if (target) {
                setPendingOrderOpts(match, 0, pick.card.instanceId, { target });
              }
              setPendingPlacement(match, 0, {
                cardInstanceId: pick.card.instanceId,
                line: pick.line,
                slot: pick.slot
              });
              const pendingCount = (match.pendingPlacements?.[0] || []).length;
              const tid = ++toastIdRef.current;
              setLogToasts((t) => [...t.slice(-4), {
                id: tid,
                text: `✓ Размещ: л.${pick.line + 1}/сл.${pick.slot + 1} · pending=${pendingCount}`,
                cls: 'logToastInflu'
              }]);
              setTimeout(() => setLogToasts((t) => t.filter((x) => x.id !== tid)), 2500);
            } catch (e: any) {
              const tidErr = ++toastIdRef.current;
              setLogToasts((t) => [...t.slice(-4), {
                id: tidErr,
                text: `✗ ОШИБКА: ${e?.message || e}`.slice(0, 80),
                cls: 'logToastDeath'
              }]);
              setTimeout(() => setLogToasts((t) => t.filter((x) => x.id !== tidErr)), 5000);
            }
            setOrderTargetPick(null);
            refresh();
          }}
          onCancel={() => setOrderTargetPick(null)}
        />
      )}

      {/* 2d: Великая жертва — мультиселект своих юнитов. */}
      {multiSelectPick && (
        <MultiSelectModal
          card={multiSelectPick.card}
          candidates={multiSelectPick.candidates}
          maxCount={multiSelectPick.maxCount}
          exactCount={multiSelectPick.exactCount}
          hintRu={multiSelectPick.hintRu}
          hintEn={multiSelectPick.hintEn}
          onConfirm={(ids) => {
            try {
              const mp = multiSelectPick;
              if (mp.mode === 'mod-reveal') {
                // Дозаправка/Конструктор: вскрытые + выбранные → резолвер разложит.
                setPlayerPendingAction(match, 0, mp.card.instanceId, null, {
                  revealedInstanceIds: mp.revealedInstanceIds || [], pickedInstanceIds: ids,
                });
              } else {
                setPlayerPendingAction(match, 0, mp.card.instanceId, null, { selectedInstanceIds: ids });
              }
              const tid = ++toastIdRef.current;
              setLogToasts((t) => [...t.slice(-4), { id: tid, text: `✓ Выбрано: ${ids.length}`, cls: 'logToastInflu' }]);
              setTimeout(() => setLogToasts((t) => t.filter((x) => x.id !== tid)), 2500);
            } catch (e: any) {
              console.error('[multiselect] err', e);
            }
            setMultiSelectPick(null);
            refresh();
          }}
          onCancel={() => setMultiSelectPick(null)}
        />
      )}

      {/* 2d: Фиксер (syn_fixer) — каскад выбора при размещении. */}
      {fikserPick && (() => {
        const fp = fikserPick;
        const unitsOf = (pl: any): any[] => {
          const out: any[] = [];
          for (const l of [0, 1] as const) {
            for (const u of (pl?.field?.[l] || [])) {
              if (u && u.type === 'unit' && u.alive !== false) out.push(u);
            }
          }
          return out;
        };
        const me0 = match.players[0];
        const commitFikser = (opts: any) => {
          if (pickingTargetRef.current) return;
          pickingTargetRef.current = true;
          try {
            setPendingOrderOpts(match, 0, fp.card.instanceId, opts);
            setPendingPlacement(match, 0, { cardInstanceId: fp.card.instanceId, line: fp.line, slot: fp.slot });
            const tid = ++toastIdRef.current;
            setLogToasts((t) => [...t.slice(-4), { id: tid, text: `✓ Фиксер размещён`, cls: 'logToastInflu' }]);
            setTimeout(() => setLogToasts((t) => t.filter((x) => x.id !== tid)), 2500);
          } catch (e: any) {
            const tidErr = ++toastIdRef.current;
            setLogToasts((t) => [...t.slice(-4), { id: tidErr, text: `✗ ОШИБКА: ${e?.message || e}`.slice(0, 80), cls: 'logToastDeath' }]);
            setTimeout(() => setLogToasts((t) => t.filter((x) => x.id !== tidErr)), 5000);
          }
          setFikserPick(null);
          refresh();
        };
        if (fp.step === 'source') {
          const candidates = unitsOf(me0).filter((u: any) => (u.modifications?.length ?? 0) > 0);
          return <OrderTargetPickerModal card={fp.card} candidates={candidates} purpose="fikser_source"
            onPick={(u) => { if (!u) { setFikserPick(null); return; } setFikserPick({ ...fp, sourceInstanceId: u.instanceId, sourceModIndex: 0, step: 'target' }); }}
            onCancel={() => setFikserPick(null)} />;
        }
        if (fp.step === 'target') {
          const fieldUnits = unitsOf(me0).filter((u: any) => u.instanceId !== fp.sourceInstanceId);
          const pendingUnits = ((match.pendingPlacements?.[0] || []) as any[])
            .map((pl) => (me0.hand || []).find((c: any) => c.instanceId === pl.cardInstanceId))
            .filter((c: any) => c && c.type === 'unit' && c.instanceId !== fp.sourceInstanceId);
          const candidates = [...fieldUnits, ...pendingUnits];
          return <OrderTargetPickerModal card={fp.card} candidates={candidates} purpose="fikser_target"
            onPick={(u) => { if (!u) { setFikserPick(null); return; } commitFikser({ sourceInstanceId: fp.sourceInstanceId, sourceModIndex: fp.sourceModIndex || 0, targetInstanceId: u.instanceId }); }}
            onCancel={() => setFikserPick(null)} />;
        }
        if (fp.step === 'unit') {
          const candidates = [...unitsOf(match.players[0]), ...unitsOf(match.players[1])];
          return <OrderTargetPickerModal card={fp.card} candidates={candidates} purpose="fikser_unit"
            onPick={(u) => { if (!u) { setFikserPick(null); return; } setFikserPick({ ...fp, unitInstanceId: u.instanceId, step: 'slot' }); }}
            onCancel={() => setFikserPick(null)} />;
        }
        if (fp.step === 'slot') {
          let moved: any = null, ownerPl: any = null;
          for (const pl of match.players) {
            for (const l of [0, 1] as const) {
              for (const u of (pl.field?.[l] || [])) {
                if (u && u.instanceId === fp.unitInstanceId) { moved = u; ownerPl = pl; }
              }
            }
          }
          if (!moved || !ownerPl) return null;
          return <MoveDestinationModal card={fp.card} target={moved} me={ownerPl}
            onPick={(line, slot) => commitFikser({ unitInstanceId: fp.unitInstanceId, line, slot })}
            onCancel={() => setFikserPick(null)} />;
        }
        return null;
      })()}

      {/* End-of-match — самый-самый верхний (поверх всего). */}
      {match.matchOver && !endOfMatchClosed && (
        <EndOfMatchModal
          winnerIdx={match.winnerIdx}
          myFaction={myFaction}
          turns={match.currentTurn}
          onLeave={() => {
            // АНИМ.2-4: cascade. Сначала проверяем milestone level-up,
            // потом league event, потом — выход в /pvp.
            const levelUps = finishResult?.account.level_ups || [];
            const hasMilestone = levelUps.some(isMilestone);
            if (hasMilestone && finishResult) {
              setLevelUpData({
                levelUps,
                rewards: finishResult.account.rewards
              });
              setEndOfMatchClosed(true);
              return;
            }
            const evt = extractLeagueEvent(finishResult);
            if (evt) {
              setLeagueEvent(evt);
              setEndOfMatchClosed(true);
            } else {
              tx.navigate('/pvp', { replace: true, variant: 'heavy_glitch' });
            }
          }}
          finishLoading={finishLoading}
          finishResult={finishResult}
          finishError={finishError}
          scriptsEarned={
            match.winnerIdx === 0
              ? (battleState?.foundIn === 'rated' ? 10 : battleState?.foundIn === 'war' ? 0 : 1)
              : 0
          }
          logEntries={match.log?.entries}
          foundIn={battleState?.foundIn}
          warStake={battleState?.warStake}
          warMultiplier={warMultiplier}
          warEndReason={(match as any)?.warEndReason || ''}
          opponent={isPvP ? {
            nick: match.players[1]?.nick || '',
            accentColor: (match.players[1] as any)?.accentColor,
            avatarId: (match.players[1] as any)?.avatarId,
            titleCode: (match.players[1] as any)?.titleCode,
            titleText: (match.players[1] as any)?.titleText,
          } : undefined}
        />
      )}

      {/* АНИМ.4: милестон-уровень аккаунта — показываем перед лигами. */}
      {levelUpData && (
        <LevelUpModal
          levelUps={levelUpData.levelUps}
          rewards={levelUpData.rewards}
          faction={myFaction}
          onClose={() => {
            setLevelUpData(null);
            // Следующий шаг в cascade — league event если есть.
            const evt = extractLeagueEvent(finishResult);
            if (evt) {
              setLeagueEvent(evt);
            } else {
              tx.navigate('/pvp', { replace: true, variant: 'heavy_glitch' });
            }
          }}
        />
      )}

      {/* АНИМ.2: модалка переходов между лигами (поверх EndOfMatchModal,
          но он уже закрыт через endOfMatchClosed=true). */}
      {leagueEvent && (
        <LeagueTransitionModal
          event={leagueEvent.event}
          fromLeague={leagueEvent.fromLeague}
          toLeague={leagueEvent.toLeague}
          remainingFirewalls={leagueEvent.remainingFirewalls}
          rewardDescription={leagueEvent.rewardDescription}
          onClose={() => {
            setLeagueEvent(null);
            tx.navigate('/pvp', { replace: true, variant: 'heavy_glitch' });
          }}
        />
      )}

      <div className={styles.scaffoldMark}>БОЙ.1.1 · каркас (mock engine)</div>
    </div>
  );
}
